/**
 * ConversationalCreativeV2.jsx
 *
 * A fully JSON-driven conversational creative component.
 * All prompts, questions, options, and flow logic are read from the JSON config file
 * specified by VITE_CONVERSATION_FLOW_CONFIG environment variable.
 *
 * NO hardcoded prompts, questions, or options in this file.
 */

import { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { GitFork, Wand2, Star, Loader2, ZoomIn, ZoomOut, Check, AlertCircle, Download, Beaker, Compass, Maximize2, Minimize2 } from 'lucide-react';
import conversationFlowService from '../services/conversationFlowService';
import { ConversationAgentV2, FUNNEL_STAGE_CONTEXT } from '../services/conversationAgentV2';
import { useApp } from '../contexts/AppContext';
import { useAuth } from '../contexts/AuthContext';
import { scheduleAutoSave, loadCreativeChat, forkCreativeChat } from '../services/creativeChatsService';
import { batchSaveItems, getFavorites, toggleFavorite, uploadLibraryImage } from '../services/libraryService';
import { getPillarsByBrand } from '../services/pillarService';
import BriefSheet from '../components/BriefSheet';
import FavoriteButton from '../components/FavoriteButton';
import ConfirmationModal from '../components/ConfirmationModal';
import ImageRefinementPanel from '../components/ImageRefinementPanel';
import VariantLab from '../components/VariantLab';
import PillarContextBanner from '../components/strategy/PillarContextBanner';
import PillarSelector from '../components/strategy/PillarSelector';
import { useImageGenerationJob, usePendingImageJobs, startImageGenerationJob, cancelImageJob } from '../hooks/useImageGenerationJob';

// Load phases from JSON config
const PHASES = (() => {
  const phases = conversationFlowService.getPhases();
  const result = {};
  for (const [key, phase] of Object.entries(phases || {})) {
    result[key] = phase.id;
  }
  return result;
})();

// Load phase info from JSON config
const PHASE_INFO = conversationFlowService.getPhaseInfo();

// Simple markdown renderer for chat messages
const renderMarkdown = (text) => {
  if (!text) return null;

  const paragraphs = text.split(/\n\n+/);

  return paragraphs.map((paragraph, pIdx) => {
    const lines = paragraph.split('\n');

    const processedLines = lines.map((line, lIdx) => {
      let processed = line.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
      processed = processed.replace(/__(.+?)__/g, '<strong>$1</strong>');
      processed = processed.replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, '<em>$1</em>');
      processed = processed.replace(/(?<!_)_(?!_)(.+?)(?<!_)_(?!_)/g, '<em>$1</em>');

      return (
        <span key={lIdx}>
          <span dangerouslySetInnerHTML={{ __html: processed }} />
          {lIdx < lines.length - 1 && <br />}
        </span>
      );
    });

    return (
      <p key={pIdx} style={{ margin: pIdx > 0 ? '0.75em 0 0 0' : '0' }}>
        {processedLines}
      </p>
    );
  });
};

export default function ConversationalCreativeV2() {
  const { brands, selectedBrand } = useApp();
  const { currentUser } = useAuth();
  const [searchParams] = useSearchParams();

  // Chat state
  const [messages, setMessages] = useState([]);
  const [currentPhase, setCurrentPhase] = useState(Object.values(PHASES)[0] || 'strategist');
  const [phaseStep, setPhaseStep] = useState(0);
  const [userInput, setUserInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [creativeBrief, setCreativeBrief] = useState(() => conversationFlowService.getEmptyCreativeBrief());
  const [conversationAgent, setConversationAgent] = useState(null);
  const [previousOptionsForStep, setPreviousOptionsForStep] = useState([]); // Track options shown for "More Options"
  const [isEditingPhase, setIsEditingPhase] = useState(false); // Track if user is editing a field in phase summary

  // Color palette state
  const [colorPalette, setColorPalette] = useState([]);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [editingColorIndex, setEditingColorIndex] = useState(null);
  const [tempColor, setTempColor] = useState('');

  // Image selection state
  const [isSelectingImages, setIsSelectingImages] = useState(false);
  const [pendingImageSelections, setPendingImageSelections] = useState([]);
  const [isDescribingImage, setIsDescribingImage] = useState(false);

  // Background casting generation state
  const [castingOptions, setCastingOptions] = useState([]);
  const [isGeneratingCasting, setIsGeneratingCasting] = useState(false);
  const [castingError, setCastingError] = useState(null);
  const [zoomedCastingOption, setZoomedCastingOption] = useState(null);
  const [showingCastingFavorites, setShowingCastingFavorites] = useState(false);
  const [isGeneratingMoreCasting, setIsGeneratingMoreCasting] = useState(false);

  // Refs to track latest selections (avoids React state timing issues)
  const latestCastingRef = useRef(null);
  const latestLocationRef = useRef(null);
  const latestFirstFrameRef = useRef(null);
  const latestLastFrameRef = useRef(null);

  // Background location generation state (video director)
  const [locationOptions, setLocationOptions] = useState([]);
  const [isGeneratingLocations, setIsGeneratingLocations] = useState(false);
  const [locationError, setLocationError] = useState(null);
  const [zoomedLocationOption, setZoomedLocationOption] = useState(null);
  const [showingLocationFavorites, setShowingLocationFavorites] = useState(false);
  const [isGeneratingMoreLocations, setIsGeneratingMoreLocations] = useState(false);

  // First frame generation state (video director)
  const [firstFrameOptions, setFirstFrameOptions] = useState([]);
  const [isGeneratingFirstFrame, setIsGeneratingFirstFrame] = useState(false);
  const [firstFrameError, setFirstFrameError] = useState(null);
  const [zoomedFirstFrameOption, setZoomedFirstFrameOption] = useState(null);

  // Last frame generation state (video director)
  const [lastFrameOptions, setLastFrameOptions] = useState([]);
  const [isGeneratingLastFrame, setIsGeneratingLastFrame] = useState(false);
  const [lastFrameError, setLastFrameError] = useState(null);
  const [zoomedLastFrameOption, setZoomedLastFrameOption] = useState(null);

  // Generated images state (for image/carousel output)
  const [zoomedGeneratedImage, setZoomedGeneratedImage] = useState(null);

  // Image generation job state (for persistent background generation)
  const [imageJobId, setImageJobId] = useState(null);
  const [imageJobRecovered, setImageJobRecovered] = useState(false);

  // Video generation progress state
  const [videoProgress, setVideoProgress] = useState(null);
  // { operationId, status, message, progress (0-100), detailedStatus, estimatedTime, startedAt }

  // Pending video generation state (persisted for resume on refresh)
  const [pendingVideoGeneration, setPendingVideoGeneration] = useState(null);
  // { operationId, startedAt, progressMessageId, provider }

  // Script approval state (shown inline in chat before video generation)
  const [pendingScript, setPendingScript] = useState(null);
  const [isGeneratingScript, setIsGeneratingScript] = useState(false);

  // Image refinement state
  const [refinementText, setRefinementText] = useState('');
  const [isRefining, setIsRefining] = useState(false);
  const [refinementError, setRefinementError] = useState(null);
  const [refiningImageId, setRefiningImageId] = useState(null); // Track which image is being refined
  // Multiple reference images for refinement: [{ id, url, status, label }]
  // Reference images are now managed internally by ImageRefinementPanel component
  const [imageZoomLevel, setImageZoomLevel] = useState(1); // Zoom level for magnifying glass (1 = 100%)

  // Generated images state - favorites and library IDs
  const [generatedImageFavorites, setGeneratedImageFavorites] = useState({}); // { imageId: true/false }
  const [generatedImageLibraryIds, setGeneratedImageLibraryIds] = useState({}); // { imageId: libraryItemId }

  // Chat persistence state
  const [chatId, setChatId] = useState(null);
  const [isLoadingChat, setIsLoadingChat] = useState(false);

  // Funnel context state (passed from FunnelBuilder via URL params)
  const [funnelContext, setFunnelContext] = useState(null);
  // { funnelId, funnelStage, campaignId, campaignName, campaignObjective, previousStageCreatives }

  // Marketing pillar context state
  const [availablePillars, setAvailablePillars] = useState([]);
  const [selectedPillar, setSelectedPillar] = useState(null);
  const [showPillarSelector, setShowPillarSelector] = useState(false);
  const [pillarBannerExpanded, setPillarBannerExpanded] = useState(false);
  const [isLoadingPillars, setIsLoadingPillars] = useState(false);

  // A/B Variant Lab state
  const [showVariantLab, setShowVariantLab] = useState(false);
  const [generatedVariants, setGeneratedVariants] = useState(null);
  // { testElement, testDescription, variants: [...], recommendedMetrics, baseBrief }

  // Fork state
  const navigate = useNavigate();
  const [forkConfirmation, setForkConfirmation] = useState(null);
  // { messageIndex: number, show: boolean }

  // Brief sheet state
  const [briefSheetData, setBriefSheetData] = useState(null);

  // Expert notes sidebar state
  const [expandedExperts, setExpandedExperts] = useState({});
  const [editingNote, setEditingNote] = useState(null); // { expertKey: string, field: string }
  const [editNoteValue, setEditNoteValue] = useState('');
  const [noteAiInstructions, setNoteAiInstructions] = useState('');
  const [isProcessingNoteAi, setIsProcessingNoteAi] = useState(false);

  // Direct mode state ("I know what I want")
  const [isDirectMode, setIsDirectMode] = useState(false);
  const [directModeInput, setDirectModeInput] = useState('');
  const [directModePrompt, setDirectModePrompt] = useState(''); // The AI-crafted prompt
  const [directModeLoading, setDirectModeLoading] = useState(false);
  const [directModeReferenceImages, setDirectModeReferenceImages] = useState([]); // [{ id, url, status, label }]
  const [directModeRefUrlInput, setDirectModeRefUrlInput] = useState(''); // URL input field
  const [directModeStep, setDirectModeStep] = useState('prompt'); // 'prompt' | 'format' | 'aspectRatio'
  const directModeFileInputRef = useRef(null);

  // Refs
  const chatContainerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const userInputRef = useRef(null);
  const lastAutoSaveKeyRef = useRef(null); // Track last auto-save state to avoid redundant saves

  // State refs to ensure snapshots always capture latest state
  const stateRefs = useRef({
    creativeBrief,
    currentPhase,
    phaseStep,
    previousOptionsForStep,
    briefSheetData,
    colorPalette,
    conversationAgent
  });

  useEffect(() => {
    stateRefs.current = {
      creativeBrief,
      currentPhase,
      phaseStep,
      previousOptionsForStep,
      briefSheetData,
      colorPalette,
      conversationAgent
    };
  }, [creativeBrief, currentPhase, phaseStep, previousOptionsForStep, briefSheetData, colorPalette, conversationAgent]);

  // Subscribe to current image generation job
  const { job: imageJob, loading: imageJobLoading } = useImageGenerationJob(imageJobId);

  // Subscribe to pending jobs for recovery
  const { pendingJobs: pendingImageJobs } = usePendingImageJobs(
    currentUser?.uid,
    chatId
  );

  // Handle image job completion/progress updates
  useEffect(() => {
    if (!imageJob) return;

    // Update loading message with progress
    if (imageJob.status === 'processing' && imageJob.progress) {
      setMessages(prev => {
        const newText = `Generating images... (${imageJob.progress.current}/${imageJob.progress.total})\n\n_You can close this page and come back later - generation will continue in the background._`;
        const loadingMsg = prev.find(m => m.data?.showImageGenerationLoading && m.data?.jobId === imageJob.id);
        // Skip update if progress text hasn't changed to avoid unnecessary re-renders
        if (!loadingMsg || loadingMsg.text === newText) return prev;
        return prev.map(m => {
          if (m.data?.showImageGenerationLoading && m.data?.jobId === imageJob.id) {
            return {
              ...m,
              text: newText,
              data: { ...m.data, progress: imageJob.progress }
            };
          }
          return m;
        });
      });
    }

    // Handle job completion (results.images may be object or array from Firestore)
    const resultsImages = imageJob.results?.images;
    const imagesArray = Array.isArray(resultsImages) ? resultsImages : Object.values(resultsImages || {});

    if (imageJob.status === 'completed' && imagesArray.length > 0) {
      console.log('[ConversationalCreativeV2] Image job completed:', imageJob.id, 'images:', imagesArray.length);

      // Remove loading message
      setMessages(prev => prev.filter(m => !m.data?.showImageGenerationLoading || m.data?.jobId !== imageJob.id));

      // Process completed images
      const successfulImages = imagesArray;
      const isCarousel = imageJob.outputType === 'carousel';

      // Generate caption
      const postCaption = generateSocialMediaCaption(imageJob.creativeBrief || creativeBrief, isCarousel);

      // Update creative brief
      updateCreativeBrief('generatedImages', successfulImages);
      updateCreativeBrief('socialMediaCaption', postCaption);

      // Add result message (prompts may be object or array from Firestore)
      const totalPrompts = Array.isArray(imageJob.prompts) ? imageJob.prompts.length : Object.keys(imageJob.prompts || {}).length;
      const resultMessage = {
        role: 'assistant',
        text: isCarousel
          ? `Here's your ${successfulImages.length}-image carousel! ${imageJob.results.failedCount > 0 ? `(${imageJob.results.failedCount} failed)` : ''}`
          : `Here's your generated image! ${imageJob.results.failedCount > 0 ? `(${imageJob.results.failedCount} of ${totalPrompts} failed)` : ''}`,
        timestamp: new Date().toISOString(),
        phase: currentPhase,
        options: [
          { text: "Save to Library", value: 'save_generated_images', action: 'save_generated_images' },
          { text: "Regenerate", value: 'regenerate_images', action: 'regenerate_images' },
          { text: "Refine with AI", value: 'refine_generated_images', action: 'refine_generated_images' }
        ],
        data: {
          showGeneratedImages: true,
          generatedImages: successfulImages,
          isCarousel,
          socialMediaCaption: postCaption,
          jobCompleted: true
        }
      };

      setMessages(prev => [...prev, resultMessage]);

      // Clear job ID
      setImageJobId(null);
    }

    // Handle job failure
    if (imageJob.status === 'failed') {
      console.log('[ConversationalCreativeV2] Image job failed:', imageJob.id, imageJob.error);

      // Remove loading message
      setMessages(prev => prev.filter(m => !m.data?.showImageGenerationLoading || m.data?.jobId !== imageJob.id));

      // Get error details (prompts may be object or array from Firestore)
      const promptsArray = Array.isArray(imageJob.prompts) ? imageJob.prompts : Object.values(imageJob.prompts || {});
      const firstError = promptsArray.find(p => p.error);
      const errorMessage = firstError?.error?.message || imageJob.error || imageJob.progress?.message || 'Image generation failed';
      const suggestions = firstError?.error?.suggestions || [];

      // Build error message
      let message = `**${errorMessage}**`;
      if (suggestions.length > 0) {
        message += `\n\n**Suggestions:**\n${suggestions.map(s => `* ${s}`).join('\n')}`;
      }

      addAIMessage(message, [
        { text: "Try Again", value: 'retry_generation', action: 'retry_generation' },
        { text: "Adjust Settings", value: 'adjust_settings', action: 'adjust_settings' }
      ]);

      // Clear job ID
      setImageJobId(null);
    }
  }, [imageJob]);

  // Recover jobs on page load (handles processing, failed, and completed jobs)
  useEffect(() => {
    // Don't recover jobs for new chats (no chatId yet) - only recover for existing saved chats
    if (imageJobRecovered || !currentUser?.uid || !chatId || pendingImageJobs.length === 0) return;

    // Find the most recent job for this chat that needs attention
    const recentJob = pendingImageJobs[0];
    if (!recentJob || imageJobId) return;

    // Check if this job was created recently (within last 30 minutes) and needs recovery
    const jobCreatedAt = recentJob.createdAt?.toDate?.() || new Date(recentJob.createdAt?._seconds * 1000 || 0);
    const thirtyMinutesAgo = new Date(Date.now() - 30 * 60 * 1000);

    // Only recover recent jobs
    if (jobCreatedAt < thirtyMinutesAgo) {
      console.log('[ConversationalCreativeV2] Skipping old job recovery:', recentJob.id);
      setImageJobRecovered(true);
      return;
    }

    console.log('[ConversationalCreativeV2] Recovering image generation job:', recentJob.id, 'status:', recentJob.status);
    setImageJobRecovered(true);

    // Handle based on job status
    if (recentJob.status === 'processing' || recentJob.status === 'pending') {
      // Job is still running - set job ID to track it
      setImageJobId(recentJob.id);

      // Add a loading message (prompts may be object or array from Firestore)
      const promptCount = Array.isArray(recentJob.prompts) ? recentJob.prompts.length : Object.keys(recentJob.prompts || {}).length;
      const recoveryMessage = {
        role: 'assistant',
        text: `Resuming image generation... (${recentJob.progress?.current || 0}/${recentJob.progress?.total || promptCount || 0})`,
        timestamp: new Date().toISOString(),
        phase: currentPhase,
        data: {
          showImageGenerationLoading: true,
          jobId: recentJob.id,
          progress: recentJob.progress,
          isRecovered: true
        }
      };

      setMessages(prev => {
        const hasLoadingMessage = prev.some(m => m.data?.showImageGenerationLoading && m.data?.jobId === recentJob.id);
        if (hasLoadingMessage) return prev;
        return [...prev, recoveryMessage];
      });

    } else if (recentJob.status === 'failed') {
      // Job failed while user was away - show error message
      console.log('[ConversationalCreativeV2] Job failed while away:', recentJob.id);

      // prompts may be object or array from Firestore
      const promptsArray = Array.isArray(recentJob.prompts) ? recentJob.prompts : Object.values(recentJob.prompts || {});
      const firstError = promptsArray.find(p => p.error);
      const errorMessage = firstError?.error?.message || recentJob.error || recentJob.progress?.message || 'Image generation failed';
      const suggestions = firstError?.error?.suggestions || [];

      let message = `**${errorMessage}**`;
      if (suggestions.length > 0) {
        message += `\n\n**Suggestions:**\n${suggestions.map(s => `• ${s}`).join('\n')}`;
      }

      // Check if we already have an error message for this job
      setMessages(prev => {
        const hasErrorMessage = prev.some(m => m.data?.failedJobId === recentJob.id);
        if (hasErrorMessage) return prev;

        // Remove any loading message for this job
        const filtered = prev.filter(m => !m.data?.showImageGenerationLoading || m.data?.jobId !== recentJob.id);

        return [...filtered, {
          role: 'assistant',
          text: message,
          timestamp: new Date().toISOString(),
          phase: currentPhase,
          options: [
            { text: "Try Again", value: 'retry_generation', action: 'retry_generation' },
            { text: "Adjust Settings", value: 'adjust_settings', action: 'adjust_settings' }
          ],
          data: { failedJobId: recentJob.id, isRecovered: true }
        }];
      });

    } else if (recentJob.status === 'completed') {
      // Job completed while user was away - show results (images may be object or array from Firestore)
      const recoveryImages = recentJob.results?.images;
      const recoveryImagesArray = Array.isArray(recoveryImages) ? recoveryImages : Object.values(recoveryImages || {});

      if (recoveryImagesArray.length > 0) {
        console.log('[ConversationalCreativeV2] Job completed while away:', recentJob.id, 'images:', recoveryImagesArray.length);

        const successfulImages = recoveryImagesArray;
        const isCarousel = recentJob.outputType === 'carousel';
        const postCaption = generateSocialMediaCaption(recentJob.creativeBrief || creativeBrief, isCarousel);

        // Update creative brief
        updateCreativeBrief('generatedImages', successfulImages);
        updateCreativeBrief('socialMediaCaption', postCaption);

        // Check if we already have a result message for this job
        setMessages(prev => {
          const hasResultMessage = prev.some(m => m.data?.completedJobId === recentJob.id);
          if (hasResultMessage) return prev;

          // Remove any loading message for this job
          const filtered = prev.filter(m => !m.data?.showImageGenerationLoading || m.data?.jobId !== recentJob.id);

          return [...filtered, {
            role: 'assistant',
            text: isCarousel
              ? `Your ${successfulImages.length}-image carousel is ready!`
              : `Your image is ready!`,
            timestamp: new Date().toISOString(),
            phase: currentPhase,
            options: [
              { text: "Save to Library", value: 'save_generated_images', action: 'save_generated_images' },
              { text: "Regenerate", value: 'regenerate_images', action: 'regenerate_images' },
              { text: "Refine with AI", value: 'refine_generated_images', action: 'refine_generated_images' }
            ],
            data: {
              showGeneratedImages: true,
              generatedImages: successfulImages,
              isCarousel,
              socialMediaCaption: postCaption,
              completedJobId: recentJob.id,
              isRecovered: true
            }
          }];
        });
      }
    }
  }, [pendingImageJobs, currentUser?.uid, chatId, imageJobId, imageJobRecovered, currentPhase, creativeBrief]);

  // Log conversation and auto-save whenever messages change
  useEffect(() => {
    if (messages.length > 0) {
      // Build a lightweight key to detect actual content changes and avoid redundant logging
      const autoSaveKey = `${messages.length}:${currentPhase}:${messages[messages.length - 1]?.text?.substring(0, 50)}`;
      const shouldLog = autoSaveKey !== lastAutoSaveKeyRef.current;
      if (shouldLog) lastAutoSaveKeyRef.current = autoSaveKey;

      // Group messages by phase for clearer console output (only when content actually changed)
      if (shouldLog) {
        const messagesByPhase = messages.reduce((acc, m) => {
          const phase = m.phase || 'unknown';
          if (!acc[phase]) {
            acc[phase] = [];
          }
          acc[phase].push({
            role: m.role,
            text: m.text,
            options: m.options?.map(o => o.text)
          });
          return acc;
        }, {});

        console.group('[ConversationalCreativeV2] Conversation by Phase');
        Object.entries(messagesByPhase).forEach(([phase, phaseMessages]) => {
          console.group(`📝 ${phase} (${phaseMessages.length} messages)`);
          console.log(phaseMessages);
          console.groupEnd();
        });
        console.groupEnd();
      }

      // Auto-save conversation at every step
      if (currentUser && selectedBrand) {
        const chatState = {
          messages,
          creativeBrief,
          currentPhase,
          brandName: selectedBrand.name,
          brandId: selectedBrand.id,
          pendingVideoGeneration, // Include pending video generation for resume on refresh
          pillarId: selectedPillar?.id || null,
          pillarName: selectedPillar?.name || null
        };

        scheduleAutoSave(chatState, currentUser.uid, chatId, (savedChatId) => {
          if (!chatId) {
            setChatId(savedChatId);
          }
        });
      }
    }
  }, [messages, creativeBrief, currentPhase, currentUser, selectedBrand, chatId, pendingVideoGeneration, selectedPillar]);

  // Config from JSON
  const flowConfig = conversationFlowService.getFlowData();
  const globalRules = conversationFlowService.getGlobalRules();

  // ============================================
  // SCROLL TO BOTTOM
  // ============================================

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // ============================================
  // FUNNEL CONTEXT FROM URL
  // ============================================

  // Parse funnel context from URL params on mount
  useEffect(() => {
    const funnelStage = searchParams.get('stage');
    const funnelId = searchParams.get('funnelId');
    const campaignId = searchParams.get('campaignId');
    const campaignName = searchParams.get('campaignName');
    const campaignObjective = searchParams.get('objective');

    if (funnelStage && FUNNEL_STAGE_CONTEXT[funnelStage]) {
      const context = {
        funnelId,
        funnelStage,
        campaignId,
        campaignName: campaignName ? decodeURIComponent(campaignName) : null,
        campaignObjective: campaignObjective ? decodeURIComponent(campaignObjective) : null,
        previousStageCreatives: [] // TODO: Could be passed or fetched from funnel
      };
      setFunnelContext(context);
      console.log('[ConversationalCreativeV2] Funnel context loaded from URL:', context);
    }
  }, [searchParams]);

  // ============================================
  // LOAD PILLARS FOR BRAND
  // ============================================

  // Load available pillars when brand changes
  useEffect(() => {
    const loadPillars = async () => {
      if (!selectedBrand?.id) {
        setAvailablePillars([]);
        return;
      }

      setIsLoadingPillars(true);
      try {
        const pillars = await getPillarsByBrand(selectedBrand.id);
        setAvailablePillars(pillars);
        console.log('[ConversationalCreativeV2] Loaded pillars for brand:', pillars.length);

        // Check URL params for pillarId
        const pillarId = searchParams.get('pillarId');
        if (pillarId) {
          const pillar = pillars.find(p => p.id === pillarId);
          if (pillar) {
            setSelectedPillar(pillar);
            console.log('[ConversationalCreativeV2] Pillar loaded from URL:', pillar.name);
          }
        }
      } catch (error) {
        console.error('[ConversationalCreativeV2] Error loading pillars:', error);
      } finally {
        setIsLoadingPillars(false);
      }
    };

    loadPillars();
  }, [selectedBrand?.id, searchParams]);

  // Update agent when pillar changes
  useEffect(() => {
    if (conversationAgent && selectedPillar) {
      conversationAgent.setPillarContext({ pillar: selectedPillar });
    } else if (conversationAgent) {
      conversationAgent.setPillarContext(null);
    }
  }, [conversationAgent, selectedPillar]);

  // ============================================
  // INITIALIZATION
  // ============================================

  // Track the brand we've initialized for
  const initializedBrandRef = useRef(null);

  // Initialize conversation when component mounts or brand changes
  useEffect(() => {
    // Skip initialization if we're loading a saved chat from URL
    const urlChatId = searchParams.get('chatId');
    if (urlChatId && !chatId) {
      // We have a chatId in URL but haven't loaded it yet - wait for load
      return;
    }

    // No brand selected - show prompt to select one
    if (!selectedBrand) {
      // Only show this message if we haven't shown anything yet
      if (messages.length === 0) {
        setMessages([{
          id: Date.now(),
          role: 'ai',
          text: "Please select a brand from the sidebar to get started.",
          options: null,
          phase: currentPhase,
          timestamp: new Date()
        }]);
      }
      return;
    }

    // Initialize conversation agent for the selected brand (with funnel context if available)
    const pillarContext = selectedPillar ? { pillar: selectedPillar } : null;
    const agent = new ConversationAgentV2(selectedBrand, creativeBrief, funnelContext, pillarContext);
    setConversationAgent(agent);

    // Show entry point from JSON - only once per brand
    // Check if we've already initialized for this specific brand
    if (initializedBrandRef.current !== selectedBrand.id) {
      initializedBrandRef.current = selectedBrand.id;

      // Get entry point from JSON and show it
      const entryPoint = conversationFlowService.getEntryPoint();

      // Build custom message if funnel context is present
      let interpolatedMessage;
      if (funnelContext?.funnelStage) {
        const stageInfo = FUNNEL_STAGE_CONTEXT[funnelContext.funnelStage];
        interpolatedMessage = `Hey! I see you're creating a **${stageInfo.name}** creative${funnelContext.campaignName ? ` for the "${funnelContext.campaignName}" campaign` : ''}. Let's build something that will ${stageInfo.goal.toLowerCase()}.\n\n${conversationFlowService.interpolate(entryPoint.message, { brandName: selectedBrand.name })}`;
      } else {
        interpolatedMessage = conversationFlowService.interpolate(
          entryPoint.message,
          { brandName: selectedBrand.name }
        );
      }

      // Set the initial message directly
      setMessages([{
        id: Date.now(),
        role: 'ai',
        text: interpolatedMessage,
        options: entryPoint.options,
        phase: currentPhase,
        timestamp: new Date()
      }]);
    }
  }, [selectedBrand, searchParams, chatId, funnelContext]);

  // ============================================
  // LOAD SAVED CHAT FROM URL
  // ============================================

  // Load a specific chat by ID
  const handleLoadChat = async (loadChatId) => {
    setIsLoadingChat(true);
    try {
      const chatData = await loadCreativeChat(loadChatId);

      // Restore all chat state including stateSnapshot for rewind functionality
      const restoredMessages = chatData.messages.map(msg => ({
        ...msg,
        timestamp: new Date(msg.timestamp),
        // Preserve stateSnapshot if it was saved (for proper rewind support)
        stateSnapshot: msg.stateSnapshot || null
      }));
      setMessages(restoredMessages);

      const loadedBrief = chatData.creativeBrief || {};

      // Debug: Log product assets being restored
      console.log('[handleLoadChat] loadedBrief.selectedAssets:', loadedBrief.selectedAssets?.length || 0, loadedBrief.selectedAssets);
      console.log('[handleLoadChat] loadedBrief.crawledImages:', loadedBrief.crawledImages?.length || 0, loadedBrief.crawledImages);

      setCreativeBrief(loadedBrief);
      setCurrentPhase(chatData.currentPhase || Object.values(PHASES)[0] || 'strategist');
      setChatId(loadChatId);

      // Restore refs from loaded brief (refs aren't persisted, but the brief is)
      if (loadedBrief.videoSettings?.firstFrame) {
        latestFirstFrameRef.current = loadedBrief.videoSettings.firstFrame;
      }
      if (loadedBrief.videoSettings?.lastFrame) {
        latestLastFrameRef.current = loadedBrief.videoSettings.lastFrame;
      }
      if (loadedBrief.videoSettings?.selectedCasting || loadedBrief.selectedCasting) {
        latestCastingRef.current = loadedBrief.videoSettings?.selectedCasting || loadedBrief.selectedCasting;
      }
      if (loadedBrief.videoSettings?.selectedLocation || loadedBrief.selectedLocation) {
        latestLocationRef.current = loadedBrief.videoSettings?.selectedLocation || loadedBrief.selectedLocation;
      }

      // Recovery: Try to find frames from message history if not in brief
      if (!latestFirstFrameRef.current || !latestLastFrameRef.current) {
        console.log('[handleLoadChat] Attempting to recover frames from messages...');
        for (let i = restoredMessages.length - 1; i >= 0; i--) {
          const msg = restoredMessages[i];
          if (!latestFirstFrameRef.current && msg.data?.firstFrameOptions && restoredMessages[i + 1]?.role === 'user') {
            const userChoice = restoredMessages[i + 1]?.text;
            const matchedFrame = msg.data.firstFrameOptions.find(opt =>
              opt.title === userChoice || opt.name === userChoice
            );
            if (matchedFrame) {
              console.log('[handleLoadChat] Recovered firstFrame from messages:', matchedFrame.title);
              latestFirstFrameRef.current = matchedFrame;
            }
          }
          if (!latestLastFrameRef.current && msg.data?.lastFrameOptions && restoredMessages[i + 1]?.role === 'user') {
            const userChoice = restoredMessages[i + 1]?.text;
            const matchedFrame = msg.data.lastFrameOptions.find(opt =>
              opt.title === userChoice || opt.name === userChoice
            );
            if (matchedFrame) {
              console.log('[handleLoadChat] Recovered lastFrame from messages:', matchedFrame.title);
              latestLastFrameRef.current = matchedFrame;
            }
          }
        }
      }

      // Restore additional UI state from the last message's snapshot or from chatData
      const lastMessage = restoredMessages[restoredMessages.length - 1];
      if (lastMessage?.stateSnapshot) {
        // Restore from snapshot if available
        if (lastMessage.stateSnapshot.briefSheetData) {
          setBriefSheetData(lastMessage.stateSnapshot.briefSheetData);
        }
        if (lastMessage.stateSnapshot.colorPalette && lastMessage.stateSnapshot.colorPalette.length > 0) {
          setColorPalette(lastMessage.stateSnapshot.colorPalette);
        }
        if (lastMessage.stateSnapshot.phaseStep !== undefined) {
          setPhaseStep(lastMessage.stateSnapshot.phaseStep);
        }
        if (lastMessage.stateSnapshot.previousOptionsForStep) {
          setPreviousOptionsForStep(lastMessage.stateSnapshot.previousOptionsForStep);
        }
      } else if (chatData.briefSheetData) {
        // Fallback to chatData level briefSheetData
        setBriefSheetData(chatData.briefSheetData);
      }

      // Update the initialized brand ref to prevent re-initialization
      if (chatData.brandId) {
        initializedBrandRef.current = chatData.brandId;
      }

      // Restore pillar selection if saved
      if (chatData.pillarId && availablePillars.length > 0) {
        const pillar = availablePillars.find(p => p.id === chatData.pillarId);
        if (pillar) {
          setSelectedPillar(pillar);
          console.log('[handleLoadChat] Restored pillar:', pillar.name);
        }
      }

      // Sync the conversation agent with the loaded brief AND rebuild history
      if (conversationAgent) {
        conversationAgent.updateContext(selectedBrand, loadedBrief);

        // Rebuild agent's conversation history from loaded messages
        conversationAgent.resetHistory();
        restoredMessages.forEach(msg => {
          conversationAgent.addToHistory(msg.role, msg.text);
        });

        // Restore pillar context if available
        if (chatData.pillarId && availablePillars.length > 0) {
          const pillar = availablePillars.find(p => p.id === chatData.pillarId);
          if (pillar) {
            conversationAgent.setPillarContext({ pillar });
          }
        }

        console.log('[ConversationalCreativeV2] Synced loaded brief and rebuilt agent history:', {
          brief: loadedBrief,
          historyLength: restoredMessages.length
        });
      }

      // Resume video generation polling if there's a pending operation
      if (chatData.pendingVideoGeneration) {
        const { operationId, startedAt, progressMessageId, provider } = chatData.pendingVideoGeneration;
        console.log('[ConversationalCreativeV2] Found pending video generation, resuming polling:', operationId);

        // Set the pending state
        setPendingVideoGeneration(chatData.pendingVideoGeneration);

        // Set initial progress state for UI
        setVideoProgress({
          operationId,
          status: 'resuming',
          message: 'Resuming video generation check...',
          progress: null,
          provider,
          startedAt
        });

        // Start polling - use a small delay to ensure state is ready
        setTimeout(() => {
          pollVideoStatus(operationId, 0, progressMessageId, startedAt);
        }, 1000);
      }

      console.log('[ConversationalCreativeV2] Chat loaded successfully:', loadChatId);
    } catch (error) {
      console.error('[ConversationalCreativeV2] Error loading chat:', error);
    } finally {
      setIsLoadingChat(false);
    }
  };

  // Load chat from URL parameter on component mount or URL change (e.g., after fork)
  useEffect(() => {
    const urlChatId = searchParams.get('chatId');
    // Load if: URL has chatId AND (no current chatId OR URL chatId differs from current)
    if (urlChatId && currentUser && urlChatId !== chatId) {
      handleLoadChat(urlChatId);
    }
  }, [searchParams, currentUser, chatId]);

  // Reset state when navigating to a new chat (chatId removed from URL)
  useEffect(() => {
    const urlChatId = searchParams.get('chatId');
    // If URL has no chatId but we had one before, reset for new chat
    if (!urlChatId && chatId) {
      console.log('[ConversationalCreativeV2] Resetting state for new chat');
      setChatId(null);
      setMessages([]);
      setImageJobId(null);
      setImageJobRecovered(false);
      initializedBrandRef.current = null; // Allow brand initialization to run again
    }
  }, [searchParams]);

  // Update chat when casting generation completes
  useEffect(() => {
    if (!isGeneratingCasting && castingOptions.length > 0) {
      // Find the last message with showCastingLoading and update it
      setMessages(prev => {
        const lastLoadingIndex = prev.findLastIndex(m => m.data?.showCastingLoading);
        if (lastLoadingIndex === -1) return prev;

        const updated = [...prev];
        updated[lastLoadingIndex] = {
          ...updated[lastLoadingIndex],
          text: "Here are some casting options I've created for your ad. Click on one to select them:",
          data: { castingOptions },
          options: [
            { text: "Regenerate with different options", value: "regenerate_casting", action: "retry_casting" }
          ]
        };
        return updated;
      });
    }
  }, [isGeneratingCasting, castingOptions]);

  // Update chat when location generation completes (video director)
  useEffect(() => {
    if (!isGeneratingLocations && locationOptions.length > 0) {
      setMessages(prev => {
        const lastLoadingIndex = prev.findLastIndex(m => m.data?.showLocationLoading);
        if (lastLoadingIndex === -1) return prev;

        const updated = [...prev];
        updated[lastLoadingIndex] = {
          ...updated[lastLoadingIndex],
          text: "Here are some locations I've scouted for your video. Click on one to select it:",
          data: { locationOptions },
          options: [
            { text: "Scout different locations", value: "regenerate_location", action: "retry_location" }
          ]
        };
        return updated;
      });
    }
  }, [isGeneratingLocations, locationOptions]);

  // Update chat when first frame generation completes (video director)
  useEffect(() => {
    if (!isGeneratingFirstFrame && firstFrameOptions.length > 0) {
      setMessages(prev => {
        const lastLoadingIndex = prev.findLastIndex(m => m.data?.showFirstFrameLoading);
        if (lastLoadingIndex === -1) return prev;

        const updated = [...prev];
        updated[lastLoadingIndex] = {
          ...updated[lastLoadingIndex],
          text: "Here are some opening shots I've composed for your video. Click on one to select it:",
          data: { firstFrameOptions },
          options: [
            { text: "Generate different opening shots", value: "regenerate_first_frame", action: "retry_first_frame" }
          ]
        };
        return updated;
      });
    }
  }, [isGeneratingFirstFrame, firstFrameOptions]);

  // Update chat when last frame generation completes (video director)
  useEffect(() => {
    if (!isGeneratingLastFrame && lastFrameOptions.length > 0) {
      setMessages(prev => {
        const lastLoadingIndex = prev.findLastIndex(m => m.data?.showLastFrameLoading);
        if (lastLoadingIndex === -1) return prev;

        const updated = [...prev];
        updated[lastLoadingIndex] = {
          ...updated[lastLoadingIndex],
          text: "Here are some closing shots I've designed for your video. Click on one to select it:",
          data: { lastFrameOptions },
          options: [
            { text: "Design different closing shots", value: "regenerate_last_frame", action: "retry_last_frame" }
          ]
        };
        return updated;
      });
    }
  }, [isGeneratingLastFrame, lastFrameOptions]);

  // ============================================
  // MESSAGE HANDLING
  // ============================================

  // Create a snapshot of current state to store with messages for revert capability
  const createStateSnapshot = () => ({
    creativeBrief: { ...stateRefs.current.creativeBrief },
    currentPhase: stateRefs.current.currentPhase,
    phaseStep: stateRefs.current.phaseStep,
    previousOptionsForStep: [...stateRefs.current.previousOptionsForStep],
    // Additional UI state for complete rewind
    briefSheetData: stateRefs.current.briefSheetData ? { ...stateRefs.current.briefSheetData } : null,
    colorPalette: stateRefs.current.colorPalette ? [...stateRefs.current.colorPalette] : [],
    // Track agent's conversation history length for proper sync on rewind
    agentHistoryLength: stateRefs.current.conversationAgent?.getHistoryLength() || 0,
    // Track strategist phase completion for proper brand context restoration on rewind
    strategistPhaseComplete: stateRefs.current.conversationAgent?.isStrategistComplete() || false
  });

  const addAIMessage = (text, options = null, data = null, phaseOverride = null) => {
    const message = {
      id: Date.now(),
      role: 'ai',
      text,
      options,
      data,
      phase: phaseOverride || currentPhase,
      timestamp: new Date(),
      stateSnapshot: createStateSnapshot() // Store state at time of message
    };

    setMessages(prev => [...prev, message]);
    return message;
  };

  const addUserMessage = (text, data = null) => {
    const message = {
      id: Date.now(),
      role: 'user',
      text,
      data,
      phase: currentPhase,
      timestamp: new Date(),
      stateSnapshot: createStateSnapshot() // Store state at time of message
    };

    setMessages(prev => [...prev, message]);
    return message;
  };

  // Delete a message and revert state to before that message
  const handleDeleteMessage = (messageIndex) => {
    if (messageIndex < 0 || messageIndex >= messages.length) return;

    const messageToDelete = messages[messageIndex];
    const remainingMessages = messages.slice(0, messageIndex);

    // Get the initial state for complete reset
    const getInitialState = () => ({
      creativeBrief: conversationFlowService.getEmptyCreativeBrief(),
      currentPhase: Object.values(PHASES)[0] || 'strategist',
      phaseStep: 0,
      previousOptionsForStep: [],
      briefSheetData: null,
      colorPalette: [],
      agentHistoryLength: 0
    });

    // Get state to revert to from the deleted message's snapshot (state BEFORE this message was added)
    const stateToRevert = messageIndex > 0 && messageToDelete.stateSnapshot
      ? messageToDelete.stateSnapshot
      : getInitialState();

    // Determine the phase from the last remaining message (for UI consistency)
    let lastMessagePhase = stateToRevert?.currentPhase || Object.values(PHASES)[0] || 'strategist';
    if (remainingMessages.length > 0) {
      const lastMessage = remainingMessages[remainingMessages.length - 1];
      if (lastMessage.phase) {
        lastMessagePhase = lastMessage.phase;
      }
    }

    // Remove all messages from the deleted one onwards
    setMessages(remainingMessages);

    // Revert ALL state from snapshot
    setCreativeBrief(stateToRevert.creativeBrief || conversationFlowService.getEmptyCreativeBrief());
    setCurrentPhase(lastMessagePhase);
    setPhaseStep(stateToRevert.phaseStep ?? 0);
    setPreviousOptionsForStep(stateToRevert.previousOptionsForStep || []);

    // Revert UI states from snapshot (no special-case logic needed when snapshot has them)
    if (stateToRevert.briefSheetData !== undefined) {
      setBriefSheetData(stateToRevert.briefSheetData);
    } else {
      // Fallback for old messages without briefSheetData in snapshot
      const stillHasBriefReview = remainingMessages.some(m => m.phase === 'brief_review');
      if (!stillHasBriefReview) {
        setBriefSheetData(null);
      }
    }

    if (stateToRevert.colorPalette !== undefined && stateToRevert.colorPalette.length > 0) {
      setColorPalette(stateToRevert.colorPalette);
    } else {
      // Fallback: check if we're going back before color selection
      const colorStepExists = remainingMessages.some(m => m.data?.showColorSwatches);
      if (!colorStepExists) {
        const visual = selectedBrand?.visual || {};
        const brandColors = [
          visual.primaryColor,
          visual.secondaryColor,
          visual.accentColor
        ].filter(Boolean);
        setColorPalette(brandColors);
      }
    }

    // Update conversation agent with reverted brief AND sync history
    if (conversationAgent) {
      conversationAgent.updateContext(selectedBrand, stateToRevert.creativeBrief || {});

      // Truncate agent's conversation history to match the rewind point
      if (stateToRevert.agentHistoryLength !== undefined) {
        conversationAgent.truncateHistory(stateToRevert.agentHistoryLength);
      } else {
        // Fallback: reset history if no length was tracked (old snapshots)
        conversationAgent.resetHistory();
        // Rebuild history from remaining messages
        remainingMessages.forEach(msg => {
          conversationAgent.addToHistory(msg.role, msg.text);
        });
      }

      // Restore strategist phase completion status from snapshot
      // This ensures brand context is properly included/excluded after rewind
      if (stateToRevert.strategistPhaseComplete !== undefined) {
        if (stateToRevert.strategistPhaseComplete) {
          // If it was complete at the snapshot point, mark it complete again
          if (!conversationAgent.isStrategistComplete()) {
            conversationAgent.markStrategistComplete();
          }
        } else {
          // If it wasn't complete at the snapshot point, reset it
          conversationAgent.resetStrategistComplete();
        }
      } else if (lastMessagePhase === 'strategist') {
        // Fallback for old snapshots without strategistPhaseComplete
        conversationAgent.resetStrategistComplete();
      }
    }

    // Reset editing state
    setIsEditingPhase(false);
    setShowColorPicker(false);
    setEditingColorIndex(null);
    setTempColor('');

    // Reset casting state if we're going back before casting was generated
    const castingStepExists = remainingMessages.some(m => m.data?.castingOptions || m.data?.showCastingLoading);
    if (!castingStepExists) {
      setCastingOptions([]);
      setCastingError(null);
      setIsGeneratingCasting(false);
      setZoomedCastingOption(null);
    }

    // Reset image selection state
    setIsSelectingImages(false);
    setPendingImageSelections([]);

    console.log('[ConversationalCreativeV2] Message deleted, state reverted:', {
      deletedIndex: messageIndex,
      remainingCount: remainingMessages.length,
      revertedPhase: lastMessagePhase,
      revertedStep: stateToRevert?.phaseStep,
      agentHistoryLength: stateToRevert?.agentHistoryLength
    });
  };

  // Fork handlers
  const handleForkFromMessage = (messageIndex) => {
    // Don't allow forking if chat hasn't been saved yet
    if (!chatId) {
      console.warn('Cannot fork: chat has not been saved yet');
      return;
    }
    setForkConfirmation({ messageIndex, show: true });
  };

  const handleConfirmFork = async () => {
    if (!forkConfirmation || !chatId || !currentUser) return;

    try {
      const newChatId = await forkCreativeChat(
        chatId,
        forkConfirmation.messageIndex,
        currentUser.uid
      );
      // Navigate to the new forked chat
      navigate(`/conversational?chatId=${newChatId}`);
    } catch (error) {
      console.error('Fork failed:', error);
    } finally {
      setForkConfirmation(null);
    }
  };

  // ============================================
  // OPTION HANDLING
  // ============================================

  const handleOptionSelect = async (option, messageContext = null) => {
    console.log('[handleOptionSelect] Called with:', {
      optionText: option.text,
      optionAction: option.action,
      optionValue: option.value,
      hasMessageContext: !!messageContext,
      messageHasData: !!messageContext?.data,
      messageHasGeneratedImages: !!messageContext?.data?.generatedImages,
      generatedImagesCount: messageContext?.data?.generatedImages?.length
    });

    // Add user message
    addUserMessage(option.text);

    // Check for special actions (brainstorm, regenerate_options, etc.)
    if (option.action) {
      await handleSpecialAction(option, messageContext);
      return;
    }

    // Fallback: handle known action values even if action property is missing
    // This handles cases where messages were saved before action was preserved
    if (option.value === 'save_generated_images' || option.value === 'save_to_library') {
      console.log('[handleOptionSelect] Fallback: handling save_generated_images via value');
      await handleSaveGeneratedImagesToLibrary(messageContext?.data?.generatedImages);
      return;
    }

    if (option.value === 'view_library') {
      console.log('[handleOptionSelect] Fallback: handling view_library via value');
      window.location.href = '/library';
      return;
    }

    if (option.value === 'generate_more' || option.value === 'regenerate_images') {
      console.log('[handleOptionSelect] Fallback: handling regenerate via value');
      await generateImageOrCarouselInChat(creativeBrief);
      return;
    }

    // Check for entry point selection
    if (option.value === 'guided' || option.value === 'direct') {
      handleFlowModeSelect(option.value);
      return;
    }

    // Handle image selection from crawled images
    if (option.value === 'select_retrieved_images') {
      showRetrievedImageSelector();
      return;
    }

    // Handle individual image selection
    if (typeof option.value === 'string' && option.value.startsWith('select_image_')) {
      handleImageSelection(option);
      return;
    }

    // Handle "use all images" action
    if (option.value === 'use_all_retrieved') {
      handleUseAllImages();
      return;
    }

    // Handle "confirm selected images" action
    if (option.value === 'confirm_image_selection') {
      handleConfirmImageSelection();
      return;
    }

    // Handle "confirm library selection" action
    if (option.value === 'confirm_library_selection') {
      handleConfirmLibrarySelection();
      return;
    }

    // Handle skip/finish assets - advance the flow
    if (option.value === 'finish_assets') {
      setIsSelectingImages(false);
      setPendingImageSelections([]);
      await advanceFlow();
      return;
    }

    // Handle skip_assets - ask user to describe the image they want generated
    if (option.value === 'skip_assets') {
      setIsSelectingImages(false);
      setPendingImageSelections([]);
      // Set a flag to indicate we're in image description mode
      setIsDescribingImage(true);
      addAIMessage(
        "No problem! Since we're generating from scratch, tell me what you'd like the image to show. Describe the scene, style, or visual elements you're envisioning:",
        [],
        { awaitingImageDescription: true }
      );
      return;
    }

    // Handle cancel card refinement - clean up state and show the generated images again
    if (option.value === 'cancel_card_refinement') {
      updateCreativeBrief('refiningCardIndex', null);
      updateCreativeBrief('refiningCardImage', null);
      updateCreativeBrief('refinementReferenceImage', null);

      const generatedImages = creativeBrief.generatedImages;
      if (generatedImages && generatedImages.length > 0) {
        addAIMessage("Refinement cancelled. Here are your generated images:", [
          { text: "📋 Copy Caption", value: 'copy_caption', action: 'copy_caption' },
          { text: "💾 Save to Library", value: 'save_to_library', action: 'save_generated_images' },
          { text: "🔄 Generate More", value: 'generate_more', action: 'regenerate_images' }
        ], {
          generatedImages,
          isCarousel: creativeBrief.outputType === 'carousel',
          socialMediaPost: creativeBrief.socialMediaCaption ? {
            caption: creativeBrief.socialMediaCaption.caption,
            hashtags: creativeBrief.socialMediaCaption.hashtags,
            platform: creativeBrief.stillSettings?.platform || 'instagram',
            brandName: selectedBrand?.name || creativeBrief.product || ''
          } : null
        });
      }
      return;
    }

    // Handle "select from asset library" action
    if (option.value === 'select_from_library') {
      setIsSelectingImages(false);
      setPendingImageSelections([]);
      showAssetLibrarySelector();
      return;
    }

    // Check for custom input requests - handles "custom", "custom_fieldName", and "enter_own" formats
    // These are meta-options that tell us the user wants to type their own answer
    if (option.value === 'custom' || option.value === 'enter_own' || (typeof option.value === 'string' && option.value.startsWith('custom_'))) {
      handleCustomInput(option);
      return;
    }

    // Update creative brief if briefKey is specified
    // IMPORTANT: We must create the updated brief BEFORE calling advanceFlow
    // because React state updates are async and won't be available immediately
    let updatedBrief = creativeBrief;

    // Debug: Log if scenario is preserved in current brief
    console.log('[handleOptionSelect] Current creativeBrief has selectedStoryScenario:', !!creativeBrief.selectedStoryScenario);

    if (option.briefKey) {
      // Determine what value to store:
      // - For fields used in condition evaluation (outputType, videoSettings.dialogueType),
      //   we MUST use option.value as it matches the expected enum values in conditions
      //   e.g., conditions like "outputType === 'video'" need the value "video", not the
      //   display text "Video Ad - Motion that tells a story"
      // - For polished/original version selections, option.value contains the actual content
      // - For regular descriptive fields, option.text has the human-readable description
      const fieldsRequiringEnumValue = [
        'outputType',                    // Used in: "outputType === 'video'", phase routing
        'videoSettings.dialogueType',    // Used in: "videoSettings.dialogueType === 'voiceover'", step skipping
        'stillSettings.variationCount',  // Numeric: parseInt() needs the number, not text
        'stillSettings.carouselCardCount', // Numeric: parseInt() needs the number, not text
        'imageType',                     // Used in: "imageType === 'infographic'" for prompt construction
        'infographicType',               // Used in prompt: 'data_visualization', 'process_flow', etc.
        'infographicSections'            // Numeric: 3, 4, 5, 6 sections
      ];

      const isPolishedOrOriginalChoice = option.text?.includes('Polished Version') || option.text === 'Original';
      const requiresEnumValue = fieldsRequiringEnumValue.includes(option.briefKey);

      // Use option.value for enum fields and polished/original choices, otherwise use option.text
      const valueToStore = (requiresEnumValue || isPolishedOrOriginalChoice)
        ? option.value
        : (option.text || option.value);

      // Create updated brief for immediate use (React state update is async)
      // IMPORTANT: Preserve selectedStoryScenario which may have been set by handleScenarioSelect
      if (option.briefKey.includes('.')) {
        updatedBrief = conversationFlowService.setNestedValue({ ...creativeBrief }, option.briefKey, valueToStore);
      } else {
        updatedBrief = { ...creativeBrief, [option.briefKey]: valueToStore };
      }

      // Debug: Verify scenario is preserved in updated brief
      console.log('[handleOptionSelect] After update - updatedBrief has selectedStoryScenario:', !!updatedBrief.selectedStoryScenario);

      // Schedule the React state update
      updateCreativeBrief(option.briefKey, valueToStore);

      // Trigger background casting generation when peopleDescription or videoSettings.actors is collected
      // First shows favorites if they exist, otherwise generates new
      if (option.briefKey === 'peopleDescription' || option.briefKey === 'videoSettings.actors') {
        loadOrGenerateCasting(valueToStore, updatedBrief);
      }

      // Trigger background location generation when scenery is collected (video director)
      // First shows favorites if they exist, otherwise generates new
      if (option.briefKey === 'videoSettings.scenery') {
        loadOrGenerateLocations(valueToStore, updatedBrief);
      }

      // Trigger story arc scenario generation when a narrative structure is selected
      // This shows AI-generated scenarios for the user to choose from before continuing
      // NOTE: We check option.value (not valueToStore) because valueToStore may be the display text
      if (option.briefKey === 'stillSettings.storyArcType' &&
        ['problem_solution', 'before_after', 'feature_showcase', 'educational_steps'].includes(option.value)) {
        // Store the enum value in the brief for scenario generation
        const storyArcBrief = {
          ...updatedBrief,
          stillSettings: {
            ...updatedBrief.stillSettings,
            storyArcType: option.value // Use the enum value
          }
        };
        await generateStoryArcScenarios(option.value, storyArcBrief);
        return; // Don't advance - wait for scenario selection
      }
    }

    // Advance to next step with the updated brief (not the stale React state)
    await advanceFlow(updatedBrief);
  };

  const handleSpecialAction = async (option, messageContext = null) => {
    switch (option.action) {
      case 'regenerate_options':
        await handleRegenerateOptions();
        break;
      case 'brainstorm':
        await handleBrainstorm();
        break;
      case 'brainstorm_audiences':
        await handleBrainstormAudiences();
        break;
      case 'phase_confirm':
      case 'phase_change':
        await handlePhaseSummaryResponse(option);
        break;
      case 'edit_field':
        handleEditField(option.value);
        break;
      case 'regenerate_with_feedback':
        // For now, treat as simple regeneration, but prompt for feedback could be added here
        await handleRegenerateOptions();
        break;
      case 'retry_casting':
        // Retry casting image generation
        await handleRetryCasting();
        // Re-execute the step to show updated results
        await executeStep(currentPhase, phaseStep);
        break;
      case 'retry_location':
        // Retry location image generation (video director)
        await handleRetryLocation();
        await executeStep(currentPhase, phaseStep);
        break;
      case 'retry_first_frame':
        // Retry first frame generation (video director)
        await handleRetryFirstFrame();
        await executeStep(currentPhase, phaseStep);
        break;
      case 'retry_last_frame':
        // Retry last frame generation (video director)
        await handleRetryLastFrame();
        await executeStep(currentPhase, phaseStep);
        break;
      case 'skip_current_step':
        // Skip the current step and advance to next
        await handleSkipCurrentStep();
        break;
      case 'retry_script':
        // Retry script generation from chat
        await handleScriptRegenerateFromChat();
        break;
      case 'retry_generation':
        // Retry image/carousel generation
        await generateImageOrCarouselInChat(creativeBrief);
        break;
      case 'save_generated_images':
        // Save generated images to library - use images from message context if available
        console.log('[save_generated_images] messageContext:', {
          hasContext: !!messageContext,
          hasData: !!messageContext?.data,
          hasGeneratedImages: !!messageContext?.data?.generatedImages,
          imageCount: messageContext?.data?.generatedImages?.length
        });
        await handleSaveGeneratedImagesToLibrary(messageContext?.data?.generatedImages);
        break;
      case 'regenerate_images':
        // Generate more images
        await generateImageOrCarouselInChat(creativeBrief);
        break;
      case 'refine_generated_images':
        // Find the most recent generated images from messages
        const recentGenMessage = [...messages].reverse().find(m => m.data?.generatedImages?.length > 0);
        const imagesToRefine = recentGenMessage?.data?.generatedImages || creativeBrief.generatedImages || [];

        if (imagesToRefine.length === 0) {
          addAIMessage("I couldn't find any images to refine. Please generate images first.");
          break;
        }

        // Store the images being refined and set flag
        updateCreativeBrief('imagesToRefine', imagesToRefine);

        // Prompt user for refinement feedback with proper state tracking
        addAIMessage("What would you like to change about the generated images? Describe your refinements and I'll regenerate.", [
          { text: "📷 Add Reference Image", value: 'add_refinement_reference', action: 'add_refinement_reference' },
          { text: "Cancel", value: 'cancel_refinement' }
        ], {
          awaitingImageRefinement: true,
          imagesToRefine
        });
        break;
      case 'cancel_refinement':
        // Cancel refinement and clean up state
        updateCreativeBrief('imagesToRefine', null);
        updateCreativeBrief('refinementReferenceImage', null);
        addAIMessage("Refinement cancelled. What would you like to do next?", [
          { text: "Save to Library", value: 'save_generated_images', action: 'save_generated_images' },
          { text: "Regenerate", value: 'regenerate_images', action: 'regenerate_images' }
        ]);
        break;
      case 'add_refinement_reference':
        // Add a reference image for card refinement
        handleAddRefinementReference();
        break;
      case 'remove_refinement_reference':
        // Remove the reference image
        handleRemoveRefinementReference();
        break;
      case 'upload_refinement_reference':
        // Upload a new reference image from device
        handleUploadRefinementReference();
        break;
      case 'select_refinement_from_crawled':
        // Select reference from crawled website images
        handleSelectRefinementFromCrawled();
        break;
      case 'select_refinement_from_library':
        // Select reference from asset library
        handleSelectRefinementFromLibrary();
        break;
      case 'copy_caption':
        // Copy caption and hashtags to clipboard
        handleCopyCaption();
        break;
      case 'edit_caption':
        // Allow user to edit the caption
        addAIMessage("Type your edited caption below and I'll update it:", [
          { text: "Keep Original", value: 'keep_caption' }
        ]);
        break;
      case 'retry_scenarios':
        // Retry scenario generation
        await generateStoryArcScenarios(
          creativeBrief.stillSettings?.storyArcType || 'problem_solution',
          creativeBrief
        );
        break;
      case 'skip_scenarios':
        // Skip scenario selection and continue with defaults
        addAIMessage("Continuing with default content. Let's set up the visual details.");
        await advanceFlow(creativeBrief);
        break;
      case 'custom_scenario':
        // Let user write their own scenario
        handleCustomScenarioInput();
        break;
      case 'edit_scenario':
        // Edit a generated scenario
        handleEditScenario(option.scenario);
        break;
      case 'save_edited_scenario':
        // Save edited scenario and continue
        await handleSaveEditedScenario();
        break;
      case 'confirm_custom_scenario':
        // Confirm and use the custom scenario
        // First try to get scenario from option data (most reliable), then fall back to state
        const pendingScenario = option.scenario || creativeBrief.pendingCustomScenario;
        console.log('[confirm_custom_scenario] Using scenario:', {
          fromOption: !!option.scenario,
          fromState: !!creativeBrief.pendingCustomScenario,
          scenarioTitle: pendingScenario?.title
        });
        if (pendingScenario) {
          await handleScenarioSelect(pendingScenario);
        } else {
          console.error('[confirm_custom_scenario] No scenario found!');
          addAIMessage("Sorry, I lost track of your scenario. Please try again.", [
            { text: "Try Again", value: 'custom_scenario', action: 'custom_scenario' }
          ]);
        }
        break;
      case 'directModeFormat':
        // Handle direct mode format selection
        handleDirectModeFormatSelect(option.value);
        break;
      case 'directModeAspectRatio':
        // Handle direct mode aspect ratio selection and trigger generation
        await handleDirectModeAspectRatioSelect(option.value);
        break;
      case 'view_library':
        // Navigate to the asset library
        window.location.href = '/library';
        break;
      default:
        console.warn(`Unknown action: ${option.action}`);
        // For unknown actions, try to advance anyway to prevent stalling
        await advanceFlow();
    }
  };

  // Handle saving generated images to library
  const handleSaveGeneratedImagesToLibrary = async (imagesFromMessage = null) => {
    console.log('[handleSaveGeneratedImagesToLibrary] Called with:', {
      hasImagesFromMessage: !!imagesFromMessage,
      imagesFromMessageCount: imagesFromMessage?.length,
      creativeBriefImagesCount: creativeBrief.generatedImages?.length
    });

    // Use images from the message context if provided, otherwise fall back to creativeBrief
    const generatedImages = imagesFromMessage || creativeBrief.generatedImages;
    console.log('[handleSaveGeneratedImagesToLibrary] Using generatedImages:', {
      count: generatedImages?.length,
      firstImageUrl: generatedImages?.[0]?.imageUrl?.substring(0, 50)
    });

    if (!generatedImages || generatedImages.length === 0) {
      addAIMessage("No generated images to save.");
      return;
    }

    if (!currentUser?.uid || !selectedBrand?.id) {
      addAIMessage("Please log in and select a brand to save images to your library.");
      return;
    }

    try {
      const isCarousel = creativeBrief.outputType === 'carousel' && generatedImages.length > 1;

      if (isCarousel) {
        // Save carousel as a single grouped item with all data
        const scenario = creativeBrief.selectedStoryScenario;
        const caption = creativeBrief.socialMediaCaption;

        const carouselItem = {
          name: scenario?.title || `Carousel - ${creativeBrief.product || 'AI Generated'}`,
          description: scenario?.description || creativeBrief.product || 'AI Generated Carousel',
          type: 'carousel',
          // Primary image for thumbnail
          imageUrl: generatedImages[0]?.imageUrl || '',
          // All carousel images
          carouselImages: generatedImages.map((img, idx) => ({
            imageUrl: img.imageUrl || '',
            title: img.title || `Card ${idx + 1}`,
            position: idx + 1,
            headline: scenario?.cards?.[idx]?.headline || '',
            subtext: scenario?.cards?.[idx]?.subtext || '',
            role: scenario?.cards?.[idx]?.role || ''
          })),
          // Caption data
          caption: caption?.caption || '',
          hashtags: caption?.hashtags || '',
          fullPost: caption?.fullPost || '',
          // Scenario data
          scenario: scenario ? {
            id: scenario.id || 'custom',
            title: scenario.title || '',
            storyArcType: scenario.storyArcType || '',
            isCustom: scenario.isCustom || false
          } : null,
          tags: ['carousel', 'ai-generated', creativeBrief.stillSettings?.storyArcType || 'story-arc'].filter(Boolean),
          metadata: {
            product: creativeBrief.product || '',
            audience: creativeBrief.audience || '',
            platform: creativeBrief.stillSettings?.platform || 'instagram',
            aspectRatio: creativeBrief.stillSettings?.aspectRatio || '1:1',
            cardCount: generatedImages.length,
            generatedAt: new Date().toISOString()
          }
        };

        await batchSaveItems([carouselItem], currentUser.uid, selectedBrand.id);
        addAIMessage(`Saved carousel with ${generatedImages.length} cards to your library!`, [
          { text: "View Library", value: 'view_library', action: 'view_library' },
          { text: "Generate More", value: 'generate_more', action: 'regenerate_images' }
        ]);
      } else {
        // Save individual images
        const itemsToSave = generatedImages.map((img, idx) => ({
          name: img.title || `Generated Image ${idx + 1}`,
          description: img.description || creativeBrief.product || 'AI Generated',
          imageUrl: img.imageUrl || '',
          imagePrompt: img.prompt?.substring(0, 500) || '',
          type: 'image',
          tags: ['image', 'ai-generated'],
          metadata: {
            product: creativeBrief.product || '',
            audience: creativeBrief.audience || '',
            generatedAt: new Date().toISOString()
          }
        }));

        await batchSaveItems(itemsToSave, currentUser.uid, selectedBrand.id);
        addAIMessage(`Saved ${generatedImages.length} image${generatedImages.length > 1 ? 's' : ''} to your library!`, [
          { text: "View Library", value: 'view_library', action: 'view_library' },
          { text: "Generate More", value: 'generate_more', action: 'regenerate_images' }
        ]);
      }
    } catch (error) {
      console.error('Error saving generated images:', error);
      addAIMessage(`Failed to save images: ${error.message}`, [
        { text: "Try Again", value: 'save_to_library', action: 'save_generated_images' }
      ]);
    }
  };

  // Handle refining a specific carousel card
  const handleRefineCarouselCard = (image, cardIndex, allImages) => {
    // Store the card being refined
    updateCreativeBrief('refiningCardIndex', cardIndex);
    updateCreativeBrief('refiningCardImage', image);
    updateCreativeBrief('refinementReferenceImage', null); // Reset any previous reference

    // Get the scenario card content if available
    const scenarioCard = creativeBrief.selectedStoryScenario?.cards?.find(c => c.position === cardIndex + 1);
    const cardInfo = scenarioCard
      ? `Card ${cardIndex + 1}: "${scenarioCard.headline}"`
      : `Card ${cardIndex + 1}`;

    addAIMessage(
      `What would you like to change about ${cardInfo}?\n\nDescribe your refinements (e.g., "make the text more visible", "change the background color", "add more contrast").\n\nYou can also add a reference image to guide the refinement.`,
      [
        { text: "📷 Add Reference Image", value: 'add_refinement_reference', action: 'add_refinement_reference' },
        { text: "Cancel", value: 'cancel_card_refinement' }
      ],
      { awaitingCardRefinement: true, cardIndex, originalImage: image, allImages }
    );
  };

  // Handle adding a reference image for refinement - show source options
  const handleAddRefinementReference = () => {
    const crawledImages = creativeBrief.crawledImages || [];
    const brandAssets = selectedBrand?.assets || [];

    // Build options based on available sources
    const options = [
      { text: "📤 Upload from device", value: 'upload_refinement_reference', action: 'upload_refinement_reference' }
    ];

    if (crawledImages.length > 0) {
      options.push({ text: "🌐 From website images", value: 'select_refinement_from_crawled', action: 'select_refinement_from_crawled' });
    }

    if (brandAssets.length > 0) {
      options.push({ text: "📚 From asset library", value: 'select_refinement_from_library', action: 'select_refinement_from_library' });
    }

    options.push({ text: "Cancel", value: 'cancel_card_refinement' });

    addAIMessage(
      "Where would you like to get the reference image from?",
      options,
      {
        awaitingCardRefinement: true,
        cardIndex: creativeBrief.refiningCardIndex,
        originalImage: creativeBrief.refiningCardImage
      }
    );
  };

  // Upload reference image from device
  const handleUploadRefinementReference = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;

      try {
        const reader = new FileReader();
        reader.onload = () => {
          const base64 = reader.result;
          setRefinementReferenceAndConfirm(base64);
        };
        reader.readAsDataURL(file);
      } catch (error) {
        console.error('Error loading reference image:', error);
        addAIMessage("Failed to load the image. Please try again.");
      }
    };
    input.click();
  };

  // Show crawled images for reference selection
  const handleSelectRefinementFromCrawled = () => {
    const crawledImages = creativeBrief.crawledImages || [];
    if (crawledImages.length === 0) {
      addAIMessage("No website images available. Try uploading instead.", [
        { text: "📤 Upload from device", value: 'upload_refinement_reference', action: 'upload_refinement_reference' },
        { text: "Cancel", value: 'cancel_card_refinement' }
      ], {
        awaitingCardRefinement: true,
        cardIndex: creativeBrief.refiningCardIndex,
        originalImage: creativeBrief.refiningCardImage
      });
      return;
    }

    // Create selectable images for reference
    const selectableImages = crawledImages.slice(0, 8).map((img, i) => ({
      id: `ref-crawled-${i}`,
      index: i,
      url: img.url || img,
      alt: img.alt || `Image ${i + 1}`,
      source: 'crawled'
    }));

    addAIMessage(
      "Select an image to use as reference for the refinement:",
      [
        { text: "📤 Upload instead", value: 'upload_refinement_reference', action: 'upload_refinement_reference' },
        { text: "Cancel", value: 'cancel_card_refinement' }
      ],
      {
        awaitingCardRefinement: true,
        cardIndex: creativeBrief.refiningCardIndex,
        originalImage: creativeBrief.refiningCardImage,
        refinementSelectableImages: selectableImages,
        refinementImageSource: 'crawled'
      }
    );
  };

  // Show library images for reference selection
  const handleSelectRefinementFromLibrary = () => {
    const brandAssets = selectedBrand?.assets || [];
    if (brandAssets.length === 0) {
      addAIMessage("Your asset library is empty. Try uploading instead.", [
        { text: "📤 Upload from device", value: 'upload_refinement_reference', action: 'upload_refinement_reference' },
        { text: "Cancel", value: 'cancel_card_refinement' }
      ], {
        awaitingCardRefinement: true,
        cardIndex: creativeBrief.refiningCardIndex,
        originalImage: creativeBrief.refiningCardImage
      });
      return;
    }

    // Create selectable images for reference
    const selectableImages = brandAssets.slice(0, 8).map((asset, i) => ({
      id: `ref-library-${i}`,
      index: i,
      url: asset.url || asset.imageUrl || asset,
      alt: asset.name || asset.alt || `Asset ${i + 1}`,
      source: 'library'
    }));

    addAIMessage(
      "Select an image from your library to use as reference:",
      [
        { text: "📤 Upload instead", value: 'upload_refinement_reference', action: 'upload_refinement_reference' },
        { text: "Cancel", value: 'cancel_card_refinement' }
      ],
      {
        awaitingCardRefinement: true,
        cardIndex: creativeBrief.refiningCardIndex,
        originalImage: creativeBrief.refiningCardImage,
        refinementSelectableImages: selectableImages,
        refinementImageSource: 'library'
      }
    );
  };

  // Handle clicking a refinement reference image
  const handleRefinementImageClick = (imageUrl) => {
    setRefinementReferenceAndConfirm(imageUrl);
  };

  // Common function to set reference and show confirmation
  const setRefinementReferenceAndConfirm = (imageUrl) => {
    updateCreativeBrief('refinementReferenceImage', imageUrl);

    addAIMessage(
      `Reference image added! Now describe what changes you want to make, and I'll use this image as a guide.`,
      [
        { text: "🗑️ Remove Reference", value: 'remove_refinement_reference', action: 'remove_refinement_reference' },
        { text: "Cancel", value: 'cancel_card_refinement' }
      ],
      {
        awaitingCardRefinement: true,
        cardIndex: creativeBrief.refiningCardIndex,
        originalImage: creativeBrief.refiningCardImage,
        refinementReferencePreview: imageUrl
      }
    );
  };

  // Remove refinement reference image
  const handleRemoveRefinementReference = () => {
    updateCreativeBrief('refinementReferenceImage', null);

    const cardIndex = creativeBrief.refiningCardIndex;
    const scenarioCard = creativeBrief.selectedStoryScenario?.cards?.find(c => c.position === cardIndex + 1);
    const cardInfo = scenarioCard
      ? `Card ${cardIndex + 1}: "${scenarioCard.headline}"`
      : `Card ${cardIndex + 1}`;

    addAIMessage(
      `Reference image removed. Describe your refinements for ${cardInfo}:`,
      [
        { text: "📷 Add Reference Image", value: 'add_refinement_reference', action: 'add_refinement_reference' },
        { text: "Cancel", value: 'cancel_card_refinement' }
      ],
      {
        awaitingCardRefinement: true,
        cardIndex,
        originalImage: creativeBrief.refiningCardImage
      }
    );
  };

  // Process card refinement request
  const processCardRefinement = async (refinementText) => {
    const cardIndex = creativeBrief.refiningCardIndex;
    const originalImage = creativeBrief.refiningCardImage;
    const referenceImage = creativeBrief.refinementReferenceImage;

    if (cardIndex === undefined || !originalImage) {
      addAIMessage("Sorry, I lost track of which card you were refining. Please try again.");
      return;
    }

    addAIMessage(`✨ Refining card ${cardIndex + 1}${referenceImage ? ' with reference image' : ''}...`, null, { showImageGenerationLoading: true });

    try {
      // Get the original prompt and scenario card
      const scenarioCard = creativeBrief.selectedStoryScenario?.cards?.find(c => c.position === cardIndex + 1);

      // Polish the refinement prompt by analyzing the original image
      let polishedPrompt = refinementText;
      try {
        console.log(`[${new Date().toISOString()}] [ConversationalCreativeV2] Polishing card refinement prompt with image analysis...`);
        const polishResponse = await fetch('http://localhost:3001/api/polish-refinement-prompt', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            originalImageUrl: originalImage.imageUrl,
            userInstructions: refinementText,
            referenceImages: referenceImage ? [referenceImage] : [],
            context: {
              imageType: 'carousel_card',
              cardIndex: cardIndex + 1,
              headline: scenarioCard?.headline,
              subtext: scenarioCard?.subtext
            }
          })
        });

        if (polishResponse.ok) {
          const polishData = await polishResponse.json();
          polishedPrompt = polishData.polishedPrompt;
          console.log(`[${new Date().toISOString()}] [ConversationalCreativeV2] Polished card prompt received:`, polishedPrompt.substring(0, 100) + '...');
        } else {
          console.warn(`[${new Date().toISOString()}] [ConversationalCreativeV2] Polish endpoint failed, using original refinement text`);
        }
      } catch (polishError) {
        console.warn(`[${new Date().toISOString()}] [ConversationalCreativeV2] Polish request failed, using original refinement text:`, polishError.message);
      }

      // Build refinement prompt using the polished version
      const refinementPrompt = `IMAGE REFINEMENT REQUEST (Card ${cardIndex + 1}):

${polishedPrompt}

ADDITIONAL CONTEXT:
${scenarioCard ? `- Original Headline: "${scenarioCard.headline}"` : ''}
${scenarioCard ? `- Original Subtext: "${scenarioCard.subtext}"` : ''}

INSTRUCTIONS:
- Keep the same overall composition and layout.
- Apply the requested changes from the polished prompt above.
- **CONDITIONAL STYLE**: If the user explicitly asks to use the "reference image" or "Image B" for style/font/colors, apply those elements. Otherwise, treat reference images as optional visual context.
- **FONT ACCURACY**: Only if explicitly requested, mimic the specific typography characteristics (weight, serif type, spacing) from the reference image.
- Maintain brand consistency and preserve the card's role in the carousel.

OUTPUT: A refined version with the changes applied.`;

      // Build reference images array
      const referenceImages = [{
        url: originalImage.imageUrl,
        instruction: 'This is the original image to refine. Keep the same composition and improve based on the polished refinement prompt.'
      }];

      // Add user-supplied reference image if provided
      if (referenceImage) {
        referenceImages.push({
          url: referenceImage,
          instruction: 'STYLE REFERENCE: Use this image as inspiration for the refinement. Incorporate relevant visual elements, colors, or style.'
        });
      }

      let response;
      let retries = 0;
      const MAX_RETRIES = 1;

      while (retries <= MAX_RETRIES) {
        response = await fetch('http://localhost:3001/api/generate-image-gemini', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            prompt: refinementPrompt,
            referenceImages,
            aspectRatio: creativeBrief.stillSettings?.aspectRatio || '1:1',
            userId: currentUser?.uid
          })
        });

        if (response.ok) break;

        const errorData = await response.json().catch(() => ({}));
        const isOverloaded = response.status === 500 && (
          errorData.details?.toLowerCase().includes('overloaded') ||
          errorData.error?.toLowerCase().includes('overloaded')
        );

        if (isOverloaded && retries < MAX_RETRIES) {
          retries++;
          addAIMessage(`⚠️ The image model is currently overloaded. Automatically retrying in 60 seconds (Attempt ${retries}/${MAX_RETRIES})...`);
          await new Promise(resolve => setTimeout(resolve, 60000));
          continue;
        }

        throw new Error(errorData.details || errorData.error || `HTTP ${response.status}`);
      }

      const data = await response.json();

      // Update the generated images array with the refined image
      const updatedImages = [...(creativeBrief.generatedImages || [])];
      updatedImages[cardIndex] = {
        ...originalImage,
        imageUrl: data.url,
        title: `Card ${cardIndex + 1} (Refined)`,
        isRefined: true
      };

      updateCreativeBrief('generatedImages', updatedImages);
      updateCreativeBrief('refiningCardIndex', null);
      updateCreativeBrief('refiningCardImage', null);
      updateCreativeBrief('refinementReferenceImage', null); // Clean up reference image

      // Remove loading and show success
      setMessages(prev => prev.filter(m => !m.data?.showImageGenerationLoading));
      addAIMessage(`✅ Card ${cardIndex + 1} has been refined!`, [
        { text: "📋 Copy Caption", value: 'copy_caption', action: 'copy_caption' },
        { text: "💾 Save to Library", value: 'save_to_library', action: 'save_generated_images' },
        { text: "🔄 Refine More", value: 'refine_more', action: 'refine_generated_images' }
      ], {
        generatedImages: updatedImages,
        isCarousel: true,
        socialMediaPost: creativeBrief.socialMediaCaption ? {
          caption: creativeBrief.socialMediaCaption.caption,
          hashtags: creativeBrief.socialMediaCaption.hashtags,
          platform: creativeBrief.stillSettings?.platform || 'instagram',
          brandName: selectedBrand?.name || creativeBrief.product || ''
        } : null
      });

    } catch (error) {
      console.error(`[${new Date().toISOString()}] [ConversationalCreativeV2] Card refinement error:`, error);
      setMessages(prev => prev.filter(m => !m.data?.showImageGenerationLoading));
      addAIMessage(`❌ Failed to refine card: ${error.message}`, [
        { text: "🔄 Try Again", value: 'retry_card_refinement', action: 'retry_card_refinement' }
      ]);
    }
  };

  // Process generated image refinement from chat (when user clicks "Refine with AI" option)
  const processGeneratedImageRefinement = async (refinementText, imagesToRefine) => {
    if (!imagesToRefine || imagesToRefine.length === 0) {
      addAIMessage("I couldn't find any images to refine. Please generate images first.");
      return;
    }

    // For now, refine the first image. In the future could allow selecting which one.
    const imageToRefine = imagesToRefine[0];
    const referenceImage = creativeBrief.refinementReferenceImage;

    addAIMessage("Refining image...", null, { showImageGenerationLoading: true });

    try {
      // Get user's color choices
      const userColors = creativeBrief.brandColors || colorPalette || [];
      const colorString = userColors.length > 0 ? userColors.join(', ') : '';

      // Polish the refinement prompt by analyzing the original image
      let polishedPrompt = refinementText;
      try {
        console.log(`[${new Date().toISOString()}] [ConversationalCreativeV2] Polishing image refinement prompt with image analysis...`);
        const polishResponse = await fetch('http://localhost:3001/api/polish-refinement-prompt', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            originalImageUrl: imageToRefine.imageUrl,
            userInstructions: refinementText,
            referenceImages: referenceImage ? [referenceImage] : [],
            context: {
              imageType: 'generated',
              title: imageToRefine.title,
              description: imageToRefine.description,
              brandColors: colorString || undefined
            }
          })
        });

        if (polishResponse.ok) {
          const polishData = await polishResponse.json();
          polishedPrompt = polishData.polishedPrompt;
          console.log(`[${new Date().toISOString()}] [ConversationalCreativeV2] Polished prompt received:`, polishedPrompt.substring(0, 100) + '...');
        } else {
          console.warn(`[${new Date().toISOString()}] [ConversationalCreativeV2] Polish endpoint failed, using original refinement text`);
        }
      } catch (polishError) {
        console.warn(`[${new Date().toISOString()}] [ConversationalCreativeV2] Polish request failed, using original refinement text:`, polishError.message);
      }

      // Build refinement prompt
      const refinementPrompt = `IMAGE REFINEMENT REQUEST:

${polishedPrompt}

INSTRUCTIONS:
- Apply the requested changes while maintaining the core subject and composition.
- If a reference image is provided and explicitly mentioned, incorporate its style elements.
${colorString ? `- Use brand colors: ${colorString}` : ''}

OUTPUT: A refined version of the original image with the changes applied.`;

      // Build reference images array
      const referenceImages = [{
        url: imageToRefine.imageUrl,
        instruction: 'This is the original image to refine. Keep the same composition and improve based on the polished refinement prompt.'
      }];

      if (referenceImage) {
        referenceImages.push({
          url: referenceImage,
          instruction: 'STYLE REFERENCE: Use this image as inspiration for the refinement if the user mentioned it.'
        });
      }

      const stillSettings = creativeBrief.stillSettings || {};
      const response = await fetch('http://localhost:3001/api/generate-image-gemini', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: refinementPrompt,
          referenceImages,
          aspectRatio: stillSettings.aspectRatio || '1:1',
          userId: currentUser?.uid
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.details || errorData.error || 'Failed to refine image');
      }

      const data = await response.json();
      console.log(`[${new Date().toISOString()}] [ConversationalCreativeV2] Image refined successfully via chat`);

      // Create refined image
      const refinedImage = {
        id: `refined-${Date.now()}`,
        imageUrl: data.url,
        title: `${imageToRefine.title?.replace(' (Refined)', '')} - Refined`,
        description: `Refined from: ${imageToRefine.title}\nChanges: ${refinementText}`,
        variationStyle: 'Refined',
        isRefined: true,
        originalId: imageToRefine.id
      };

      // Clean up state
      updateCreativeBrief('imagesToRefine', null);
      updateCreativeBrief('refinementReferenceImage', null);

      // Remove loading and show success
      setMessages(prev => prev.filter(m => !m.data?.showImageGenerationLoading));
      addAIMessage(`Here's your refined image based on: "${refinementText}"`, [
        { text: "Save to Library", value: 'save_generated_images', action: 'save_generated_images' },
        { text: "Refine More", value: 'refine_generated_images', action: 'refine_generated_images' }
      ], {
        generatedImages: [refinedImage],
        isCarousel: false,
        socialMediaPost: {
          brandName: selectedBrand?.name || 'Your Brand'
        }
      });

    } catch (error) {
      console.error(`[${new Date().toISOString()}] [ConversationalCreativeV2] Image refinement error:`, error);
      setMessages(prev => prev.filter(m => !m.data?.showImageGenerationLoading));
      addAIMessage(`Failed to refine image: ${error.message}`, [
        { text: "Try Again", value: 'refine_generated_images', action: 'refine_generated_images' }
      ]);
    }
  };

  // Generate story arc scenarios using AI
  const generateStoryArcScenarios = async (storyArcType, brief) => {
    console.log('[ConversationalCreativeV2] Generating story arc scenarios', { storyArcType, brief });

    // Show loading message
    addAIMessage("✨ Creating personalized story scenarios for your carousel...", null, { showScenarioLoading: true });

    try {
      if (!conversationAgent) {
        throw new Error('Conversation agent not available');
      }

      // Build context for AI - prioritize URL-scanned product details
      const product = brief.product || 'your product';
      const audience = brief.audience || brief.targetAudience || 'your target audience';
      const emotionalHook = brief.emotionalHook || brief.desiredEmotion || '';
      const marketingAngle = brief.marketingAngle || '';
      const headline = brief.headline || brief.selectedHeadline || '';

      // Extract product details from URL scan (prioritized) or brand
      const urlSummary = brief.urlSummary || {};
      const productDetails = {
        name: product,
        description: urlSummary.description || selectedBrand?.description || '',
        features: urlSummary.features || selectedBrand?.keyFeatures || [],
        sellingPoints: urlSummary.sellingPoints || urlSummary.benefits || selectedBrand?.sellingPoints || [],
        uniqueValue: urlSummary.uniqueValue || brief.differentiators || selectedBrand?.uniqueValue || ''
      };

      console.log('[ConversationalCreativeV2] Product details for scenario generation:', productDetails);

      // Generate scenarios based on story arc type
      const scenarios = await conversationAgent.generateStoryArcScenarios({
        storyArcType,
        product,
        audience,
        emotionalHook,
        marketingAngle,
        headline,
        cardCount: brief.stillSettings?.carouselCardCount || 4,
        productDetails // Pass rich product context
      });

      // Remove loading message
      setMessages(prev => prev.filter(m => !m.data?.showScenarioLoading));

      if (!scenarios || scenarios.length === 0) {
        throw new Error('No scenarios generated');
      }

      // Show scenarios as selectable options
      const arcTypeLabels = {
        'problem_solution': 'Problem → Solution',
        'before_after': 'Before → After',
        'feature_showcase': 'Feature Showcase',
        'educational_steps': 'Educational Steps',
        'testimonial_journey': 'Testimonial Journey'
      };

      addAIMessage(
        `Here are some ${arcTypeLabels[storyArcType] || storyArcType} story approaches for your carousel. Choose one, or write your own:`,
        [
          { text: "✏️ Write my own scenario", value: 'custom_scenario', action: 'custom_scenario' }
        ],
        {
          storyArcScenarios: scenarios,
          storyArcType,
          allowCustomScenario: true
        }
      );

    } catch (error) {
      console.error('[ConversationalCreativeV2] Error generating scenarios:', error);

      // Remove loading message
      setMessages(prev => prev.filter(m => !m.data?.showScenarioLoading));

      // Show error with option to retry or skip
      addAIMessage(
        "I couldn't generate custom scenarios right now. Would you like to try again or continue with default content?",
        [
          { text: "🔄 Try Again", value: 'retry_scenarios', action: 'retry_scenarios' },
          { text: "Continue with defaults", value: 'skip_scenarios', action: 'skip_scenarios' }
        ]
      );
    }
  };

  // Handle scenario selection
  const handleScenarioSelect = async (scenario) => {
    console.log('[ConversationalCreativeV2] Scenario selected:', {
      title: scenario.title,
      cardCount: scenario.cards?.length,
      cards: scenario.cards?.map(c => ({ position: c.position, headline: c.headline }))
    });

    // Store the selected scenario in the brief
    updateCreativeBrief('selectedStoryScenario', scenario);
    console.log('[ConversationalCreativeV2] Called updateCreativeBrief for selectedStoryScenario');

    // Add confirmation message
    addUserMessage(`Selected: "${scenario.title}"`);
    addAIMessage(`Great choice! "${scenario.title}" will guide your carousel's narrative. Let's continue setting up the visual details.`);

    // Now advance the flow
    const updatedBrief = {
      ...creativeBrief,
      selectedStoryScenario: scenario,
      stillSettings: {
        ...creativeBrief.stillSettings,
        storyArcType: scenario.storyArcType || creativeBrief.stillSettings?.storyArcType
      }
    };

    await advanceFlow(updatedBrief);
  };

  // State for editing scenarios
  const [editingScenario, setEditingScenario] = useState(null);

  // Handle custom scenario input - let user write their own
  const handleCustomScenarioInput = () => {
    const cardCount = creativeBrief.stillSettings?.carouselCardCount || 4;
    const storyArcType = creativeBrief.stillSettings?.storyArcType || 'problem_solution';

    addAIMessage(
      `Tell me about your ${storyArcType.replace('_', ' ')} story. What's the main message or feature you want to highlight across the ${cardCount} cards?`,
      [
        { text: "Cancel", value: 'cancel_custom', action: 'retry_scenarios' }
      ],
      { awaitingCustomScenario: true, storyArcType, cardCount }
    );
  };

  // Handle user's custom scenario text input
  const handleCustomScenarioSubmit = async (userInput) => {
    const cardCount = creativeBrief.stillSettings?.carouselCardCount || 4;
    const storyArcType = creativeBrief.stillSettings?.storyArcType || 'problem_solution';

    addAIMessage("Creating your custom scenario...", null, { showScenarioLoading: true });

    try {
      // Use AI to structure the user's input into card headlines
      const scenario = await conversationAgent.generateCustomScenario({
        userInput,
        storyArcType,
        cardCount,
        product: creativeBrief.product,
        audience: creativeBrief.audience || creativeBrief.targetAudience
      });

      setMessages(prev => prev.filter(m => !m.data?.showScenarioLoading));

      if (scenario) {
        // Show the generated scenario for confirmation
        // IMPORTANT: Pass the scenario directly in the option to avoid React state timing issues
        addAIMessage(
          `Here's your custom scenario. You can use it as-is or edit any card:`,
          [
            { text: "✔ Use This Scenario", value: 'confirm_scenario', action: 'confirm_custom_scenario', scenario },
            { text: "✏️ Edit Cards", value: 'edit_scenario', action: 'edit_scenario', scenario }
          ],
          { customScenarioPreview: scenario, storyArcType }
        );

        // Also store in state as backup
        updateCreativeBrief('pendingCustomScenario', scenario);
      }
    } catch (error) {
      console.error('[ConversationalCreativeV2] Error creating custom scenario:', error);
      setMessages(prev => prev.filter(m => !m.data?.showScenarioLoading));
      addAIMessage("I couldn't create that scenario. Please try again or choose from the generated options.", [
        { text: "🔄 Try Again", value: 'custom_scenario', action: 'custom_scenario' },
        { text: "Show Generated Options", value: 'retry_scenarios', action: 'retry_scenarios' }
      ]);
    }
  };

  // Handle user's image description input (when generating from scratch without assets)
  const handleImageDescriptionSubmit = async (userInput) => {
    setIsDescribingImage(false);

    // Store the image description in the brief
    updateCreativeBrief('imageDescription', userInput);

    // Use AI to polish and confirm the description if we have an agent
    if (conversationAgent) {
      try {
        const result = await conversationAgent.processCustomInput({
          input: userInput,
          field: 'imageDescription',
          creativeBrief,
          phase: currentPhase
        });

        if (result?.type === 'answer' && result.polishedValue && result.polishedValue !== userInput) {
          // Show polished version for confirmation
          addAIMessage(
            `Great vision! Here's how I'd refine that for our image generation:\n\n"${result.polishedValue}"\n\nShould we go with this, or use your original description?`,
            [
              { text: "Use polished version", value: result.polishedValue, briefKey: 'imageDescription' },
              { text: "Use my original", value: userInput, briefKey: 'imageDescription' }
            ]
          );
          return;
        }
      } catch (error) {
        console.error('[handleImageDescriptionSubmit] Error polishing description:', error);
        // Continue with original if polishing fails
      }
    }

    // Confirm and continue
    addAIMessage(`Perfect! I'll use that vision for your creative: "${userInput}"\n\nLet's continue with the design process.`);
    await advanceFlow();
  };

  // Handle editing a scenario
  const handleEditScenario = (scenario) => {
    setEditingScenario(scenario);
  };

  // Save edited scenario
  const handleSaveEditedScenario = async () => {
    if (!editingScenario) return;

    // Store the edited scenario
    updateCreativeBrief('selectedStoryScenario', editingScenario);

    addUserMessage(`Edited scenario: "${editingScenario.title}"`);
    addAIMessage(`Your edited scenario is saved. Let's continue with the visual details.`);

    const updatedBrief = {
      ...creativeBrief,
      selectedStoryScenario: editingScenario,
      stillSettings: {
        ...creativeBrief.stillSettings,
        storyArcType: editingScenario.storyArcType || creativeBrief.stillSettings?.storyArcType
      }
    };

    setEditingScenario(null);
    await advanceFlow(updatedBrief);
  };

  // Update a card in the editing scenario
  const handleUpdateScenarioCard = (cardIndex, field, value) => {
    if (!editingScenario) return;

    const updatedCards = [...editingScenario.cards];
    updatedCards[cardIndex] = {
      ...updatedCards[cardIndex],
      [field]: value
    };

    setEditingScenario({
      ...editingScenario,
      cards: updatedCards
    });
  };

  // Handle copying caption to clipboard
  const handleCopyCaption = () => {
    const socialMediaCaption = creativeBrief.socialMediaCaption;
    if (!socialMediaCaption) {
      addAIMessage("No caption available to copy.");
      return;
    }

    const fullPost = socialMediaCaption.fullPost || `${socialMediaCaption.caption}\n\n${socialMediaCaption.hashtags}`;

    navigator.clipboard.writeText(fullPost).then(() => {
      addAIMessage("Caption and hashtags copied to clipboard! You can now paste it directly into your social media platform.");
    }).catch((err) => {
      console.error('Failed to copy caption:', err);
      addAIMessage("Couldn't copy to clipboard. Here's your caption:\n\n" + fullPost);
    });
  };

  // Handle toggling favorite status for a generated creative
  const handleToggleFavorite = async (messageIndex, itemId) => {
    if (!itemId || !currentUser?.uid) {
      console.warn('[handleToggleFavorite] Missing itemId or user');
      return;
    }

    try {
      const newFavoriteStatus = await toggleFavorite(itemId, currentUser.uid);

      // Update the message data with the new favorite status
      setMessages(prev => prev.map((msg, idx) => {
        if (idx === messageIndex && msg.data?.savedLibraryItemId === itemId) {
          return {
            ...msg,
            data: {
              ...msg.data,
              isFavorite: newFavoriteStatus
            }
          };
        }
        return msg;
      }));

      console.log('[handleToggleFavorite] Toggled favorite:', { itemId, newFavoriteStatus });
    } catch (error) {
      console.error('[handleToggleFavorite] Error:', error);
    }
  };

  // Handle skipping the current step
  const handleSkipCurrentStep = async () => {
    console.log(`[ConversationalCreativeV2] Skipping step ${phaseStep} in ${currentPhase}`);

    // Get navigation context and advance
    const navContext = conversationFlowService.getNavigationContext(currentPhase, phaseStep, creativeBrief);

    if (navContext.isPhaseComplete) {
      // Phase is complete, move to next phase
      if (navContext.nextPhase) {
        const transitionMessage = navContext.phaseTransitionMessage;
        if (transitionMessage) {
          addAIMessage(transitionMessage);
        }
        setTimeout(() => startPhase(navContext.nextPhase, creativeBrief), 500);
      } else {
        handleFlowComplete(creativeBrief);
      }
    } else if (navContext.nextStep !== null) {
      // Move to next step
      setPhaseStep(navContext.nextStep);
      setTimeout(() => executeStep(currentPhase, navContext.nextStep), 300);
    } else {
      // Fallback: try incrementing step manually
      const nextStep = phaseStep + 1;
      setPhaseStep(nextStep);
      setTimeout(() => executeStep(currentPhase, nextStep), 300);
    }
  };

  const handleRegenerateOptions = async () => {
    setIsLoading(true);
    try {
      // Pass true to indicate we want fresh options (will use previousOptionsForStep)
      await executeStep(currentPhase, phaseStep, true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBrainstorm = async () => {
    if (!conversationAgent) return;

    setIsLoading(true);
    try {
      const result = await conversationAgent.brainstorm({
        phaseId: currentPhase,
        stepIndex: phaseStep,
        creativeBrief,
        brand: selectedBrand
      });

      if (result?.message) {
        addAIMessage(result.message, result.options);
      }
    } catch (error) {
      console.error('Error in brainstorm:', error);
      addAIMessage("Let me think about that... Could you tell me more about what you're looking for?");
    } finally {
      setIsLoading(false);
    }
  };

  const handleBrainstormAudiences = async () => {
    if (!conversationAgent) return;

    setIsLoading(true);
    try {
      const result = await conversationAgent.brainstormAudiences({
        creativeBrief,
        brand: selectedBrand,
        previousAudiences: messages
          .filter(m => m.data?.briefKey === 'targetAudience')
          .map(m => m.text)
      });

      if (result?.audiences) {
        const options = result.audiences.map(audience => ({
          text: audience,
          value: audience,
          briefKey: 'targetAudience'
        }));

        const audienceStep = conversationFlowService.getStepByField('strategist', 'targetAudience');
        const alwaysInclude = audienceStep?.alwaysInclude || [];

        addAIMessage(result.message || "Here are some more audience options:", [...options, ...alwaysInclude]);
      }
    } catch (error) {
      console.error('Error brainstorming audiences:', error);
      addAIMessage("I'm having trouble generating more options. Please try again or enter your own.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCustomInput = (option) => {
    // Get the current step's field name for context
    const currentStep = conversationFlowService.getPhaseStep(currentPhase, phaseStep);
    const field = option.value === 'custom' || option.value === 'enter_own'
      ? currentStep?.field || 'answer'
      : option.value.replace('custom_', '');

    // Different messages based on what the user selected
    const optionText = option.text?.toLowerCase() || '';
    const isUrlOption = optionText.includes('url');
    const isSomethingElse = optionText.includes('something else');
    const isEnterOwn = optionText.includes('enter my own') || optionText.includes('enter own') || option.value === 'enter_own';

    let message;
    if (isUrlOption) {
      message = "Great! Paste your product or service URL below and I'll analyze it:";
    } else if (isSomethingElse) {
      message = `No problem! Type your ${field} below:`;
    } else if (isEnterOwn) {
      message = `Sure! Type your own ${field} below:`;
    } else {
      message = `Great! Please type your ${field}:`;
    }

    addAIMessage(message);
  };

  // ============================================
  // FLOW CONTROL
  // ============================================

  const handleFlowModeSelect = (mode) => {
    if (mode === 'guided') {
      const phaseOrder = conversationFlowService.getPhaseOrder(creativeBrief);
      const firstPhase = phaseOrder[0];
      startPhase(firstPhase);
    } else {
      // Direct mode - activate prompt crafting experience
      setIsDirectMode(true);
      setCurrentPhase('direct_mode');
      initializeDirectMode();
    }
  };

  // ============================================
  // DIRECT MODE FUNCTIONS
  // ============================================

  // Helper: Extract prompt from <current_prompt> tags in AI response
  const extractPromptFromResponse = (text) => {
    if (!text) return null;
    const match = text.match(/<current_prompt>([\s\S]*?)(?:<\/current_prompt>|$)/);
    return match ? match[1].trim() : null;
  };

  // Helper: Clean <current_prompt> tags from displayed text
  const cleanPromptTags = (text) => {
    if (!text) return "";
    let cleaned = text.replace(/<current_prompt>[\s\S]*?<\/current_prompt>/g, '');
    cleaned = cleaned.replace(/<current_prompt>/g, '');
    return cleaned.trim();
  };

  // Initialize direct mode with welcome message
  const initializeDirectMode = () => {
    const welcomeMessage = `I'm your AI Art Director, and I'm here to help you bring your vision to life.

**Tell me about your creative:**
- What product or service is this for?
- What's the mood or vibe you're going for?
- Any specific elements you want to include?

You can also upload **reference images** below to show me the style you're after.

Type your brief and I'll help you craft the perfect prompt!`;

    addAIMessage(welcomeMessage);
    setDirectModeInput('');
    setDirectModePrompt('');
    setDirectModeReferenceImages([]);
    setDirectModeLoading(false);
    setDirectModeStep('prompt');
  };

  // Handle direct mode message submission with retry logic
  const handleDirectModeSubmit = async (retryCount = 0) => {
    const userInput = directModeInput.trim();
    if (!userInput || directModeLoading) return;

    // Only add user message on first attempt
    if (retryCount === 0) {
      addUserMessage(userInput);
      setDirectModeInput('');
    }
    setDirectModeLoading(true);

    try {
      // Build conversation history from messages
      const conversationHistory = messages
        .filter(m => m.phase === 'direct_mode' || currentPhase === 'direct_mode')
        .map(m => ({
          role: m.role === 'assistant' ? 'assistant' : 'user',
          content: m.text
        }));

      // Call the /api/build-prompt endpoint with timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 120000); // 2 minute client timeout

      const response = await fetch('http://localhost:3001/api/build-prompt', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal,
        body: JSON.stringify({
          userId: currentUser?.uid,
          brandId: selectedBrand?.id,
          originalPrompt: directModePrompt || '',
          originalImageUrl: null, // No source image in direct mode
          referenceImages: directModeReferenceImages.filter(img => img.status === 'valid'),
          currentInput: userInput,
          conversationHistory,
          context: {
            mode: 'direct_creative',
            brandName: selectedBrand?.name,
            brandColors: selectedBrand?.colors,
            funnelStage: funnelContext?.funnelStage,
            campaignObjective: funnelContext?.campaignObjective
          }
        })
      });

      clearTimeout(timeoutId);

      // Handle error responses with potential retry
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));

        // Auto-retry on 503 (service unavailable) up to 2 times
        if (response.status === 503 && retryCount < 2) {
          console.log(`API returned 503, retrying in ${(retryCount + 1) * 2} seconds... (attempt ${retryCount + 2}/3)`);
          await new Promise(resolve => setTimeout(resolve, (retryCount + 1) * 2000));
          return handleDirectModeSubmit(retryCount + 1);
        }

        // Show user-friendly error message
        const errorMessage = errorData.details || errorData.error || 'Failed to get AI response';
        throw new Error(errorMessage);
      }

      const data = await response.json();

      // Extract prompt from <current_prompt> tags
      const extractedPrompt = extractPromptFromResponse(data.response);
      const cleanedContent = cleanPromptTags(data.response);

      if (extractedPrompt) {
        setDirectModePrompt(extractedPrompt);
      }

      addAIMessage(cleanedContent);
    } catch (error) {
      console.error('Direct mode AI error:', error);
      let errorMsg;
      if (error.name === 'AbortError' || error.message.includes('timeout') || error.message.includes('timed out')) {
        errorMsg = "The AI is taking longer than expected. Try a simpler request or fewer reference images.";
      } else if (error.message.includes('unavailable') || error.message.includes('overloaded')) {
        errorMsg = "The AI service is temporarily busy. Please wait a moment and try again.";
      } else if (error.message.includes('Rate limit')) {
        errorMsg = "Too many requests. Please wait a few seconds before trying again.";
      } else {
        errorMsg = "I'm having trouble connecting right now. Please try again!";
      }
      addAIMessage(errorMsg);
    } finally {
      setDirectModeLoading(false);
    }
  };

  // Add reference image by URL
  const addDirectModeReferenceImage = (url) => {
    if (!url.trim() || directModeReferenceImages.length >= 5) return;

    const nextLabel = `[Image ${String.fromCharCode(65 + directModeReferenceImages.length)}]`;
    const newImage = { id: `ref-${Date.now()}`, url: url.trim(), status: 'loading', label: nextLabel };
    setDirectModeReferenceImages(prev => [...prev, newImage]);
    setDirectModeRefUrlInput('');

    // Validate image
    const img = new Image();
    img.onload = () => {
      setDirectModeReferenceImages(prev =>
        prev.map(i => i.id === newImage.id ? { ...i, status: 'valid' } : i)
      );
    };
    img.onerror = () => {
      setDirectModeReferenceImages(prev =>
        prev.map(i => i.id === newImage.id ? { ...i, status: 'error' } : i)
      );
    };
    img.src = url.trim();
  };

  // Add reference images from files
  const handleDirectModeFileUpload = (e) => {
    const files = Array.from(e.target.files || []);
    const remaining = 5 - directModeReferenceImages.length;
    const filesToProcess = files.slice(0, remaining);

    filesToProcess.forEach(file => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const dataUrl = event.target.result;
        const nextLabel = `[Image ${String.fromCharCode(65 + directModeReferenceImages.length)}]`;
        setDirectModeReferenceImages(prev => {
          const label = `[Image ${String.fromCharCode(65 + prev.length)}]`;
          return [...prev, { id: `ref-${Date.now()}-${Math.random()}`, url: dataUrl, status: 'valid', label }];
        });
      };
      reader.readAsDataURL(file);
    });

    // Reset input
    if (directModeFileInputRef.current) {
      directModeFileInputRef.current.value = '';
    }
  };

  // Remove reference image
  const removeDirectModeReferenceImage = (imageId) => {
    setDirectModeReferenceImages(prev => {
      const filtered = prev.filter(img => img.id !== imageId);
      // Re-label remaining images
      return filtered.map((img, idx) => ({
        ...img,
        label: `[Image ${String.fromCharCode(65 + idx)}]`
      }));
    });
  };

  // Apply the crafted prompt and proceed to format selection
  const handleDirectModeApply = () => {
    if (!directModePrompt) return;

    // Store the crafted prompt in creative brief
    updateCreativeBrief('directModePrompt', directModePrompt);
    updateCreativeBrief('referenceStyleImages', directModeReferenceImages.filter(img => img.status === 'valid'));

    // Move to format selection step
    setDirectModeStep('format');

    // Show format selection options
    addAIMessage(
      "Your creative vision is locked in! What format would you like?",
      [
        { text: "Static Image", value: "image", action: 'directModeFormat' },
        { text: "Carousel", value: "carousel", action: 'directModeFormat' },
        { text: "Video", value: "video", action: 'directModeFormat' }
      ]
    );
  };

  // Handle direct mode format selection
  const handleDirectModeFormatSelect = (format) => {
    addUserMessage(format === 'image' ? 'Static Image' : format === 'carousel' ? 'Carousel' : 'Video');
    updateCreativeBrief('outputType', format);

    // Move to aspect ratio step
    setDirectModeStep('aspectRatio');

    // Show aspect ratio options based on format
    const aspectRatioOptions = format === 'video'
      ? [
          { text: "9:16 (Vertical/Stories)", value: "9:16", action: 'directModeAspectRatio' },
          { text: "16:9 (Horizontal/YouTube)", value: "16:9", action: 'directModeAspectRatio' },
          { text: "1:1 (Square)", value: "1:1", action: 'directModeAspectRatio' }
        ]
      : [
          { text: "1:1 (Square)", value: "1:1", action: 'directModeAspectRatio' },
          { text: "4:5 (Portrait/Feed)", value: "4:5", action: 'directModeAspectRatio' },
          { text: "9:16 (Stories/Reels)", value: "9:16", action: 'directModeAspectRatio' },
          { text: "16:9 (Landscape)", value: "16:9", action: 'directModeAspectRatio' }
        ];

    addAIMessage("What aspect ratio do you need?", aspectRatioOptions);
  };

  // Handle direct mode aspect ratio selection and trigger generation
  const handleDirectModeAspectRatioSelect = async (aspectRatio) => {
    addUserMessage(aspectRatio);

    // Update brief with aspect ratio
    const updatedBrief = {
      ...creativeBrief,
      stillSettings: {
        ...creativeBrief.stillSettings,
        aspectRatio: aspectRatio,
        variationCount: 1
      }
    };
    setCreativeBrief(updatedBrief);

    // Exit direct mode UI
    setIsDirectMode(false);
    setDirectModeStep('prompt');
    setCurrentPhase('generation');

    // Go straight to generation
    addAIMessage("Let's create your vision!");

    // Check if video - needs different flow
    if (updatedBrief.outputType === 'video') {
      // For video, we need to generate a script first
      addAIMessage("For video, I'll generate a production script based on your prompt. One moment...");
      // Trigger video script generation
      await generateVideoScriptFromDirectMode(updatedBrief);
    } else {
      // For image/carousel, generate directly
      await generateImageOrCarouselInChat(updatedBrief);
    }
  };

  // Generate video script from direct mode prompt
  const generateVideoScriptFromDirectMode = async (brief) => {
    if (!conversationAgent) {
      addAIMessage("Unable to generate video script. Please try again.");
      return;
    }

    setIsGeneratingScript(true);
    try {
      const aspectRatio = brief.stillSettings?.aspectRatio || '9:16';
      const prompt = `You are a professional TV commercial director creating a production script for a video advertisement.

USER'S CREATIVE VISION:
${brief.directModePrompt}

VIDEO SETTINGS:
- Duration: 5-8 seconds
- Aspect Ratio: ${aspectRatio}
- Brand: ${selectedBrand?.name || 'Brand'}

Generate a detailed professional TV production script that:
1. Brings the user's creative vision to life
2. Includes specific CAMERA DIRECTIONS (e.g., "WIDE SHOT", "CLOSE-UP", "PAN LEFT")
3. Includes LIGHTING notes
4. Includes ACTION descriptions
5. Includes TIMING markers (e.g., "0:00-0:02", "0:03-0:05")
6. Includes MUSIC/SOUND cues

Format the script professionally with timing markers, camera directions, and action descriptions.
Return ONLY the script, no additional commentary.`;

      const response = await conversationAgent.callGeminiAPI(prompt);
      const script = response.trim();

      setPendingScript(script);

      // Add the script as an inline editor message
      addAIMessage(
        "**Production Script Ready**\n\nReview your production script below. You can edit it directly, then approve to start video generation.",
        null,
        { scriptEditor: { script, isEditable: true } }
      );
    } catch (error) {
      console.error('Script generation error:', error);
      addAIMessage("I had trouble generating the video script. Please try again.", [
        { text: "Try Again", action: 'retry_script' }
      ]);
    } finally {
      setIsGeneratingScript(false);
    }
  };

  // Reset direct mode and switch to guided
  const handleDirectModeReset = () => {
    setIsDirectMode(false);
    setDirectModeInput('');
    setDirectModePrompt('');
    setDirectModeReferenceImages([]);
    setDirectModeLoading(false);
    setDirectModeStep('prompt');

    // Go to guided mode
    const phaseOrder = conversationFlowService.getPhaseOrder(creativeBrief);
    const firstPhase = phaseOrder[0];
    startPhase(firstPhase);
  };

  const startPhase = async (phaseId, updatedBrief = null) => {
    console.log(`[ConversationalCreativeV2] Starting phase: ${phaseId}`);

    // Use provided updated brief or fall back to current state
    // This is important because React state updates are async
    const briefForPhase = updatedBrief || creativeBrief;

    // Safety check: ensure phaseId is valid
    if (!phaseId) {
      console.error('[ConversationalCreativeV2] startPhase called with invalid phaseId');
      handleFlowComplete(briefForPhase);
      return;
    }

    // The "generation" phase is a terminal phase - it means the flow is complete
    // and we should show generation options instead of trying to execute steps
    if (phaseId === 'generation') {
      console.log('[ConversationalCreativeV2] Reached generation phase - flow complete');
      setCurrentPhase(phaseId);
      handleFlowComplete(briefForPhase);
      return;
    }

    setCurrentPhase(phaseId);
    setPhaseStep(0);

    const { shouldSkip, reason } = conversationFlowService.shouldSkipPhase(phaseId, briefForPhase);
    if (shouldSkip) {
      if (reason) {
        addAIMessage(reason);
      }
      const nextPhase = conversationFlowService.getNextPhase(phaseId, briefForPhase);
      if (nextPhase) {
        setTimeout(() => startPhase(nextPhase, briefForPhase), 500);
      } else {
        // No more phases to skip to - complete the flow
        console.log('[ConversationalCreativeV2] No next phase after skipping, completing flow');
        handleFlowComplete(briefForPhase);
      }
      return;
    }

    // Special handling for brief_review phase: generate brief sheet data BEFORE showing the step
    // Note: The transition message ("Perfect! Let me compile...") is already added by transitionToNextPhase()
    // We only need to generate the briefSheetData here so it's available when the step executes
    if (phaseId === 'brief_review' && conversationAgent) {
      setIsLoading(true);

      try {
        // Sync the agent with the latest brief before generating
        conversationAgent.updateContext(selectedBrand, briefForPhase);

        // Generate brief sheet data
        const briefData = await conversationAgent.generateBriefSheet(briefForPhase);
        console.log('[ConversationalCreativeV2] Brief data generated for brief_review:', briefData);

        // Generate image description (brief_review is format-agnostic, so we generate a generic description)
        const imageDescription = await conversationAgent.generateImageDescription(briefForPhase);
        briefData.imageDescription = imageDescription;

        setBriefSheetData(briefData);
      } catch (error) {
        console.error('[ConversationalCreativeV2] Error generating brief sheet for brief_review:', error);
      } finally {
        setIsLoading(false);
      }
    }

    // Execute the first step of this phase
    await executeStep(phaseId, 0, false, briefForPhase);
  };

  const executeStep = async (phaseId, stepIndex, isRegenerate = false, updatedBrief = null) => {
    // Use provided updated brief or fall back to current state
    const briefForStep = updatedBrief || creativeBrief;
    const context = conversationFlowService.getStepExecutionContext(phaseId, stepIndex, briefForStep);

    // Debug logging to trace step execution issues
    console.log(`[executeStep] Phase: ${phaseId}, Step: ${stepIndex}`);
    console.log(`[executeStep] context.step:`, context.step ? context.step.field : 'NULL');
    console.log(`[executeStep] context.message:`, context.message || '(empty)');
    console.log(`[executeStep] context.options:`, context.options?.length || 0, 'options');
    console.log(`[executeStep] context.isAIGenerated:`, context.isAIGenerated);

    // If step not found, automatically advance to next step or phase
    if (!context.step) {
      console.error(`[ConversationalCreativeV2] Step ${stepIndex} not found in phase ${phaseId}, auto-advancing...`);
      const navContext = conversationFlowService.getNavigationContext(phaseId, stepIndex, briefForStep);
      if (navContext.isPhaseComplete && navContext.nextPhase) {
        const transitionMessage = navContext.phaseTransitionMessage;
        if (transitionMessage) {
          addAIMessage(transitionMessage);
        }
        setTimeout(() => startPhase(navContext.nextPhase, briefForStep), 500);
      } else if (navContext.nextStep !== null) {
        setTimeout(() => executeStep(phaseId, navContext.nextStep, false, briefForStep), 100);
      } else {
        // Ultimate fallback - complete the flow
        handleFlowComplete(briefForStep);
      }
      return;
    }

    // Handle conditional skip with proper navigation
    if (context.shouldSkip) {
      const navContext = conversationFlowService.getNavigationContext(phaseId, stepIndex, briefForStep);
      if (navContext.isPhaseComplete && navContext.nextPhase) {
        const transitionMessage = navContext.phaseTransitionMessage;
        if (transitionMessage) {
          addAIMessage(transitionMessage);
        }
        setTimeout(() => startPhase(navContext.nextPhase, briefForStep), 500);
      } else if (navContext.nextStep !== null) {
        setTimeout(() => executeStep(phaseId, navContext.nextStep, false, briefForStep), 100);
      } else {
        // Skip to specified step or next
        const nextStepIndex = context.skipTo !== null ? context.skipTo : stepIndex + 1;
        setTimeout(() => executeStep(phaseId, nextStepIndex, false, briefForStep), 100);
      }
      return;
    }

    // Reset previous options when starting a new step (not regenerating)
    if (!isRegenerate) {
      setPreviousOptionsForStep([]);
    }

    // Special handling for color palette step - initialize colors from brand
    if (context.step?.showColorSwatches && context.field === 'brandColors') {
      // Brand colors are stored in visual object as primaryColor, secondaryColor, accentColor
      const visual = selectedBrand?.visual || {};
      const brandColors = [
        visual.primaryColor,
        visual.secondaryColor,
        visual.accentColor
      ].filter(Boolean); // Remove any undefined/null values
      setColorPalette(brandColors);
    }

    setIsLoading(true);

    try {
      let message = context.message;
      // Initialize options from context (fallback options from JSON) instead of empty array
      let options = context.options || [];
      let messageData = null;

      // Filter options based on conditions (e.g., hide actor_driven when no casting)
      if (options.length > 0) {
        options = options.filter(option => {
          // Check for requiresCasting flag
          if (option.requiresCasting) {
            const hasCasting = creativeBrief.selectedCasting !== null && creativeBrief.selectedCasting !== undefined;
            if (!hasCasting) {
              console.log(`[executeStep] Hiding option "${option.text}" - requires casting but none selected`);
              return false;
            }
          }
          // Check for generic condition string (future-proofing)
          if (option.condition) {
            try {
              // Simple condition evaluation using creativeBrief context
              const conditionMet = new Function('brief', `with(brief) { return ${option.condition}; }`)(creativeBrief);
              if (!conditionMet) {
                console.log(`[executeStep] Hiding option "${option.text}" - condition not met: ${option.condition}`);
                return false;
              }
            } catch (e) {
              console.warn(`[executeStep] Failed to evaluate condition for option "${option.text}":`, e);
            }
          }
          return true;
        });
      }

      // Check if this step needs color swatches
      if (context.step?.showColorSwatches) {
        messageData = { showColorSwatches: true };
      }

      // Handle conditional drill-down steps (e.g., textOverlayDetails based on textOverlays selection)
      if (context.step?.conditionalDrillDown) {
        // Find the previous step's field to get the condition value
        // For textOverlayDetails, we need the textOverlays value
        const drillDownKeys = Object.keys(context.step.conditionalDrillDown);
        // Get the field name that controls the drill-down (e.g., 'textOverlays' for 'textOverlayDetails')
        const controllingFieldName = context.step.field?.replace('Details', '') || 'textOverlays';
        const storedValue = briefForStep[controllingFieldName];

        // Check which drill-down option applies based on creativeBrief values
        // The stored value might be the option.text (display text) or option.value (key)
        // We need to match against the drill-down keys (which are option.value strings)
        let matchedDrillDown = null;
        let matchedKey = null;

        for (const key of drillDownKeys) {
          // Direct match: stored value equals the drill-down key
          if (storedValue === key) {
            matchedDrillDown = context.step.conditionalDrillDown[key];
            matchedKey = key;
            break;
          }
          // Partial match: stored display text contains the key or vice versa
          // e.g., "Include pricing or offer details" contains "pricing"
          if (typeof storedValue === 'string' && storedValue.toLowerCase().includes(key.toLowerCase())) {
            matchedDrillDown = context.step.conditionalDrillDown[key];
            matchedKey = key;
            break;
          }
        }

        if (matchedDrillDown) {
          message = matchedDrillDown.message || message;
          options = matchedDrillDown.options || options;
          console.log(`[executeStep] Using conditional drill-down for key: ${matchedKey} (stored: ${storedValue})`, { message, optionsCount: options.length });
        } else {
          // No matching drill-down found - this step should probably be skipped
          // This happens when the user selected "none" or an unrecognized value
          console.log(`[executeStep] No matching conditional drill-down for step ${stepIndex} (stored: ${storedValue}), advancing...`);
          const navContext = conversationFlowService.getNavigationContext(phaseId, stepIndex, briefForStep);
          if (navContext.nextStep !== null) {
            setTimeout(() => executeStep(phaseId, navContext.nextStep, false, briefForStep), 100);
          } else if (navContext.isPhaseComplete && navContext.nextPhase) {
            const transitionMessage = navContext.phaseTransitionMessage;
            if (transitionMessage) {
              addAIMessage(transitionMessage);
            }
            setTimeout(() => startPhase(navContext.nextPhase, briefForStep), 500);
          }
          setIsLoading(false);
          return;
        }
      }

      // Special handling for logo check step
      if (context.step?.type === 'logoCheck') {
        const hasLogo = selectedBrand?.logo;
        const conditionalMessages = context.step.conditionalMessages;

        if (hasLogo && conditionalMessages?.hasLogo) {
          // Brand has a logo - use hasLogo conditional message and show preview
          message = conditionalMessages.hasLogo.message;
          options = conditionalMessages.hasLogo.options || options;
          messageData = {
            ...messageData,
            showLogoPreview: true,
            logoUrl: selectedBrand.logo,
            logoName: selectedBrand.name
          };
        } else if (!hasLogo && conditionalMessages?.noLogo) {
          // Brand doesn't have a logo - use noLogo conditional message
          message = conditionalMessages.noLogo.message;
          options = conditionalMessages.noLogo.options || options;
        }
      }

      if (context.isAIGenerated && conversationAgent) {
        const variables = buildTemplateVariables();
        const prompt = conversationFlowService.interpolate(context.promptTemplate || '', variables);

        try {
          const aiResult = await conversationAgent.generateOptions({
            prompt,
            phaseId,
            stepIndex,
            field: context.field,
            creativeBrief,
            brand: selectedBrand,
            previousOptions: isRegenerate ? previousOptionsForStep : []
          });

          if (aiResult) {
            message = aiResult.message || message;
            // Use AI options if available, otherwise keep JSON fallback options
            if (aiResult.options && aiResult.options.length > 0) {
              options = aiResult.options;
            }
            // If AI didn't return options but we have context.options, use those (already set above)

            // Track these options for potential "More Options" requests
            setPreviousOptionsForStep(prev => [...prev, ...options]);
          }
        } catch (error) {
          console.error('Error generating AI options:', error);
          // Use JSON fallback options if available, otherwise show retry
          if (!options || options.length === 0) {
            message = `I'm having trouble generating options: ${error.message}. Please try again.`;
            options = [
              { text: "🔄 Try again", value: "retry_generation", action: "regenerate_options" }
            ];
          }
          // If we have fallback options, just log the error but use them
        }
      } else if ((context.field === 'selectedCasting' || context.field === 'videoSettings.selectedCasting') && context.step?.generation) {
        // Special handling for selectedCasting - use pre-generated casting images
        // Handles both art_director (selectedCasting) and video_director (videoSettings.selectedCasting)
        if (isGeneratingCasting) {
          // Still generating - show loading message
          message = "🎭 I'm still preparing your casting options... This takes a moment as I'm creating personalized headshots.";
          messageData = { showCastingLoading: true };
          options = [];
        } else if (castingError) {
          // Error occurred during generation
          message = `I had trouble generating casting options: ${castingError}. Please try again.`;
          options = [
            { text: "🔄 Try again", value: "retry_casting", action: "retry_casting" }
          ];
        } else if (castingOptions.length > 0) {
          // Casting images are ready - display them
          message = context.step.message || "Here are some people I've found who could bring your vision to life:";
          messageData = { castingOptions };
          options = [
            { text: "Regenerate with different options", value: "regenerate_casting", action: "retry_casting" }
          ];
        } else {
          // Auto-trigger casting: first check for favorites, otherwise generate
          // For video_director, the actor description is in videoSettings.actors
          const peopleDescription = briefForStep.peopleDescription || briefForStep.videoSettings?.actors || 'professional person';
          message = "🎭 I'm preparing your casting options... This takes a moment as I'm checking your favorites and creating personalized headshots.";
          messageData = { showCastingLoading: true };
          options = [];
          // Trigger favorites check or generation in background (non-blocking)
          loadOrGenerateCasting(peopleDescription, briefForStep);
        }
      } else if (context.field === 'videoSettings.selectedLocation' || context.field === 'selectedLocation') {
        // Special handling for selectedLocation - auto-trigger location generation if not started
        if (isGeneratingLocations) {
          message = "I'm scouting some perfect locations for your shoot... This takes a moment as I'm generating options.";
          messageData = { showLocationLoading: true };
          options = [];
        } else if (locationError) {
          message = `I had trouble generating location options: ${locationError}. Please try again.`;
          options = [
            { text: "🔄 Try again", value: "retry_location", action: "retry_location" }
          ];
        } else if (locationOptions.length > 0) {
          message = context.step?.message || "Here are some locations I've scouted for your shoot:";
          messageData = { locationOptions };
          options = [
            { text: "Scout different locations", value: "regenerate_location", action: "retry_location" }
          ];
        } else {
          // Auto-trigger location: first check for favorites, otherwise generate
          const sceneDescription = briefForStep.sceneType || briefForStep.videoSettings?.scenery || 'lifestyle scene';
          message = "I'm scouting some perfect locations for your shoot... Checking your favorites and generating options.";
          messageData = { showLocationLoading: true };
          options = [];
          // Trigger favorites check or generation in background (non-blocking)
          loadOrGenerateLocations(sceneDescription, briefForStep);
        }
      } else if (context.field === 'videoSettings.firstFrame') {
        // Special handling for firstFrame in video director
        if (isGeneratingFirstFrame) {
          message = "🎥 I'm composing opening shot options... This takes a moment as I'm creating the first frames.";
          messageData = { showFirstFrameLoading: true };
          options = [];
        } else if (firstFrameError) {
          message = `I had trouble generating first frame options: ${firstFrameError}. Please try again.`;
          options = [
            { text: "🔄 Try again", value: "retry_first_frame", action: "retry_first_frame" }
          ];
        } else if (firstFrameOptions.length > 0) {
          message = context.step?.message || "Here are some opening shots I've composed for your video:";
          messageData = { firstFrameOptions };
          options = [
            { text: "Generate different opening shots", value: "regenerate_first_frame", action: "retry_first_frame" }
          ];
        } else {
          message = "I don't have any first frame options ready yet. Let me compose some for you.";
          options = [
            { text: "🔄 Compose opening shots", value: "retry_first_frame", action: "retry_first_frame" }
          ];
        }
      } else if (context.field === 'videoSettings.lastFrame') {
        // Special handling for lastFrame in video director
        if (isGeneratingLastFrame) {
          message = "🎬 I'm designing closing frame options... This takes a moment as I'm creating the final shots.";
          messageData = { showLastFrameLoading: true };
          options = [];
        } else if (lastFrameError) {
          message = `I had trouble generating last frame options: ${lastFrameError}. Please try again.`;
          options = [
            { text: "🔄 Try again", value: "retry_last_frame", action: "retry_last_frame" }
          ];
        } else if (lastFrameOptions.length > 0) {
          message = context.step?.message || "Here are some closing shots I've designed for your video:";
          messageData = { lastFrameOptions };
          options = [
            { text: "Design different closing shots", value: "regenerate_last_frame", action: "retry_last_frame" }
          ];
        } else {
          message = "I don't have any last frame options ready yet. Let me design some for you.";
          options = [
            { text: "🔄 Design closing shots", value: "retry_last_frame", action: "retry_last_frame" }
          ];
        }
      } else if (context.step?.generation && conversationAgent) {
        // Handle other generation steps
        if (context.step.generation.type === 'image_options' || context.step.generation.method) {
          try {
            const genConfig = context.step.generation;
            const basedOnFields = genConfig.basedOn || [];

            const contextData = basedOnFields
              .map(field => `${field}: ${JSON.stringify(creativeBrief[field] || 'Not specified')}`)
              .join('\n');

            const generationPrompt = `Based on the following context:\n${contextData}\n\nGenerate ${genConfig.count || 4} distinct options for "${context.field || 'selection'}". \n\nProvide a descriptive title for each option. The user will select one.`;

            const aiResult = await conversationAgent.generateOptions({
              prompt: generationPrompt,
              phaseId,
              stepIndex,
              field: context.field,
              creativeBrief,
              brand: selectedBrand,
              previousOptions: []
            });

            if (aiResult && aiResult.options && aiResult.options.length > 0) {
              message = aiResult.message || context.step.message || message;
              options = aiResult.options || [];

              // Add selection options if defined
              if (context.step.selectionOptions) {
                const actionOptions = context.step.selectionOptions.map(opt => ({
                  text: opt.text || opt.action,
                  value: opt.value || opt.action,
                  action: opt.action,
                  isAction: true
                })).filter(o => o.text);

                options = [...options, ...actionOptions];
              }

              setPreviousOptionsForStep(prev => [...prev, ...options]);
            } else {
              // AI returned empty results - show error and retry option
              console.error(`[ConversationalCreativeV2] AI returned empty results for ${context.field}`);
              message = "I'm having trouble generating options right now. Please try again.";
              options = [
                { text: "🔄 Try again", value: "retry_generation", action: "regenerate_options" }
              ];
            }
          } catch (error) {
            console.error('Error in generation step:', error);
            message = `I'm having trouble generating options: ${error.message}. Please try again.`;
            options = [
              { text: "🔄 Try again", value: "retry_generation", action: "regenerate_options" }
            ];
          }
        }
      } else if (context.options) {
        options = context.options;
      }

      if (context.alwaysInclude && context.alwaysInclude.length > 0) {
        options = [...options, ...context.alwaysInclude];
      }

      const variables = buildTemplateVariables();

      // Filter options based on conditions and interpolate text
      const hasRetrievedImages = (creativeBrief.crawledImages?.length || 0) > 0;
      options = options
        .filter(opt => {
          // Check if option has a condition
          if (opt.condition === 'hasRetrievedImages') {
            return hasRetrievedImages;
          }
          return true; // No condition, always include
        })
        .map(opt => ({
          ...opt,
          // Interpolate option text (e.g., replace {count} with actual count)
          text: conversationFlowService.interpolate(opt.text || '', variables)
        }));

      // Ensure message is a string before interpolation to prevent "split is not a function" error
      let messageToInterpolate = '';
      if (typeof message === 'string') {
        messageToInterpolate = message;
      } else if (typeof message === 'object' && message !== null) {
        // Try common properties or find first string value
        messageToInterpolate = message.text || message.message ||
          Object.values(message).find(val => typeof val === 'string') ||
          JSON.stringify(message);
      } else {
        messageToInterpolate = String(message || '');
      }
      const interpolatedMessage = conversationFlowService.interpolate(messageToInterpolate, variables);

      // Log if we ended up with no options - this indicates a configuration issue
      if (!options || options.length === 0) {
        console.error(`[ConversationalCreativeV2] NO OPTIONS for step ${stepIndex} in ${phaseId}!`);
        console.error(`[executeStep] context.step:`, context.step);
        console.error(`[executeStep] context.options:`, context.options);
        console.error(`[executeStep] context.isAIGenerated:`, context.isAIGenerated);
      }

      // Log if message is empty - this also indicates a configuration issue
      if (!interpolatedMessage) {
        console.error(`[ConversationalCreativeV2] EMPTY MESSAGE for step ${stepIndex} in ${phaseId}!`);
        console.error(`[executeStep] context.message was:`, context.message);
      }

      console.log(`[executeStep] SUCCESS - Adding AI message for step ${stepIndex}:`, {
        message: interpolatedMessage?.substring(0, 50) + '...',
        optionsCount: options?.length || 0,
        optionTexts: options?.map(o => o.text)
      });
      // Pass phaseId explicitly to ensure correct phase is recorded (React state may not be updated yet)
      addAIMessage(interpolatedMessage, options, messageData, phaseId);

    } catch (error) {
      console.error('Error executing step:', error);
      const errorMessage = globalRules.aiGenerationFailures?.errorMessage ||
        "I'm having trouble right now. Please try again.";
      addAIMessage(errorMessage, [
        { text: "🔄 Try again", value: "retry_generation", action: "regenerate_options" }
      ], null, phaseId);
    } finally {
      setIsLoading(false);
    }
  };

  // Get fields collected in a specific phase
  const getPhaseFields = (phaseId) => {
    const phaseFieldMap = {
      strategist: ['product', 'targetAudience', 'desiredEmotion', 'objective', 'geography'],
      creative_director: ['creativeRiskTolerance', 'marketingAngle'],
      copywriter: ['selectedHeadline', 'tone', 'copyStyle', 'cta', 'legalRequirements'],
      // Note: logo/assets are collected but shown differently in the brief sheet (with images)
      art_director: ['visualStyle', 'imageType', 'sceneType', 'peopleDescription', 'photographyMood', 'brandColors', 'focusHierarchy', 'textOverlays', 'textOverlayDetails', 'selectedCasting'],
      still_director: [
        'stillSettings.aspectRatio', 'stillSettings.compositionStyle',
        'stillSettings.focalPoint', 'stillSettings.variationCount',
        'stillSettings.carouselCardCount', 'stillSettings.hookStrategy',
        'stillSettings.storyArcType', 'stillSettings.visualConsistency',
        'stillSettings.ctaPlacement'
      ]
    };
    return phaseFieldMap[phaseId] || [];
  };

  // Generate a summary of fields collected in current phase
  // briefToUse: optional parameter to use a specific brief (for when React state is stale)
  const generatePhaseSummary = (phaseId, briefToUse = null) => {
    const fields = getPhaseFields(phaseId);
    const phaseInfo = PHASE_INFO[phaseId];
    const summaryItems = [];
    const brief = briefToUse || creativeBrief;

    const fieldLabels = {
      product: 'Product/Service',
      targetAudience: 'Target Audience',
      desiredEmotion: 'Desired Emotion',
      objective: 'Campaign Objective',
      geography: 'Geography',
      marketingAngle: 'Marketing Angle',
      creativeRiskTolerance: 'Creative Boldness',
      selectedHeadline: 'Headline',
      tone: 'Tone',
      copyStyle: 'Copy Style',
      cta: 'Call to Action',
      legalRequirements: 'Legal Requirements',
      visualStyle: 'Visual Style',
      imageType: 'Image Type',
      sceneType: 'Scene Type',
      peopleDescription: 'People Description',
      photographyMood: 'Photography Mood',
      brandColors: 'Brand Colors',
      focusHierarchy: 'Focus Hierarchy',
      textOverlays: 'Text Overlays',
      textOverlayDetails: 'Text Overlay Details',
      selectedCasting: 'Selected Casting',
      'stillSettings.aspectRatio': 'Aspect Ratio',
      'stillSettings.compositionStyle': 'Composition Style',
      'stillSettings.focalPoint': 'Focal Point',
      'stillSettings.variationCount': 'Variation Count',
      'stillSettings.carouselCardCount': 'Carousel Cards',
      'stillSettings.hookStrategy': 'Hook Strategy',
      'stillSettings.storyArcType': 'Story Arc',
      'stillSettings.visualConsistency': 'Visual Consistency',
      'stillSettings.ctaPlacement': 'CTA Placement'
    };

    for (const field of fields) {
      // Handle nested fields (e.g., stillSettings.aspectRatio)
      const value = conversationFlowService.getNestedValue(brief, field);

      // Skip empty values, empty arrays, and empty objects
      if (!value || (Array.isArray(value) && value.length === 0) ||
        (typeof value === 'object' && Object.keys(value).length === 0)) {
        continue;
      }

      const label = fieldLabels[field] || field;
      // Format display value - for objects, try to extract meaningful text
      let displayValue;
      if (typeof value === 'object') {
        // For objects like selectedLocation, try to get a description
        displayValue = value.description || value.title || value.name || value.text ||
          (value.option?.description) || (value.option?.title) ||
          (Array.isArray(value) ? value.join(', ') : JSON.stringify(value));
      } else {
        displayValue = value;
      }
      summaryItems.push(`• **${label}:** ${displayValue}`);
    }

    if (summaryItems.length === 0) {
      return null;
    }

    return `Here's what we've decided so far with ${phaseInfo?.title || phaseId}:\n\n${summaryItems.join('\n')}`;
  };

  // Handle phase summary confirmation
  const handlePhaseSummaryResponse = async (option) => {
    if (option.value === 'confirm_phase') {
      // User confirmed, proceed to next phase
      const navContext = conversationFlowService.getNavigationContext(currentPhase, phaseStep, creativeBrief);
      console.log('[ConversationalCreativeV2] handlePhaseSummaryResponse navContext:', navContext);
      console.log('[ConversationalCreativeV2] handlePhaseSummaryResponse creativeBrief has selectedStoryScenario:', !!creativeBrief.selectedStoryScenario);

      if (navContext.nextPhase) {
        const transitionMessage = navContext.phaseTransitionMessage;
        if (transitionMessage) {
          addAIMessage(transitionMessage);
        }
        setTimeout(() => startPhase(navContext.nextPhase, creativeBrief), 800);
      } else {
        // No more phases - complete the flow
        console.log('[ConversationalCreativeV2] Calling handleFlowComplete with creativeBrief, selectedStoryScenario:', !!creativeBrief.selectedStoryScenario);
        handleFlowComplete(creativeBrief);
      }
    } else if (option.value === 'change_field') {
      // User wants to change something - show options for what to change
      const fields = getPhaseFields(currentPhase);
      const changeOptions = fields
        .filter(field => creativeBrief[field])
        .map(field => ({
          text: `Change ${field.replace(/([A-Z])/g, ' $1').toLowerCase()}`,
          value: field,
          action: 'edit_field'
        }));

      // Always provide a way to continue even if no fields to edit
      if (changeOptions.length === 0) {
        changeOptions.push({
          text: "Actually, let's continue",
          value: 'confirm_phase',
          action: 'phase_confirm'
        });
      } else {
        changeOptions.push({
          text: "Never mind, continue",
          value: 'confirm_phase',
          action: 'phase_confirm'
        });
      }

      addAIMessage("What would you like to change?", changeOptions);
    }
  };

  // Handle editing a specific field
  const handleEditField = (field) => {
    // Mark that we're editing
    setIsEditingPhase(true);

    // Clear the field value so user can re-enter
    updateCreativeBrief(field, null);

    // Find the step index for this field and re-execute it
    const stepIndex = conversationFlowService.findStepByField(currentPhase, field);
    if (stepIndex >= 0) {
      setPhaseStep(stepIndex);
      setTimeout(() => executeStep(currentPhase, stepIndex), 500);
    }
  };

  const advanceFlow = async (updatedBrief = null, stepOverride = null) => {
    // Use provided updated brief or fall back to current state
    // This is important because React state updates are async
    const briefForNavigation = updatedBrief || creativeBrief;

    // Use step override if provided (for handlers where React state may be stale)
    const currentStep = stepOverride !== null ? stepOverride : phaseStep;

    // If we were editing a field from phase summary, show the updated summary
    if (isEditingPhase) {
      setIsEditingPhase(false);
      const summary = generatePhaseSummary(currentPhase);
      if (summary) {
        addAIMessage("Updated! " + summary + "\n\nAnything else you'd like to change?", [
          { text: "✔ Looks good, continue!", value: 'confirm_phase', action: 'phase_confirm' },
          { text: "✏️ Change something else", value: 'change_field', action: 'phase_change' }
        ]);
        return;
      }
      // If no summary, fall through to continue advancing
    }

    console.log('[ConversationalCreativeV2] advanceFlow called with brief:', {
      dialogueType: briefForNavigation?.videoSettings?.dialogueType,
      currentStep,
      phaseStep,
      currentPhase
    });
    const navContext = conversationFlowService.getNavigationContext(currentPhase, currentStep, briefForNavigation);
    console.log('[ConversationalCreativeV2] advanceFlow navContext:', navContext);

    // Don't complete the video_director phase while first frame or last frame generation is in progress
    // The user needs to see and select from the generated frame options
    if (navContext.isPhaseComplete && currentPhase === 'video_director' && (isGeneratingFirstFrame || isGeneratingLastFrame)) {
      console.log('[ConversationalCreativeV2] advanceFlow: Waiting for frame generation to complete before phase transition');
      return; // The useEffect for frame options will handle displaying the options
    }

    if (navContext.isPhaseComplete) {
      // Phase is complete - always proceed to next phase or complete the flow
      const transitionToNextPhase = () => {
        // Mark strategist phase as complete when transitioning away from it
        // This stops brand-level context from being passed to the AI in subsequent phases
        if (currentPhase === 'strategist' && conversationAgent) {
          conversationAgent.markStrategistComplete();
        }

        if (navContext.nextPhase) {
          const transitionMessage = navContext.phaseTransitionMessage;
          if (transitionMessage) {
            addAIMessage(transitionMessage);
          }
          // Pass the updated brief to ensure correct phase skip evaluation
          setTimeout(() => startPhase(navContext.nextPhase, briefForNavigation), 800);
        } else {
          // No more phases - flow is complete
          // Pass briefForNavigation to ensure we have the latest values (React state may be stale)
          handleFlowComplete(briefForNavigation);
        }
      };

      // Show phase summary before transitioning (optional)
      // Use briefForNavigation to ensure we have the latest values (React state may be stale)
      const summary = generatePhaseSummary(currentPhase, briefForNavigation);
      if (summary) {
        addAIMessage(summary + "\n\nDoes this look good, or would you like to make any changes?", [
          { text: "✔ Looks good, continue!", value: 'confirm_phase', action: 'phase_confirm' },
          { text: "✏️ I'd like to change something", value: 'change_field', action: 'phase_change' }
        ]);
      } else {
        // No summary to show, proceed directly
        transitionToNextPhase();
      }
    } else if (navContext.nextStep !== null) {
      // Not phase complete - advance to next step
      setPhaseStep(navContext.nextStep);
      setTimeout(() => executeStep(currentPhase, navContext.nextStep, false, briefForNavigation), 500);
    } else {
      // Fallback: try to advance anyway to prevent stalling
      console.warn('[ConversationalCreativeV2] advanceFlow: no clear next action, attempting recovery');
      const fallbackNextStep = phaseStep + 1;
      setPhaseStep(fallbackNextStep);
      setTimeout(() => executeStep(currentPhase, fallbackNextStep, false, briefForNavigation), 500);
    }
  };

  const handleFlowComplete = async (updatedBrief = null) => {
    // Flow is complete - trigger actual generation
    // Format selection already happened, so we just need to generate
    // Use provided brief or fall back to React state (which may be stale)
    const brief = updatedBrief || creativeBrief;
    console.log('[ConversationalCreativeV2] Flow complete, ready for generation', { brief });

    // Check if generation options were already shown to prevent duplicates
    const lastMessage = messages[messages.length - 1];
    if (lastMessage?.text?.includes('generating') ||
      lastMessage?.options?.some(o => o.value === 'generate_video' || o.value === 'generate_image')) {
      console.log('[ConversationalCreativeV2] Generation already triggered, skipping duplicate');
      return;
    }

    // Start the generation process based on the selected output type
    const outputType = brief.outputType || 'image';

    if (outputType === 'video') {
      // For video: Generate script and show it in the chat for approval
      addAIMessage("📝 Perfect! Now I'm creating your professional production script based on the opening and closing frames. This script will guide the video generation...");
      await generateProductionScriptInChat(brief);
    } else {
      // For image/carousel: Generate directly
      await generateImageOrCarouselInChat(brief);
    }
  };

  // Auto-save generated creative to library and return the saved item ID
  const autoSaveGeneratedCreative = async (generatedImages, brief, isCarousel, postCaption) => {
    if (!currentUser?.uid || !selectedBrand?.id) return null;

    try {
      if (isCarousel && generatedImages.length > 1) {
        // Save carousel as a single grouped item
        const scenario = brief.selectedStoryScenario;

        // Upload all carousel images to Storage first to avoid document size limits
        const uploadedCarouselImages = await Promise.all(
          generatedImages.map(async (img, idx) => {
            let imageUrl = img.imageUrl || '';
            // Check if it's a base64 data URL and upload to Storage
            if (imageUrl && imageUrl.startsWith('data:image')) {
              try {
                imageUrl = await uploadLibraryImage(imageUrl, currentUser.uid, selectedBrand.id, 'carousel');
              } catch (uploadError) {
                console.error(`[autoSaveGeneratedCreative] Failed to upload carousel image ${idx}:`, uploadError);
              }
            }
            return {
              imageUrl,
              title: img.title || `Card ${idx + 1}`,
              position: idx + 1,
              headline: scenario?.cards?.[idx]?.headline || '',
              subtext: scenario?.cards?.[idx]?.subtext || '',
              role: scenario?.cards?.[idx]?.role || ''
            };
          })
        );

        const carouselItem = {
          name: scenario?.title || `Carousel - ${brief.product || 'AI Generated'}`,
          description: scenario?.description || brief.product || 'AI Generated Carousel',
          type: 'carousel',
          imageUrl: uploadedCarouselImages[0]?.imageUrl || '',
          carouselImages: uploadedCarouselImages,
          caption: postCaption?.caption || '',
          hashtags: postCaption?.hashtags || '',
          fullPost: postCaption?.fullPost || '',
          scenario: scenario ? {
            id: scenario.id || 'custom',
            title: scenario.title || '',
            storyArcType: scenario.storyArcType || '',
            isCustom: scenario.isCustom || false
          } : null,
          tags: ['carousel', 'ai-generated', brief.stillSettings?.storyArcType || 'story-arc'].filter(Boolean),
          metadata: {
            product: brief.product || '',
            audience: brief.audience || '',
            platform: brief.stillSettings?.platform || 'instagram',
            aspectRatio: brief.stillSettings?.aspectRatio || '1:1',
            cardCount: generatedImages.length,
            generatedAt: new Date().toISOString()
          }
        };
        const savedIds = await batchSaveItems([carouselItem], currentUser.uid, selectedBrand.id);
        return savedIds[0] || null;
      } else {
        // Save ALL image variations
        const imageItems = await Promise.all(
          generatedImages.filter(img => img.imageUrl).map(async (img, idx) => {
            let imageUrl = img.imageUrl;
            // Check if it's a base64 data URL and upload to Storage
            if (imageUrl && imageUrl.startsWith('data:image')) {
              try {
                imageUrl = await uploadLibraryImage(imageUrl, currentUser.uid, selectedBrand.id, 'image');
              } catch (uploadError) {
                console.error(`[autoSaveGeneratedCreative] Failed to upload image ${idx}:`, uploadError);
              }
            }

            return {
              name: img.title || `Generated Image ${idx + 1}`,
              description: brief.headline || brief.product || 'AI Generated',
              imageUrl: imageUrl,
              imagePrompt: img.prompt?.substring(0, 500) || '',
              type: 'image',
              tags: ['image', 'ai-generated', img.variationStyle?.toLowerCase() || ''].filter(Boolean),
              metadata: {
                product: brief.product || '',
                audience: brief.audience || '',
                visualStyle: brief.visualStyle || '',
                variationStyle: img.variationStyle || '',
                variationIndex: idx,
                totalVariations: generatedImages.length,
                generatedAt: new Date().toISOString()
              }
            };
          })
        );

        if (imageItems.length === 0) return null;

        const savedIds = await batchSaveItems(imageItems, currentUser.uid, selectedBrand.id);

        // Return mapping of image IDs to library IDs
        // NOTE: We need to map the filtered images to their saved IDs
        const validImages = generatedImages.filter(img => img.imageUrl);
        const idMapping = {};
        validImages.forEach((img, idx) => {
          if (savedIds[idx]) {
            idMapping[img.id] = savedIds[idx];
          }
        });

        console.log('[autoSaveGeneratedCreative] Saved library IDs:', idMapping);

        // Update state with library IDs
        setGeneratedImageLibraryIds(prev => ({ ...prev, ...idMapping }));

        return savedIds[0] || null;
      }
    } catch (error) {
      console.error('[autoSaveGeneratedCreative] Error:', error);
      throw error;
    }
  };

  // Generate image or carousel and show results inline in the chat
  const generateImageOrCarouselInChat = async (updatedBrief = null) => {
    const brief = updatedBrief || creativeBrief;
    const outputType = brief.outputType || 'image';
    const stillSettings = brief.stillSettings || {};
    const isCarousel = outputType === 'carousel';

    // Determine how many images to generate
    const imageCount = isCarousel
      ? (parseInt(stillSettings.carouselCardCount) || 4)
      : (parseInt(stillSettings.variationCount) || 1);

    // Check if this is a direct mode generation
    const isDirectModeGeneration = !!brief.directModePrompt;

    console.log('[ConversationalCreativeV2] Starting image/carousel generation', {
      outputType,
      imageCount,
      stillSettings,
      hasSelectedScenario: !!brief.selectedStoryScenario,
      scenarioTitle: brief.selectedStoryScenario?.title,
      scenarioCardCount: brief.selectedStoryScenario?.cards?.length,
      briefFromParam: !!updatedBrief,
      briefFromState: !updatedBrief,
      isDirectModeGeneration,
      directModePrompt: brief.directModePrompt?.substring(0, 100)
    });

    // Show loading message
    const loadingText = isCarousel
      ? `🎨 Generating your ${imageCount}-card carousel...`
      : `🎨 Generating your image${imageCount > 1 ? ` (${imageCount} variations)` : ''}...`;
    addAIMessage(loadingText, null, { showImageGenerationLoading: true });

    try {
      // Build reference images from brief
      const referenceImages = [];

      // Add brand logo as reference image (CRITICAL for logo display)
      const brandLogo = selectedBrand?.logo || brief.logo;
      if (brandLogo) {
        referenceImages.push({
          url: brandLogo,
          instruction: `REFERENCE IMAGE (BRAND LOGO):
CRITICAL: Include this EXACT logo in every generated image. Place it in the top-right or bottom-right corner.
The logo must be clearly visible, properly sized, and maintain its original colors and proportions.
DO NOT generate a different logo - use THIS exact image.`
        });
      }

      // Add selected casting/actor image
      const selectedCasting = brief.selectedCasting;
      if (selectedCasting?.imageUrl) {
        referenceImages.push({
          url: selectedCasting.imageUrl,
          instruction: `REFERENCE IMAGE (SELECTED TALENT):
CRITICAL: Maintain this person's exact appearance, face, and characteristics in all generated images.`
        });
      }

      // Add selected location image
      const selectedLocation = brief.selectedLocation;
      if (selectedLocation?.imageUrl) {
        referenceImages.push({
          url: selectedLocation.imageUrl,
          instruction: `REFERENCE IMAGE (SELECTED LOCATION):
Use this as the background/setting. Match the lighting and atmosphere.`
        });
      }

      // Add product images from selected assets - with stronger instructions
      const productImages = brief.selectedAssets || brief.crawledImages || [];
      productImages.slice(0, 2).forEach((img, idx) => {
        const imgUrl = typeof img === 'string' ? img : img.url;
        if (imgUrl) {
          referenceImages.push({
            url: imgUrl,
            instruction: `REFERENCE IMAGE (PRODUCT ${idx + 1}):
CRITICAL: The product in the generated image MUST match this reference exactly.
- Maintain the exact shape, color, design, and branding of the product
- Integrate naturally into the scene with realistic lighting
- The product should be recognizable as the same item
DO NOT create a generic or different product - use THIS specific product design.`
          });
        }
      });

      // Add direct mode style reference images
      const directModeStyleRefs = brief.referenceStyleImages || [];
      directModeStyleRefs.forEach((img, idx) => {
        if (img.url) {
          referenceImages.push({
            url: img.url,
            instruction: `STYLE REFERENCE IMAGE ${img.label || `[Image ${String.fromCharCode(65 + idx)}]`}:
Use this image as a style/mood reference. Match its visual aesthetic, colors, and overall vibe.`
          });
        }
      });

      console.log('[ConversationalCreativeV2] Reference images for generation:', {
        hasLogo: !!brandLogo,
        hasCasting: !!selectedCasting?.imageUrl,
        hasLocation: !!selectedLocation?.imageUrl,
        productCount: productImages.length,
        hasSelectedScenario: !!brief.selectedStoryScenario,
        directModeStyleRefs: directModeStyleRefs.length
      });

      // Get brand colors
      const userColors = brief.brandColors || colorPalette || [];
      const colorString = userColors.length > 0 ? userColors.join(', ') : '';

      // Build prompts for each image/card
      const prompts = [];

      if (isDirectModeGeneration) {
        // Direct mode: use the user-crafted prompt
        const basePrompt = brief.directModePrompt;

        // Add brand context if available
        let enhancedPrompt = basePrompt;
        if (selectedBrand?.name) {
          enhancedPrompt = `Brand: ${selectedBrand.name}\n\n${basePrompt}`;
        }
        if (colorString) {
          enhancedPrompt += `\n\nBrand colors to incorporate: ${colorString}`;
        }

        const productName = brief.product?.split(' ').slice(0, 3).join(' ') || 'Creative';

        for (let i = 0; i < imageCount; i++) {
          const variationName = imageCount > 1
            ? `${productName || 'Creative'} - Version ${i + 1}`
            : productName || 'Creative';

          prompts.push({
            index: i,
            title: variationName,
            description: basePrompt.substring(0, 100) + (basePrompt.length > 100 ? '...' : ''),
            prompt: enhancedPrompt
          });
        }
      } else if (isCarousel) {
        // Generate carousel card prompts with story arc
        const hookStrategy = stillSettings.hookStrategy || 'bold statement';
        const storyArcType = stillSettings.storyArcType || 'problem-solution';
        const ctaPlacement = stillSettings.ctaPlacement || 'final card';

        for (let i = 0; i < imageCount; i++) {
          const cardPosition = getCardNarrative(i, imageCount, hookStrategy, storyArcType, ctaPlacement);
          prompts.push({
            index: i,
            title: `Card ${i + 1}: ${cardPosition.title}`,
            description: cardPosition.description,
            prompt: buildCarouselCardPrompt(brief, stillSettings, cardPosition, colorString, i, imageCount)
          });
        }
      } else {
        // Generate single image or variations with meaningful names
        const variationStyles = [
          { suffix: 'Hero', focus: 'primary product focus' },
          { suffix: 'Lifestyle', focus: 'lifestyle context' },
          { suffix: 'Detail', focus: 'close-up details' },
          { suffix: 'Environment', focus: 'environmental setting' },
          { suffix: 'Action', focus: 'dynamic energy' },
          { suffix: 'Minimal', focus: 'clean minimal style' }
        ];

        const productName = brief.product?.split(' ').slice(0, 3).join(' ') || 'Creative';
        const isInfographic = brief.imageType === 'infographic';
        const imageTypeName = isInfographic ? 'Infographic' : 'Ad';

        for (let i = 0; i < imageCount; i++) {
          const style = variationStyles[i % variationStyles.length];
          const variationName = imageCount > 1
            ? `${productName} - ${imageTypeName} ${style.suffix}`
            : `${productName} - ${imageTypeName}`;

          prompts.push({
            index: i,
            title: variationName,
            description: brief.headline || brief.product || 'Ad creative',
            variationStyle: style.suffix,
            prompt: buildSingleImagePrompt(brief, stillSettings, colorString, i, imageCount)
          });
        }
      }

      // Start background image generation job
      // This persists across page reloads and PC sleep
      console.log('[ConversationalCreativeV2] Starting background image generation job...');

      const jobResult = await startImageGenerationJob({
        userId: currentUser?.uid,
        brandId: selectedBrand?.id,
        chatId: chatId,
        prompts: prompts,
        referenceImages: referenceImages,
        aspectRatio: stillSettings.aspectRatio || '1:1',
        outputType: outputType,
        creativeBrief: brief
      });

      console.log('[ConversationalCreativeV2] Job started:', jobResult.jobId);

      // Store job ID - the useEffect will handle updates and completion
      setImageJobId(jobResult.jobId);

      // Update loading message with job ID for tracking
      setMessages(prev => prev.map(m => {
        if (m.data?.showImageGenerationLoading) {
          return {
            ...m,
            text: `Generating ${prompts.length} image${prompts.length > 1 ? 's' : ''}... (0/${prompts.length})\n\n_You can close this page and come back later - generation will continue in the background._`,
            data: {
              ...m.data,
              jobId: jobResult.jobId,
              progress: { current: 0, total: prompts.length }
            }
          };
        }
        return m;
      }));

    } catch (error) {
      console.error('[ConversationalCreativeV2] Image/carousel generation error:', error);

      // Remove loading message
      setMessages(prev => prev.filter(m => !m.data?.showImageGenerationLoading));

      // Check if it's a billing/credits error
      if (error.message?.includes('Insufficient credits') || error.message?.includes('subscription')) {
        addAIMessage(`${error.message}`, [
          { text: "View Billing", value: 'view_billing', action: 'view_billing' }
        ]);
      } else {
        addAIMessage(`Something went wrong: ${error.message}`, [
          { text: "Try Again", value: 'retry_generation', action: 'retry_generation' }
        ]);
      }
    }
  };

  // Helper: Get narrative description for carousel card position
  const getCardNarrative = (cardIndex, totalCards, hookStrategy, storyArcType, ctaPlacement) => {
    const isFirstCard = cardIndex === 0;
    const isLastCard = cardIndex === totalCards - 1;
    const isMiddleCard = !isFirstCard && !isLastCard;

    if (isFirstCard) {
      // Hook card - grab attention
      const hookTitles = {
        'bold statement': 'Bold Statement Hook',
        'intriguing question': 'Question Hook',
        'visual surprise': 'Visual Surprise',
        'problem highlight': 'Problem Statement',
        'statistic shock': 'Shocking Statistic'
      };
      return {
        title: hookTitles[hookStrategy] || 'Hook',
        description: 'Opening card to grab attention and stop the scroll',
        role: 'hook',
        hookStrategy
      };
    }

    if (isLastCard && (ctaPlacement === 'final card' || ctaPlacement === 'last card')) {
      return {
        title: 'Call to Action',
        description: 'Final card with clear call-to-action',
        role: 'cta'
      };
    }

    // Middle cards - build the story
    const middleCardIndex = cardIndex - 1;
    const middleCardCount = totalCards - (ctaPlacement === 'final card' ? 2 : 1);

    const storyRoles = {
      'problem-solution': ['Problem Deep-Dive', 'Solution Introduction', 'Solution Benefits', 'Proof/Results'],
      'feature-showcase': ['Feature 1', 'Feature 2', 'Feature 3', 'Feature 4'],
      'transformation': ['Before State', 'The Change', 'After State', 'Ongoing Benefits'],
      'storytelling': ['Setup', 'Rising Action', 'Climax', 'Resolution'],
      'educational': ['Concept', 'Explanation', 'Example', 'Application']
    };

    const roles = storyRoles[storyArcType] || storyRoles['feature-showcase'];
    const roleTitle = roles[middleCardIndex % roles.length] || `Content ${middleCardIndex + 1}`;

    return {
      title: roleTitle,
      description: `Build the narrative with ${roleTitle.toLowerCase()}`,
      role: 'build',
      position: middleCardIndex + 1
    };
  };

  // Helper: Generate card-specific text content for carousel
  const getCarouselCardText = (brief, cardNarrative, cardIndex, totalCards) => {
    const cta = brief.cta || 'Learn More';
    const product = brief.product || 'Product';

    // CHECK FOR AI-GENERATED SCENARIO FIRST
    // If user selected a story scenario, use the specific card content from that
    const selectedScenario = brief.selectedStoryScenario;

    console.log(`[getCarouselCardText] Card ${cardIndex + 1} - Checking for scenario:`, {
      hasScenario: !!selectedScenario,
      scenarioTitle: selectedScenario?.title,
      cardCount: selectedScenario?.cards?.length,
      cardPositions: selectedScenario?.cards?.map(c => c.position)
    });

    if (selectedScenario?.cards && selectedScenario.cards.length > 0) {
      // Find the card for this position (1-indexed in scenario)
      const scenarioCard = selectedScenario.cards.find(c => c.position === cardIndex + 1);
      if (scenarioCard) {
        console.log(`[getCarouselCardText] ✔ Using AI-generated content for card ${cardIndex + 1}:`, scenarioCard.headline);
        return {
          headline: scenarioCard.headline || `Card ${cardIndex + 1}`,
          subtext: scenarioCard.subtext || '',
          cta: scenarioCard.role === 'cta' ? cta : ''
        };
      } else {
        console.warn(`[getCarouselCardText] ✗ No scenario card found for position ${cardIndex + 1}`);
      }
    } else {
      console.warn(`[getCarouselCardText] ✗ No valid scenario or cards - using fallback text`);
    }

    // FALLBACK: Generate default text based on card role
    // (Only used if no scenario is selected)
    const headline = brief.headline || brief.product || 'Your Solution';
    const audience = brief.audience || '';

    if (cardNarrative.role === 'hook') {
      return {
        headline: headline,
        subtext: brief.emotionalHook || `For ${audience}`.trim() || '',
        cta: 'Swipe to learn more →'
      };
    } else if (cardNarrative.role === 'cta') {
      return {
        headline: cta.toUpperCase(),
        subtext: `Get started with ${product} today`,
        cta: cta
      };
    } else {
      // Build cards - feature/benefit focused (fallback only)
      const buildTexts = {
        'Problem Deep-Dive': { headline: 'The Problem', subtext: 'What you\'re facing today' },
        'Solution Introduction': { headline: 'The Solution', subtext: `How ${product} helps` },
        'Solution Benefits': { headline: 'Key Benefits', subtext: 'What you\'ll gain' },
        'Proof/Results': { headline: 'Real Results', subtext: 'See the difference' },
        'Feature 1': { headline: 'Feature Spotlight', subtext: 'What makes us different' },
        'Feature 2': { headline: 'More to Love', subtext: 'Additional benefits' },
        'Feature 3': { headline: 'Why Choose Us', subtext: 'The advantage' },
        'Feature 4': { headline: 'The Details', subtext: 'What\'s included' },
        'Before State': { headline: 'Before', subtext: 'The old way' },
        'The Change': { headline: 'The Transformation', subtext: 'Making the switch' },
        'After State': { headline: 'After', subtext: 'The new reality' },
        'Ongoing Benefits': { headline: 'Long-term Value', subtext: 'Benefits that last' }
      };
      const defaultText = buildTexts[cardNarrative.title] || {
        headline: cardNarrative.title,
        subtext: `Card ${cardIndex + 1} of ${totalCards}`
      };
      return { ...defaultText, cta: '' };
    }
  };

  // Helper: Build prompt for a carousel card
  const buildCarouselCardPrompt = (brief, stillSettings, cardNarrative, colorString, cardIndex, totalCards) => {
    const aspectRatio = stillSettings.aspectRatio || '1:1';
    const compositionStyle = stillSettings.compositionStyle || 'centered';
    const visualConsistency = stillSettings.visualConsistency || 'high';

    // Get text content for this card
    const cardText = getCarouselCardText(brief, cardNarrative, cardIndex, totalCards);

    let cardSpecificInstructions = '';

    if (cardNarrative.role === 'hook') {
      cardSpecificInstructions = `
CARD ROLE: HOOK (First Card - Grab Attention)
- This card must STOP THE SCROLL
- Hook strategy: ${cardNarrative.hookStrategy}
- Create visual intrigue that demands the viewer swipe
- Bold, attention-grabbing composition
- HEADLINE TEXT TO INCLUDE: "${cardText.headline}"
- SUBTEXT TO INCLUDE: "${cardText.subtext}"`;
    } else if (cardNarrative.role === 'cta') {
      cardSpecificInstructions = `
CARD ROLE: CALL TO ACTION (Final Card)
- This card drives action
- CTA BUTTON TEXT: "${cardText.cta}"
- HEADLINE TEXT: "${cardText.headline}"
- SUBTEXT: "${cardText.subtext}"
- Include a clear, tappable CTA button design
- Product prominently featured`;
    } else {
      cardSpecificInstructions = `
CARD ROLE: BUILD (${cardNarrative.title})
- This card advances the narrative
- Position in story: Card ${cardIndex + 1} of ${totalCards}
- HEADLINE TEXT: "${cardText.headline}"
- SUBTEXT: "${cardText.subtext}"
- Maintain visual flow with other cards`;
    }

    return `SOCIAL MEDIA CAROUSEL AD - CARD ${cardIndex + 1} OF ${totalCards}

CREATE A PUBLISH-READY CAROUSEL CARD WITH TEXT OVERLAYS.

PRODUCT/SERVICE: ${brief.product || 'Product'}
TARGET AUDIENCE: ${brief.audience || 'General audience'}
MARKETING ANGLE: ${brief.marketingAngle || 'Product benefits'}
TONE: ${brief.tone || 'Professional and engaging'}

${cardSpecificInstructions}

TEXT OVERLAY REQUIREMENTS (MUST INCLUDE):
- HEADLINE: "${cardText.headline}" - Large, bold text, prominently placed
${cardText.subtext ? `- SUBTEXT: "${cardText.subtext}" - Smaller supporting text below headline` : ''}
${cardNarrative.role === 'cta' ? `- CTA BUTTON: "${cardText.cta}" - Clear button design at bottom` : ''}
- LOGO: Use the EXACT brand logo from reference image - place in top-right or bottom-right corner
- Text must be legible against the background (use contrast, shadows, or overlays)
- Typography should be modern, clean, and on-brand
- IMPORTANT: Use the SPECIFIC text shown above, not generic placeholders

VISUAL SPECIFICATIONS:
- Aspect Ratio: ${aspectRatio}
- Composition: ${compositionStyle}
- Visual Consistency: ${visualConsistency} (maintain consistent style across all cards)
${brief.visualStyle ? `- Visual Style: ${brief.visualStyle}` : ''}
${brief.photographyMood ? `- Mood: ${brief.photographyMood}` : ''}
${colorString ? `- Brand Colors: Use these colors for text and design elements: ${colorString}` : ''}
${brief.peopleDescription ? `- People: ${brief.peopleDescription}` : ''}

CRITICAL REQUIREMENTS:
- Professional social media advertising quality
- INCLUDE all specified text overlays - this is a publish-ready ad
- Text must be readable and professionally designed
- Consistent typography and style across all carousel cards
- ${stillSettings.focalPoint ? `Focal Point: ${stillSettings.focalPoint}` : 'Clear visual hierarchy with text as key element'}

OUTPUT: A complete, publish-ready social media carousel card with all text overlays, ready to post.`;
  };

  // Helper: Build prompt for a single image
  const buildSingleImagePrompt = (brief, stillSettings, colorString, variationIndex, totalVariations) => {
    const aspectRatio = stillSettings.aspectRatio || '1:1';
    const compositionStyle = stillSettings.compositionStyle || 'centered';
    const isInfographic = brief.imageType === 'infographic';

    // Debug logging for infographic detection
    console.log('[buildSingleImagePrompt] imageType:', brief.imageType, '| isInfographic:', isInfographic);
    if (isInfographic) {
      console.log('[buildSingleImagePrompt] Infographic details:', {
        infographicType: brief.infographicType,
        infographicSections: brief.infographicSections,
        infographicStyle: brief.infographicStyle,
        infographicContent: brief.infographicContent?.substring(0, 100)
      });
    }

    const variationNote = totalVariations > 1
      ? `\n\nVARIATION ${variationIndex + 1} OF ${totalVariations}: Create a unique interpretation while maintaining brand consistency.`
      : '';

    // Build infographic-specific section if applicable
    let infographicSection = '';
    if (isInfographic) {
      const infographicType = brief.infographicType || 'feature_breakdown';
      const infographicSections = brief.infographicSections || 4;
      const infographicContent = brief.infographicContent || '';
      const infographicStyle = brief.infographicStyle || 'modern';

      infographicSection = `
INFOGRAPHIC SPECIFICATIONS:
- Type: ${infographicType}
- Sections/Points: ${infographicSections}
- Style: ${infographicStyle}
${infographicContent ? `- Content: ${infographicContent}` : ''}

INFOGRAPHIC REQUIREMENTS:
- INCLUDE clear, readable text labels and headings
- Use icons and visual elements to represent each section
- Maintain visual hierarchy with larger headers and supporting text
- Text should be part of the design, not separate overlays
- Use the brand colors for consistency
- Make data/information easy to scan and understand
`;
    }

    // Different requirements for infographics vs regular ads
    const criticalRequirements = isInfographic
      ? `CRITICAL REQUIREMENTS:
- Professional infographic quality suitable for digital sharing
- INCLUDE clear, readable text as part of the design
- Text must be legible and well-integrated into the visual
- Information hierarchy should be clear at a glance
- Clean, modern aesthetic with balanced use of graphics and text`
      : `CRITICAL REQUIREMENTS:
- Professional advertising quality
- NO text overlays, NO headlines, NO CTA buttons in the image
- Clean, polished visuals suitable for digital advertising
- Product/service prominently and naturally featured`;

    // Include user's image description if they provided one (when generating from scratch)
    const imageDescriptionSection = brief.imageDescription
      ? `
USER'S VISION:
${brief.imageDescription}
(Use this description as the primary guidance for what the image should show)
`
      : '';

    return `${isInfographic ? 'INFOGRAPHIC' : 'ADVERTISING IMAGE'}

PRODUCT/SERVICE: ${brief.product || 'Product'}
TARGET AUDIENCE: ${brief.audience || 'General audience'}
MARKETING ANGLE: ${brief.marketingAngle || 'Product benefits'}
EMOTIONAL HOOK: ${brief.emotionalHook || 'Trust and confidence'}
${brief.headline ? `HEADLINE CONTEXT: ${brief.headline}` : ''}
${variationNote}
${imageDescriptionSection}${infographicSection}
VISUAL SPECIFICATIONS:
- Aspect Ratio: ${aspectRatio}
- Composition: ${compositionStyle}
${brief.visualStyle ? `- Visual Style: ${brief.visualStyle}` : ''}
${brief.photographyMood ? `- Mood: ${brief.photographyMood}` : ''}
${colorString ? `- Brand Colors: ${colorString}` : ''}
${brief.peopleDescription ? `- People: ${brief.peopleDescription}` : ''}
${stillSettings.focalPoint ? `- Focal Point: ${stillSettings.focalPoint}` : ''}

${criticalRequirements}

OUTPUT: A ${isInfographic ? 'polished infographic with integrated text and visuals' : 'single, clean advertising image'} ready for use in digital campaigns.`;
  };

  // Helper: Generate social media caption and hashtags
  const generateSocialMediaCaption = (brief, isCarousel) => {
    const product = brief.product || 'our product';
    const audience = brief.audience || '';
    const headline = brief.headline || '';
    const cta = brief.cta || 'Shop now';
    const emotionalHook = brief.emotionalHook || '';
    const marketingAngle = brief.marketingAngle || '';

    // Get story arc content if available
    const selectedScenario = brief.selectedStoryScenario;
    const storyArcCards = selectedScenario?.cards || [];

    // Build the main caption
    let captionParts = [];

    // If we have a story arc scenario, use its card content for the caption
    if (selectedScenario && storyArcCards.length > 0) {
      // Use the hook card headline as the attention grabber
      const hookCard = storyArcCards.find(c => c.role === 'hook') || storyArcCards[0];
      if (hookCard?.headline) {
        captionParts.push(hookCard.headline);
      }

      // Add engaging body from build cards - use subtext for more natural copy
      const buildCards = storyArcCards.filter(c => c.role === 'build');
      if (buildCards.length > 0) {
        // Use the first build card's subtext as it's more descriptive
        const firstBuild = buildCards[0];
        if (firstBuild?.subtext) {
          captionParts.push(`\n\n${firstBuild.subtext}`);
        }
      }

      // Get the CTA card for the call to action
      const ctaCard = storyArcCards.find(c => c.role === 'cta');
      if (ctaCard) {
        // Use the CTA headline and subtext for a strong closing
        if (ctaCard.headline) {
          captionParts.push(`\n\n${ctaCard.headline}`);
        }
      }
    } else {
      // Fallback: Opening hook based on headline or emotional hook
      if (headline) {
        captionParts.push(headline);
      } else if (emotionalHook) {
        captionParts.push(`${emotionalHook} ✨`);
      }

      // Body text based on marketing angle
      if (marketingAngle) {
        captionParts.push(`\n\n${marketingAngle}`);
      } else if (audience) {
        captionParts.push(`\n\nPerfect for ${audience}.`);
      }
    }

    // Carousel swipe prompt
    if (isCarousel) {
      captionParts.push('\n\n👉 Swipe through to see the full story!');
    }

    // Call to action with link prompt
    captionParts.push(`\n\n${cta} - Link in bio 🔗`);

    const caption = captionParts.join('');

    // Generate relevant hashtags based on brief content
    const hashtags = generateHashtags(brief);

    return {
      caption,
      hashtags,
      fullPost: `${caption}\n\n${hashtags}`
    };
  };

  // Helper: Generate hashtags based on brief content
  const generateHashtags = (brief) => {
    const tags = new Set();

    // Add product-related tags
    if (brief.product) {
      const productTag = brief.product.toLowerCase().replace(/[^a-z0-9]/g, '');
      if (productTag.length > 2) tags.add(`#${productTag}`);
    }

    // Add industry/niche tags based on audience
    const audienceKeywords = (brief.audience || '').toLowerCase();
    if (audienceKeywords.includes('business') || audienceKeywords.includes('entrepreneur')) {
      tags.add('#business');
      tags.add('#entrepreneur');
    }
    if (audienceKeywords.includes('fitness') || audienceKeywords.includes('health')) {
      tags.add('#fitness');
      tags.add('#health');
      tags.add('#wellness');
    }
    if (audienceKeywords.includes('tech') || audienceKeywords.includes('software')) {
      tags.add('#tech');
      tags.add('#innovation');
    }
    if (audienceKeywords.includes('food') || audienceKeywords.includes('restaurant')) {
      tags.add('#foodie');
      tags.add('#foodlover');
    }
    if (audienceKeywords.includes('fashion') || audienceKeywords.includes('style')) {
      tags.add('#fashion');
      tags.add('#style');
    }
    if (audienceKeywords.includes('home') || audienceKeywords.includes('decor')) {
      tags.add('#homedecor');
      tags.add('#interiordesign');
    }

    // Add marketing-related tags
    tags.add('#ad');

    // Add emotional/benefit tags based on emotional hook
    const emotionalHook = (brief.emotionalHook || '').toLowerCase();
    if (emotionalHook.includes('trust') || emotionalHook.includes('reliable')) {
      tags.add('#trusted');
    }
    if (emotionalHook.includes('transform') || emotionalHook.includes('change')) {
      tags.add('#transformation');
    }
    if (emotionalHook.includes('easy') || emotionalHook.includes('simple')) {
      tags.add('#madeeasy');
    }
    if (emotionalHook.includes('luxury') || emotionalHook.includes('premium')) {
      tags.add('#luxury');
      tags.add('#premium');
    }

    // Add generic engagement tags
    tags.add('#linkinbio');

    // Limit to 15 hashtags for best engagement
    return Array.from(tags).slice(0, 15).join(' ');
  };

  // Generate production script and show it inline in the chat
  const generateProductionScriptInChat = async (updatedBrief = null) => {
    // Use provided brief or fall back to React state (which may be stale)
    const brief = updatedBrief || creativeBrief;

    if (!conversationAgent) {
      console.error('[ConversationalCreativeV2] No conversation agent available for script generation');
      addAIMessage("❌ Unable to generate script. Please try again.", [
        { text: "🔄 Try Again", action: 'retry_script' }
      ]);
      return;
    }

    const videoSettings = brief.videoSettings || {};
    // Use refs as fallback for frames (React state timing issues)
    let firstFrame = videoSettings.firstFrame || latestFirstFrameRef.current;
    let lastFrame = videoSettings.lastFrame || latestLastFrameRef.current;

    // Recovery: Try to find frames from message history if not available
    if (!firstFrame || !lastFrame) {
      console.log('[ScriptGen] Frame missing, attempting recovery from messages...');
      for (let i = messages.length - 1; i >= 0; i--) {
        const msg = messages[i];
        // Check if this message has frame options
        if (!firstFrame && msg.data?.firstFrameOptions && messages[i + 1]?.role === 'user') {
          // The next message after options is the user's selection
          const userChoice = messages[i + 1]?.text;
          const matchedFrame = msg.data.firstFrameOptions.find(opt =>
            opt.title === userChoice || opt.name === userChoice
          );
          if (matchedFrame) {
            console.log('[ScriptGen] Recovered firstFrame from messages:', matchedFrame.title);
            firstFrame = matchedFrame;
            latestFirstFrameRef.current = matchedFrame;
          }
        }
        if (!lastFrame && msg.data?.lastFrameOptions && messages[i + 1]?.role === 'user') {
          const userChoice = messages[i + 1]?.text;
          const matchedFrame = msg.data.lastFrameOptions.find(opt =>
            opt.title === userChoice || opt.name === userChoice
          );
          if (matchedFrame) {
            console.log('[ScriptGen] Recovered lastFrame from messages:', matchedFrame.title);
            lastFrame = matchedFrame;
            latestLastFrameRef.current = matchedFrame;
          }
        }
      }
    }

    console.log('[ScriptGen] Checking frames:', {
      firstFrame,
      lastFrame,
      videoSettings,
      refFirstFrame: latestFirstFrameRef.current,
      refLastFrame: latestLastFrameRef.current
    });

    if (!firstFrame || !lastFrame) {
      addAIMessage("❌ First and last frames are required to generate script. Please go back and select both frames.");
      return;
    }

    setIsGeneratingScript(true);

    try {
      // Determine dialogue instructions based on dialogue type
      const dialogueType = videoSettings.dialogueType || 'talking_actors';
      let dialogueInstructions = '';
      let dialogueExample = '';
      let middleExample = '';
      let closingExample = '';

      if (dialogueType === 'talking_actors') {
        dialogueInstructions = `CRITICAL - DIALOGUE REQUIREMENT:
Since "Talking Actors" is selected, the actors MUST speak dialogue throughout the video.
- Write natural, conversational dialogue for the actors to speak
- Each scene section MUST include a DIALOGUE line with the actor's spoken words in quotes
- The dialogue should convey the key message and call to action naturally
- Make the dialogue engaging, authentic, and appropriate for the target audience
- The actors speak directly - this is NOT a voiceover
- Include at least 2-3 lines of spoken dialogue across the video`;
        dialogueExample = 'DIALOGUE: "Have you tried this yet? It completely changed my morning routine!"';
        middleExample = 'DIALOGUE: "[Actor speaks about the product benefit]"';
        closingExample = 'DIALOGUE: "[Call to action spoken by actor]"';
      } else if (dialogueType === 'voiceover') {
        dialogueInstructions = `VOICEOVER/NARRATION REQUIREMENT:
Since "Voiceover/Narration" is selected, include narration that plays over the visuals.
- Write engaging voiceover narration for each scene
- Each scene section MUST include a VOICEOVER line with the narrator's words in quotes
- The voiceover should tell the brand story and convey the key message
- Actors appear on screen but do NOT speak - only the narrator speaks
- The narration should flow smoothly from scene to scene`;
        dialogueExample = 'VOICEOVER: "Discover the secret that thousands have already found..."';
        middleExample = 'VOICEOVER: "[Narrator describes the benefit]"';
        closingExample = 'VOICEOVER: "[Narrator delivers the call to action]"';
      } else if (dialogueType === 'text_overlays') {
        dialogueInstructions = `TEXT OVERLAY REQUIREMENT:
Since "Text Overlays" is selected, key messages should appear as on-screen text.
- Each scene section MUST include a TEXT line with the on-screen message
- Keep text concise and punchy (5-7 words max per overlay)
- Text should reinforce the visual action
- No spoken dialogue or voiceover - visuals and text only
- Include MUSIC/SOUND cues for atmosphere`;
        dialogueExample = 'TEXT: "Transform Your Morning"';
        middleExample = 'TEXT: "[Key benefit message]"';
        closingExample = 'TEXT: "[Call to action text]"';
      } else {
        // visual_only or any other type
        dialogueInstructions = `VISUAL STORYTELLING ONLY:
Since "Visual Only" is selected, let the visuals and music tell the story.
- Do NOT include any DIALOGUE, VOICEOVER, or TEXT lines
- Focus purely on visual storytelling through action and cinematography
- Include detailed SOUND/MUSIC cues to set mood and emotion
- Let the imagery and product speak for itself`;
        dialogueExample = 'SOUND: Upbeat, inspiring music builds';
        middleExample = 'SOUND: Music continues, rhythm intensifies';
        closingExample = 'SOUND: Music crescendo, triumphant finish';
      }

      const prompt = `You are a professional TV commercial director creating a production script for a video advertisement.

CREATIVE BRIEF:
- Product: ${brief.product || 'Product'}
- Brand: ${selectedBrand?.name || 'Brand'}
- Target Audience: ${brief.targetAudience || 'General audience'}
- Emotional Tone: ${brief.desiredEmotion || 'Engaging'}
- Key Message: ${brief.selectedHeadline || 'Key message'}
- Call to Action: ${brief.cta || 'Learn more'}

VIDEO SETTINGS:
- Duration: ${videoSettings.videoLength || '8 seconds'}
- Style: ${videoSettings.videoStyle || 'Commercial'}
- Dialogue Type: ${dialogueType}
- Music Style: ${videoSettings.musicStyle || 'Upbeat'}

${dialogueInstructions}

OPENING FRAME (First Frame):
${firstFrame.description || firstFrame.title || 'Opening shot'}
${firstFrame.imagePrompt ? `Visual: ${firstFrame.imagePrompt}` : ''}

CLOSING FRAME (Last Frame):
${lastFrame.description || lastFrame.title || 'Closing shot'}
${lastFrame.imagePrompt ? `Visual: ${lastFrame.imagePrompt}` : ''}

ACTOR/CASTING:
${videoSettings.selectedCasting?.description || brief.selectedCasting?.description || videoSettings.actors || 'Professional presenter'}

LOCATION/SETTING:
${videoSettings.selectedLocation?.description || brief.selectedLocation?.description || 'Professional setting'}

Generate a detailed professional TV production script that:
1. Seamlessly transitions from the opening frame to the closing frame
2. Includes specific CAMERA DIRECTIONS (e.g., "WIDE SHOT", "CLOSE-UP", "PAN LEFT", "DOLLY IN")
3. Includes LIGHTING notes (e.g., "warm key light", "soft fill", "dramatic backlighting")
4. ${dialogueType === 'talking_actors' ? 'Includes DIALOGUE for actors to speak (REQUIRED - actors must talk!)' : dialogueType === 'voiceover' ? 'Includes VOICEOVER narration' : dialogueType === 'text_overlays' ? 'Includes TEXT overlay messages' : 'Includes detailed SOUND/MUSIC cues (no spoken words)'}
5. Includes ACTION descriptions for the talent
6. Includes TIMING markers (e.g., "0:00-0:02", "0:03-0:05")
7. Includes MUSIC/SOUND cues
8. Maintains visual continuity with the provided frames

Format the script professionally like this example:

---
[0:00-0:02] OPENING
CAMERA: Wide establishing shot, slowly pushing in
LIGHTING: Natural daylight, warm tones
ACTION: [Actor] stands in [location], product visible
${dialogueExample}
SOUND: [Music/ambient description]

[0:02-0:05] MIDDLE
CAMERA: Medium close-up
ACTION: [Actor interaction with product]
${middleExample}
SOUND: [Music description]

[0:06-0:08] CLOSING
CAMERA: Pull back to wide shot
ACTION: [Final action with product]
${closingExample}
SUPER: [On-screen text/logo]
---

Return ONLY the script, no additional commentary.`;

      console.log('[ScriptGen] Generating production script for chat...');
      const response = await conversationAgent.callGeminiAPI(prompt);
      const script = response.trim();

      console.log('[ScriptGen] Generated script for chat:', script);
      setPendingScript(script);
      setIsGeneratingScript(false);

      // Add the script as an inline editor message
      addAIMessage(
        "📝 **Production Script Ready**\n\nReview your professional TV production script below. You can edit it directly, then approve to start video generation. This script will be the only prompt sent to video generation.",
        null,
        { scriptEditor: { script, isEditable: true } }
      );

    } catch (error) {
      console.error('[ScriptGen] Error generating script for chat:', error);
      setIsGeneratingScript(false);
      addAIMessage("❌ Failed to generate script. Please try again.", [
        { text: "🔄 Regenerate Script", action: 'retry_script' }
      ]);
    }
  };

  // Handle script regeneration from chat
  const handleScriptRegenerateFromChat = async () => {
    console.log('[ConversationalCreativeV2] Regenerating script from chat...');
    addAIMessage("🔄 Regenerating your production script...");
    await generateProductionScriptInChat();
  };

  // Handle script approval from chat
  const handleScriptApprovalFromChat = async (approvedScript) => {
    console.log('[ConversationalCreativeV2] Script approved from chat, proceeding to video generation');
    setPendingScript(null);

    // Store the approved script
    const updatedVideoSettings = {
      ...(creativeBrief.videoSettings || {}),
      approvedScript: approvedScript
    };
    setCreativeBrief(prev => ({
      ...prev,
      videoSettings: updatedVideoSettings
    }));

    // Add confirmation message
    addAIMessage("✅ Script approved! Starting video generation...");

    // Trigger video generation with the approved script
    await triggerVideoGenerationWithScript(approvedScript);
  };

  // Video generation with approved script only
  const triggerVideoGenerationWithScript = async (approvedScript) => {
    console.log('[ConversationalCreativeV2] Triggering video generation with approved script');
    setIsLoading(true);

    try {
      const videoSettings = creativeBrief.videoSettings || {};
      // Use refs as fallback for frames (React state timing issues)
      const firstFrame = videoSettings.firstFrame || latestFirstFrameRef.current;
      const lastFrame = videoSettings.lastFrame || latestLastFrameRef.current;

      console.log('[VideoGen] Approved script:', approvedScript);
      console.log('[VideoGen] First frame:', firstFrame);
      console.log('[VideoGen] Last frame:', lastFrame);

      addAIMessage("🎬 Starting video generation with your approved script... This typically takes 2-5 minutes.");

      // Collect reference images (first frame, last frame, casting)
      const referenceImages = [];

      if (firstFrame?.imageUrl) {
        referenceImages.push({
          url: firstFrame.imageUrl,
          type: 'first_frame',
          description: 'Opening frame - maintain this exact scene and character appearance'
        });
      }

      if (lastFrame?.imageUrl) {
        referenceImages.push({
          url: lastFrame.imageUrl,
          type: 'last_frame',
          description: 'Closing frame - transition to this exact scene'
        });
      }

      const selectedCasting = videoSettings.selectedCasting || creativeBrief.selectedCasting || latestCastingRef.current;
      if (selectedCasting?.imageUrl && referenceImages.length < 3) {
        referenceImages.push({
          url: selectedCasting.imageUrl,
          type: 'actor',
          description: `Maintain this person's exact appearance throughout`
        });
      }

      const requestBody = {
        prompt: approvedScript,  // Only the approved script is sent as the prompt
        videoSettings: { ...videoSettings, firstFrame, lastFrame },
        referenceImages: referenceImages,
        preProductionData: {
          firstFrame: firstFrame,
          lastFrame: lastFrame,
          selectedCasting: selectedCasting,
          selectedLocation: videoSettings.selectedLocation || creativeBrief.selectedLocation
        }
      };

      console.log('[VideoGen] Request body:', JSON.stringify(requestBody, null, 2).substring(0, 2000));

      const endpoint = 'http://localhost:3001/api/generate-video';

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();
      console.log('[VideoGen] Response:', response.status, data);

      if (!response.ok) {
        let errorMsg = data.error || 'Video generation failed';
        if (data.details) {
          if (typeof data.details === 'string') {
            errorMsg = data.details;
          } else if (data.details.error_description) {
            errorMsg = data.details.error_description;
          }
        }
        throw new Error(errorMsg);
      }

      if (data.operationId) {
        const initialProgress = {
          operationId: data.operationId,
          status: 'starting',
          message: data.message || 'Starting video generation...',
          progress: null,
          estimatedTime: data.estimatedTime || '2-5 minutes',
          provider: data.provider,
          startedAt: new Date().toISOString()
        };
        setVideoProgress(initialProgress);

        const progressMsg = addAIMessage(
          `🎬 Video generation started - progress updates will appear below`,
          null,
          { videoProgress: initialProgress }
        );

        // Save pending video generation for resume on page refresh
        setPendingVideoGeneration({
          operationId: data.operationId,
          startedAt: initialProgress.startedAt,
          progressMessageId: progressMsg.id,
          provider: data.provider
        });

        setTimeout(() => {
          pollVideoStatus(data.operationId, 0, progressMsg.id, initialProgress.startedAt);
        }, 500);
      } else if (data.videoUrl) {
        console.log('[VideoGen] Video completed immediately:', data.videoUrl);
        addAIMessage(
          `✅ Your video is ready!`,
          null,
          {
            generatedVideo: {
              url: data.videoUrl,
              mimeType: data.mimeType || 'video/mp4',
              generatedAt: data.generatedAt || new Date().toISOString()
            }
          }
        );
      }

    } catch (error) {
      console.error('[VideoGen] Error:', error);
      addAIMessage(`❌ Video generation failed: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Video generation function (legacy - kept for compatibility)
  const triggerVideoGeneration = async () => {
    console.log('[ConversationalCreativeV2] Triggering video generation');
    console.log('[VideoGen] creativeBrief:', creativeBrief);
    setIsLoading(true);

    try {
      const videoSettings = creativeBrief.videoSettings || {};
      console.log('[VideoGen] videoSettings:', videoSettings);
      console.log('[VideoGen] Script content:', videoSettings.script?.scriptContent || videoSettings.scriptContent || '(none)');
      console.log('[VideoGen] First frame:', videoSettings.firstFrame);
      console.log('[VideoGen] Last frame:', videoSettings.lastFrame);

      // Build video prompt from creative brief
      const videoPrompt = buildVideoPrompt(creativeBrief, videoSettings);
      console.log('[VideoGen] Built prompt:', videoPrompt);

      // Validate prompt before sending
      if (!videoPrompt || videoPrompt.trim() === '') {
        throw new Error('Video prompt is empty. Please ensure the creative brief has sufficient information.');
      }

      // Collect reference images
      const referenceImages = [];

      // Add first frame as primary reference
      if (videoSettings.firstFrame?.imageUrl) {
        referenceImages.push({
          url: videoSettings.firstFrame.imageUrl,
          type: 'first_frame',
          description: videoSettings.firstFrame.description
        });
      }

      // Add actor/casting image for identity preservation (most important for face consistency)
      const selectedCasting = videoSettings.selectedCasting || creativeBrief.selectedCasting;
      if (selectedCasting?.imageUrl) {
        referenceImages.push({
          url: selectedCasting.imageUrl,
          type: 'actor',
          description: `Main character - ${selectedCasting.description || selectedCasting.title || 'preserve this person\'s appearance'}`
        });
        console.log('[VideoGen] Added actor reference for identity preservation');
      }

      // Add product images
      const productImages = creativeBrief.selectedAssets || creativeBrief.crawledImages || [];
      productImages.forEach((img, idx) => {
        const imgUrl = typeof img === 'string' ? img : img.url;
        if (imgUrl && referenceImages.length < 3) { // Veo allows max 3 reference images
          referenceImages.push({
            url: imgUrl,
            type: 'product',
            description: `Product image ${idx + 1}`
          });
        }
      });

      // Pre-production data for Veo
      const preProductionData = {
        firstFrame: videoSettings.firstFrame,
        lastFrame: videoSettings.lastFrame,
        selectedCasting: selectedCasting,
        selectedLocation: videoSettings.selectedLocation || creativeBrief.selectedLocation
      };

      console.log('[VideoGen] Reference images:', referenceImages.length);
      console.log('[VideoGen] Pre-production data:', preProductionData);

      addAIMessage("🎬 Starting video generation... This typically takes 2-5 minutes. I'll update you when it's ready.");

      const requestBody = {
        prompt: videoPrompt,
        videoSettings: videoSettings,
        creativeBrief: creativeBrief,
        branding: selectedBrand,
        referenceImages: referenceImages,
        preProductionData: preProductionData
      };
      console.log('[VideoGen] Request body:', JSON.stringify(requestBody, null, 2).substring(0, 1000));

      const response = await fetch('http://localhost:3001/api/generate-video', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();
      console.log('[VideoGen] Response:', response.status, data);

      if (!response.ok) {
        // Handle error - details might be an object
        let errorMsg = data.error || 'Video generation failed';
        if (data.details) {
          if (typeof data.details === 'string') {
            errorMsg = data.details;
          } else if (data.details.error_description) {
            errorMsg = data.details.error_description;
          }
        }
        throw new Error(errorMsg);
      }

      if (data.operationId) {
        // Long-running operation - start polling with progress updates
        const initialProgress = {
          operationId: data.operationId,
          status: 'starting',
          message: data.message || 'Starting video generation...',
          progress: null,
          estimatedTime: data.estimatedTime || '2-5 minutes',
          provider: data.provider,
          startedAt: new Date().toISOString()
        };
        console.log('[VideoGen] Starting video generation with progress:', initialProgress);
        setVideoProgress(initialProgress);

        const progressMsg = addAIMessage(
          `🎬 Video generation started - progress updates will appear below`,
          null,
          { videoProgress: initialProgress }
        );
        console.log('[VideoGen] Created progress message:', {
          id: progressMsg.id,
          text: progressMsg.text,
          hasData: !!progressMsg.data,
          hasVideoProgress: !!progressMsg.data?.videoProgress
        });

        // Small delay to ensure React has updated the DOM before polling
        // Pass startedAt to avoid stale closure issues
        setTimeout(() => {
          pollVideoStatus(data.operationId, 0, progressMsg.id, initialProgress.startedAt);
        }, 500);
      } else if (data.videoUrl) {
        // Video generated immediately (Fal.ai returns synchronously)
        console.log('[VideoGen] Video completed immediately:', data.videoUrl);
        addAIMessage(
          `✅ Your video is ready!`,
          null,
          {
            generatedVideo: {
              url: data.videoUrl,
              mimeType: data.mimeType || 'video/mp4',
              generatedAt: data.generatedAt || new Date().toISOString()
            }
          }
        );
      }

    } catch (error) {
      console.error('[ConversationalCreativeV2] Video generation error:', error);
      addAIMessage(`❌ Video generation failed: ${error.message}. Please try again.`);
    } finally {
      setIsLoading(false);
    }
  };

  // Build video prompt from creative brief
  // When first/last frames are provided, exclude people descriptions (they're visible in the frames)
  // Focus on actions and dialog instead
  const buildVideoPrompt = (brief, videoSettings) => {
    const parts = [];
    const hasFirstFrame = !!(videoSettings.firstFrame?.imageUrl || videoSettings.firstFrame?.base64);
    const hasLastFrame = !!(videoSettings.lastFrame?.imageUrl || videoSettings.lastFrame?.base64);
    const hasFrames = hasFirstFrame || hasLastFrame;

    // Core video description
    parts.push(`Create a ${videoSettings.videoLength || '8s'} ${videoSettings.videoStyle || 'commercial'} video advertisement.`);

    // Product and brand
    if (brief.product) {
      parts.push(`Product: ${brief.product}`);
    }
    if (selectedBrand?.name) {
      parts.push(`Brand: ${selectedBrand.name}`);
    }

    // When frames are provided, add instruction about using them
    if (hasFrames) {
      if (hasFirstFrame && hasLastFrame) {
        parts.push('\nIMPORTANT: The characters, product, and their appearance are already defined in the first and last frames. Do NOT describe the people or product - they are visible in the frames. The video MUST:');
        parts.push('- Maintain the EXACT appearance of the person shown in the frames throughout');
        parts.push('- Keep the product consistent with how it appears in the frames');
        parts.push('- Ensure visual continuity between the first and last frame');
      } else if (hasFirstFrame) {
        parts.push('\nIMPORTANT: The characters, product, and their appearance are already defined in the first frame. Do NOT describe the people or product - they are visible in the frame. The video MUST:');
        parts.push('- Maintain the EXACT appearance of the person shown in the frame throughout');
        parts.push('- Keep the product consistent with how it appears in the frame');
      }
      parts.push('\nFocus ONLY on:');
      parts.push('1. ACTIONS: What the characters do, their movements and gestures');
      parts.push('2. DIALOG: What they say (if any spoken words)');
      parts.push('3. SCENE TRANSITIONS: How the scene evolves');
      parts.push('');
    }

    // Emotion and message (keep these - they guide the tone)
    if (brief.desiredEmotion) {
      parts.push(`Emotional tone: ${brief.desiredEmotion}`);
    }
    if (brief.selectedHeadline) {
      parts.push(`Key message to convey: ${brief.selectedHeadline}`);
    }

    // Script content - emphasize actions and dialog
    // Script can be at videoSettings.script.scriptContent (v2 flow) or videoSettings.scriptContent (legacy)
    const scriptContent = videoSettings.script?.scriptContent || videoSettings.scriptContent;
    if (scriptContent) {
      parts.push('\n--- SCRIPT (Actions & Dialog) ---');
      parts.push(scriptContent);
      parts.push('--- END SCRIPT ---\n');
    }

    // Location/setting (keep this - it's about the environment, not people)
    if (videoSettings.selectedLocation?.description || brief.selectedLocation?.description) {
      const location = videoSettings.selectedLocation || brief.selectedLocation;
      parts.push(`Setting: ${location.description || location.title || 'as shown in frames'}`);
    }

    // CTA
    if (brief.cta) {
      parts.push(`End with call to action: ${brief.cta}`);
    }

    // Target audience (keep for context, but not for describing people)
    if (brief.targetAudience && !hasFrames) {
      parts.push(`Target audience: ${brief.targetAudience}`);
    }

    return parts.join('\n');
  };

  // Track if polling is active to prevent duplicate polling
  const pollingRef = useRef(false);

  // Poll for video generation status
  // startedAt is passed as parameter to avoid stale closure issues with React state
  const pollVideoStatus = async (operationId, attempts = 0, progressMessageId = null, startedAt = null) => {
    const maxAttempts = 120; // 10 minutes with 5-second intervals
    const pollStartedAt = startedAt || new Date().toISOString();

    // Prevent duplicate polling
    if (attempts === 0) {
      if (pollingRef.current) {
        console.log('[VideoGen] Polling already in progress, skipping duplicate');
        return;
      }
      pollingRef.current = true;
      console.log('[VideoGen] Starting polling for operation:', operationId, 'started at:', pollStartedAt);
    }

    if (attempts >= maxAttempts) {
      pollingRef.current = false;
      setVideoProgress(null);
      setPendingVideoGeneration(null); // Clear pending state on timeout
      setMessages(prev => prev.filter(m => m.id !== progressMessageId));
      addAIMessage("⚠️ Video generation is taking longer than expected. Please check back later or try again.");
      return;
    }

    try {
      const statusUrl = `http://localhost:3001/api/video-status/${encodeURIComponent(operationId)}`;

      const response = await fetch(statusUrl);
      const data = await response.json();

      // Check for HTTP error
      if (!response.ok) {
        console.error('[VideoGen] Status check HTTP error:', response.status, data);
        pollingRef.current = false;
        setVideoProgress(null);
        setPendingVideoGeneration(null); // Clear pending state on HTTP error
        setMessages(prev => prev.filter(m => m.id !== progressMessageId));
        addAIMessage(`❌ Video generation status check failed: ${data.details || data.error || `HTTP ${response.status}`}. Please try again.`);
        return;
      }

      if (data.status === 'completed' && data.videoUrl) {
        console.log('[VideoGen] Video completed:', data.videoUrl);
        pollingRef.current = false;
        setVideoProgress(null);
        setPendingVideoGeneration(null); // Clear pending state on completion

        // Remove progress message and add completion message
        setMessages(prev => prev.filter(m => m.id !== progressMessageId));
        addAIMessage(
          '✅ Your video is ready!',
          null,
          {
            generatedVideo: {
              url: data.videoUrl,
              mimeType: data.mimeType || 'video/mp4',
              generatedAt: data.generatedAt
            }
          }
        );
        return;
      } else if (data.status === 'failed') {
        pollingRef.current = false;
        setVideoProgress(null);
        setPendingVideoGeneration(null); // Clear pending state on failure
        setMessages(prev => prev.filter(m => m.id !== progressMessageId));
        addAIMessage(`❌ Video generation failed: ${data.error || 'Unknown error'}. Please try again.`);
        return;
      } else {
        // Still processing - update progress and poll again
        const progressData = {
          operationId,
          status: data.detailedStatus || 'generating',
          message: data.message || 'Generating video...',
          progress: data.progress,
          queuePosition: data.queuePosition,
          estimatedTime: data.estimatedTime || '2-5 minutes',
          provider: data.provider,
          startedAt: pollStartedAt,
          lastChecked: new Date().toISOString()
        };

        console.log('[VideoGen] Progress update:', JSON.stringify(progressData, null, 2));
        setVideoProgress(progressData);

        // Update the progress message in place
        if (progressMessageId) {
          console.log('[VideoGen] Looking for message ID:', progressMessageId);
          setMessages(prev => {
            const messageIndex = prev.findIndex(m => m.id === progressMessageId);
            if (messageIndex === -1) {
              console.warn('[VideoGen] Message not found! Available IDs:', prev.map(m => m.id));
              return prev;
            }
            console.log('[VideoGen] Found message at index:', messageIndex, 'updating with progress');
            const updated = [...prev];
            updated[messageIndex] = {
              ...prev[messageIndex],
              data: {
                ...prev[messageIndex].data,
                videoProgress: progressData
              }
            };
            return updated;
          });

          // Ensure the progress message is visible
          setTimeout(() => {
            const progressEl = document.querySelector('.video-progress-container');
            if (progressEl) {
              progressEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
          }, 100);
        }

        // Poll again after 5 seconds
        setTimeout(() => pollVideoStatus(operationId, attempts + 1, progressMessageId, pollStartedAt), 5000);
      }
    } catch (error) {
      console.error('[ConversationalCreativeV2] Error polling video status:', error);
      // Retry with exponential backoff, but give up after too many consecutive errors
      const consecutiveErrors = (attempts % 10) === 9 ? attempts : attempts + 1;
      if (consecutiveErrors > 30) {
        // Too many errors - give up
        pollingRef.current = false;
        setVideoProgress(null);
        setPendingVideoGeneration(null); // Clear pending state on too many errors
        setMessages(prev => prev.filter(m => m.id !== progressMessageId));
        addAIMessage(`❌ Unable to check video status. Please try generating again.`);
        return;
      }
      // Retry polling after error
      setTimeout(() => pollVideoStatus(operationId, attempts + 1, progressMessageId, pollStartedAt), 5000);
    }
  };

  // ============================================
  // BRIEF MANAGEMENT
  // ============================================

  const handleBriefApprove = () => {
    console.log('[ConversationalCreativeV2] handleBriefApprove called');
    addUserMessage("✔ Brief approved");
    // After brief approval, transition to format selection phase
    // Don't ask about generation here - that's handled by format_selection phase
    startPhase('format_selection', creativeBrief);
  };

  const handleBriefEdit = (sectionKey, updatedFields) => {
    console.log('[ConversationalCreativeV2] handleBriefEdit called:', sectionKey, updatedFields);

    setBriefSheetData(prev => {
      if (!prev) return prev;

      if (sectionKey === 'imageDescription') {
        return { ...prev, imageDescription: updatedFields.imageDescription };
      }
      if (sectionKey === 'videoDescription') {
        return { ...prev, videoDescription: updatedFields.videoDescription };
      }
      if (sectionKey === 'videoSettings') {
        return {
          ...prev,
          videoSettings: { ...prev.videoSettings, ...updatedFields }
        };
      }
      if (sectionKey === 'stillSettings') {
        return {
          ...prev,
          stillSettings: { ...prev.stillSettings, ...updatedFields }
        };
      }
      if (sectionKey === 'videoScript') {
        return {
          ...prev,
          videoSettings: { ...prev.videoSettings, scriptContent: updatedFields.scriptContent }
        };
      }
      if (sectionKey === 'assets') {
        return {
          ...prev,
          assets: { ...prev.assets, ...updatedFields }
        };
      }

      // For other sections (strategist, creativeDirector, copywriter, artDirector)
      return {
        ...prev,
        [sectionKey]: { ...prev[sectionKey], ...updatedFields }
      };
    });

    // Also update the underlying creativeBrief state for consistency
    Object.entries(updatedFields).forEach(([field, value]) => {
      updateCreativeBrief(field, value);
    });
  };

  const handleBriefAIEdit = async (sectionKey, currentText, instructions) => {
    if (!conversationAgent) return currentText;

    try {
      const result = await conversationAgent.processAIBriefEdit(sectionKey, currentText, instructions);
      return result || currentText;
    } catch (error) {
      console.error('[ConversationalCreativeV2] Error in AI brief edit:', error);
      return currentText;
    }
  };

  const updateCreativeBrief = (field, value) => {
    setCreativeBrief(prev => {
      if (field.includes('.')) {
        return conversationFlowService.setNestedValue(prev, field, value);
      }
      return { ...prev, [field]: value };
    });
  };

  const buildTemplateVariables = () => {
    // Safely get products as array
    const products = Array.isArray(selectedBrand?.products)
      ? selectedBrand.products
      : [];

    // Get colors from visual object (primaryColor, secondaryColor, accentColor)
    const visual = selectedBrand?.visual || {};
    const colors = [
      visual.primaryColor,
      visual.secondaryColor,
      visual.accentColor
    ].filter(Boolean);

    // Get crawled images count for {count} placeholder
    const crawledImagesCount = creativeBrief.crawledImages?.length || 0;

    return {
      brandName: selectedBrand?.name || '',
      product: creativeBrief.product || '',
      productList: products.join(', '),
      targetAudience: creativeBrief.targetAudience || '',
      desiredEmotion: creativeBrief.desiredEmotion || '',
      objective: creativeBrief.objective || '',
      marketingAngle: creativeBrief.marketingAngle || '',
      selectedHeadline: creativeBrief.selectedHeadline || '',
      tone: creativeBrief.tone || '',
      cta: creativeBrief.cta || '',
      visualStyle: creativeBrief.visualStyle || '',
      imageType: creativeBrief.imageType || '',
      brandColorNote: colors.length > 0
        ? `Your brand colors (${colors.join(', ')}) have been pre-loaded.`
        : '',
      count: String(crawledImagesCount)
    };
  };

  // ============================================
  // COLOR PALETTE HANDLERS
  // ============================================

  const handleAddColor = () => {
    setEditingColorIndex(null);
    setTempColor('#667eea');
    setShowColorPicker(true);
  };

  const handleEditColor = (index) => {
    setEditingColorIndex(index);
    setTempColor(colorPalette[index]);
    setShowColorPicker(true);
  };

  const handleRemoveColor = (index) => {
    setColorPalette(prev => prev.filter((_, i) => i !== index));
  };

  const handleColorChange = (value) => {
    setTempColor(value);
  };

  const handleConfirmColor = () => {
    if (!tempColor) return;

    if (editingColorIndex !== null) {
      setColorPalette(prev => prev.map((c, i) => i === editingColorIndex ? tempColor : c));
    } else {
      setColorPalette(prev => [...prev, tempColor]);
    }

    setShowColorPicker(false);
    setEditingColorIndex(null);
    setTempColor('');
  };

  const handleCancelColor = () => {
    setShowColorPicker(false);
    setEditingColorIndex(null);
    setTempColor('');
  };

  const handleConfirmColorPalette = async () => {
    // Create updated brief for immediate use (React state update is async)
    const updatedBrief = { ...creativeBrief, brandColors: colorPalette };

    // Update creative brief with selected colors
    setCreativeBrief(prev => ({ ...prev, brandColors: colorPalette }));
    if (conversationAgent) {
      conversationAgent.updateContext(selectedBrand, updatedBrief);
    }

    // Add user message showing confirmed colors
    const colorText = colorPalette.length > 0
      ? `Using ${colorPalette.length} colors: ${colorPalette.join(', ')}`
      : 'No specific colors selected';
    addUserMessage(colorText);

    // Advance to next step with updated brief
    setIsLoading(true);
    try {
      await advanceFlow(updatedBrief);
    } finally {
      setIsLoading(false);
    }
  };

  // ============================================
  // BACKGROUND CASTING GENERATION
  // ============================================

  /**
   * Generate casting images in the background
   * Called when peopleDescription is collected (step 3 in art_director)
   */
  const generateCastingImagesInBackground = async (peopleDescription, brief) => {
    if (!conversationAgent) {
      console.error('[ConversationalCreativeV2] No conversation agent available for casting generation');
      setCastingError('Conversation agent not initialized');
      return;
    }

    console.log('[ConversationalCreativeV2] Starting background casting generation for:', peopleDescription);
    setIsGeneratingCasting(true);
    setCastingError(null);
    setCastingOptions([]);

    try {
      // Step 1: Generate casting prompts using AI
      const castingPrompts = await conversationAgent.generateCastingPrompts(
        peopleDescription,
        brief,
        selectedBrand
      );

      if (!castingPrompts || castingPrompts.length === 0) {
        throw new Error('Failed to generate casting prompts');
      }

      console.log('[ConversationalCreativeV2] Got casting prompts, generating images...');

      // Step 2: Generate images for each casting option
      const optionsWithImages = [];

      for (let i = 0; i < castingPrompts.length; i++) {
        const option = castingPrompts[i];

        try {
          // Ensure the prompt explicitly requests a casting headshot format with no text/decorations
          let headShotPrompt = option.imagePrompt;

          // Add headshot framing if not already specified
          if (!headShotPrompt.toLowerCase().includes('headshot')) {
            headShotPrompt = `Professional casting headshot: ${headShotPrompt}. Head and shoulders portrait, clean neutral studio background, professional studio lighting, looking directly at camera, sharp focus on face.`;
          }

          // Always append no-text instructions to ensure clean output
          if (!headShotPrompt.toLowerCase().includes('no text')) {
            headShotPrompt += ' Absolutely no text, no labels, no captions, no watermarks, no decorations, no borders, no graphics - clean photograph only.';
          }

          const response = await fetch('http://localhost:3001/api/generate-image-gemini', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              prompt: headShotPrompt,
              referenceImages: [],
              aspectRatio: '3:4'
            }),
          });

          if (!response.ok) {
            throw new Error(`Image generation failed for option ${i + 1}`);
          }

          const data = await response.json();
          optionsWithImages.push({
            ...option,
            imageUrl: data.url,
            id: `casting-${Date.now()}-${i}`
          });

          console.log(`[ConversationalCreativeV2] Generated casting image ${i + 1}/${castingPrompts.length}`);

          // Small delay between generations to avoid rate limiting
          if (i < castingPrompts.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 1500));
          }
        } catch (imageError) {
          console.error(`[ConversationalCreativeV2] Error generating casting image ${i + 1}:`, imageError);
          // Continue with remaining images instead of failing the entire batch
          optionsWithImages.push({
            ...option,
            imageUrl: null,
            id: `casting-${Date.now()}-${i}`,
            error: true
          });
        }
      }

      // Filter out failed options
      const successfulOptions = optionsWithImages.filter(opt => !opt.error);

      if (successfulOptions.length > 0) {
        console.log('[ConversationalCreativeV2] Background casting generation complete:', successfulOptions.length, 'options');
        if (successfulOptions.length < castingPrompts.length) {
          console.warn(`[ConversationalCreativeV2] ${castingPrompts.length - successfulOptions.length} casting images failed to generate`);
        }

        // Save to library and get IDs back
        let optionsWithLibraryIds = successfulOptions;
        if (currentUser?.uid && selectedBrand?.id) {
          try {
            const libraryIds = await batchSaveItems(
              successfulOptions.map(opt => ({
                type: 'casting',
                title: opt.title,
                name: opt.name,
                description: opt.description,
                imageUrl: opt.imageUrl,
                imagePrompt: opt.imagePrompt,
                sourceContext: {
                  chatId: chatId,
                  phaseId: currentPhase
                }
              })),
              currentUser.uid,
              selectedBrand.id
            );
            // Add libraryId to each option so favorites can work
            optionsWithLibraryIds = successfulOptions.map((opt, idx) => ({
              ...opt,
              libraryId: libraryIds[idx]
            }));
            console.log('[ConversationalCreativeV2] Saved casting options to library');
          } catch (saveError) {
            console.error('[ConversationalCreativeV2] Failed to save casting to library:', saveError);
            // Don't fail the generation if library save fails
          }
        }
        setCastingOptions(optionsWithLibraryIds);
      } else {
        // Only show error if ALL images failed
        throw new Error('All casting images failed to generate');
      }

    } catch (error) {
      console.error('[ConversationalCreativeV2] Error in background casting generation:', error);
      setCastingError(error.message);
      setCastingOptions([]);
    } finally {
      setIsGeneratingCasting(false);
    }
  };

  /**
   * Handle casting option selection
   */
  const handleCastingSelect = async (option) => {
    // Determine the correct field name based on current phase
    // art_director uses 'selectedCasting', video_director uses 'videoSettings.selectedCasting'
    const castingField = currentPhase === 'video_director' ? 'videoSettings.selectedCasting' : 'selectedCasting';

    // Get the actual step for the casting field (React state may be stale)
    const castingStepIndex = conversationFlowService.findStepByField(currentPhase, castingField);
    console.log('[handleCastingSelect] Selected casting:', option.title, 'Current phase:', currentPhase, 'Field:', castingField, 'Actual step:', castingStepIndex, 'State step:', phaseStep);

    // Create updated brief for immediate use (React state update is async)
    const selectedCastingData = {
      id: option.id,
      title: option.title,
      description: option.description,
      imageUrl: option.imageUrl,
      imagePrompt: option.imagePrompt
    };

    // Store in ref immediately (avoids React state timing issues for first frame generation)
    latestCastingRef.current = selectedCastingData;

    // Update the brief with the correct field path
    let updatedBrief;
    if (currentPhase === 'video_director') {
      // For video_director, update videoSettings.selectedCasting
      updatedBrief = {
        ...creativeBrief,
        videoSettings: {
          ...creativeBrief.videoSettings,
          selectedCasting: selectedCastingData
        }
      };
      updateCreativeBrief('videoSettings.selectedCasting', selectedCastingData);
    } else {
      // For art_director, update selectedCasting at root level
      updatedBrief = { ...creativeBrief, selectedCasting: selectedCastingData };
      updateCreativeBrief('selectedCasting', selectedCastingData);
    }

    addUserMessage(option.title);

    // Clear casting options after selection to prevent re-showing on next step
    setCastingOptions([]);

    console.log('[handleCastingSelect] Calling advanceFlow with step override:', castingStepIndex);
    // Advance to next step with updated brief and step override to prevent stale state issues
    await advanceFlow(updatedBrief, castingStepIndex);
  };

  /**
   * Retry casting generation if it failed
   */
  const handleRetryCasting = async () => {
    // Check both art_director (peopleDescription) and video_director (videoSettings.actors) fields
    const peopleDescription = creativeBrief.peopleDescription || creativeBrief.videoSettings?.actors;
    if (peopleDescription) {
      await generateCastingImagesInBackground(peopleDescription, creativeBrief);
    }
  };

  // ============================================
  // VIDEO DIRECTOR: LOCATION GENERATION
  // ============================================

  /**
   * Generate location images in background for video director phase
   */
  const generateLocationImagesInBackground = async (sceneryDescription, brief) => {
    if (!conversationAgent) {
      console.error('[ConversationalCreativeV2] No conversation agent available for location generation');
      setLocationError('Conversation agent not initialized');
      return;
    }

    console.log('[ConversationalCreativeV2] Starting background location generation for:', sceneryDescription);
    setIsGeneratingLocations(true);
    setLocationError(null);
    setLocationOptions([]);

    try {
      // Step 1: Generate location prompts using AI
      const locationPrompts = await conversationAgent.generateLocationPrompts(
        sceneryDescription,
        brief,
        selectedBrand
      );

      if (!locationPrompts || locationPrompts.length === 0) {
        throw new Error('Failed to generate location prompts');
      }

      console.log('[ConversationalCreativeV2] Got location prompts, generating images...');

      // Step 2: Generate images for each location option
      const optionsWithImages = [];

      for (let i = 0; i < locationPrompts.length; i++) {
        const option = locationPrompts[i];

        try {
          const response = await fetch('http://localhost:3001/api/generate-image-gemini', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              prompt: option.imagePrompt,
              referenceImages: [],
              aspectRatio: '1:1'
            }),
          });

          if (!response.ok) {
            throw new Error(`Image generation failed for location ${i + 1}`);
          }

          const data = await response.json();
          optionsWithImages.push({
            ...option,
            imageUrl: data.url,
            id: `location-${Date.now()}-${i}`
          });

          console.log(`[ConversationalCreativeV2] Generated location image ${i + 1}/${locationPrompts.length}`);

          // Small delay between generations to avoid rate limiting
          if (i < locationPrompts.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 1500));
          }
        } catch (imageError) {
          console.error(`[ConversationalCreativeV2] Error generating location image ${i + 1}:`, imageError);
          // Continue with remaining images instead of failing the entire batch
          optionsWithImages.push({
            ...option,
            imageUrl: null,
            id: `location-${Date.now()}-${i}`,
            error: true
          });
        }
      }

      // Filter out failed options
      const successfulOptions = optionsWithImages.filter(opt => !opt.error);

      if (successfulOptions.length > 0) {
        console.log('[ConversationalCreativeV2] Background location generation complete:', successfulOptions.length, 'options');
        if (successfulOptions.length < locationPrompts.length) {
          console.warn(`[ConversationalCreativeV2] ${locationPrompts.length - successfulOptions.length} location images failed to generate`);
        }

        // Save to library and get IDs back
        let optionsWithLibraryIds = successfulOptions;
        if (currentUser?.uid && selectedBrand?.id) {
          try {
            const libraryIds = await batchSaveItems(
              successfulOptions.map(opt => ({
                type: 'location',
                title: opt.title,
                name: opt.name,
                description: opt.description,
                imageUrl: opt.imageUrl,
                imagePrompt: opt.imagePrompt,
                sourceContext: {
                  chatId: chatId,
                  phaseId: currentPhase
                }
              })),
              currentUser.uid,
              selectedBrand.id
            );
            // Add libraryId to each option so favorites can work
            optionsWithLibraryIds = successfulOptions.map((opt, idx) => ({
              ...opt,
              libraryId: libraryIds[idx]
            }));
            console.log('[ConversationalCreativeV2] Saved location options to library');
          } catch (saveError) {
            console.error('[ConversationalCreativeV2] Failed to save locations to library:', saveError);
            // Don't fail the generation if library save fails
          }
        }
        setLocationOptions(optionsWithLibraryIds);
      } else {
        // Only show error if ALL images failed
        throw new Error('All location images failed to generate');
      }

    } catch (error) {
      console.error('[ConversationalCreativeV2] Error in background location generation:', error);
      setLocationError(error.message);
      setLocationOptions([]);
    } finally {
      setIsGeneratingLocations(false);
    }
  };

  /**
   * Handle location option selection
   */
  const handleLocationSelect = async (option) => {
    // Determine the correct field name based on current phase
    // art_director uses 'selectedLocation', video_director uses 'videoSettings.selectedLocation'
    const locationField = currentPhase === 'video_director' ? 'videoSettings.selectedLocation' : 'selectedLocation';

    // Get the actual step for the location field (React state may be stale)
    const locationStepIndex = conversationFlowService.findStepByField(currentPhase, locationField);
    console.log('[handleLocationSelect] Selected location:', option.title, 'Current phase:', currentPhase, 'Field:', locationField, 'Actual step:', locationStepIndex, 'State step:', phaseStep);

    const selectedLocationData = {
      id: option.id,
      name: option.name,
      title: option.title,
      description: option.description,
      imageUrl: option.imageUrl,
      imagePrompt: option.imagePrompt
    };

    // Store in ref immediately (avoids React state timing issues for first frame generation)
    latestLocationRef.current = selectedLocationData;

    // Store in videoSettings for video director flow
    const updatedVideoSettings = {
      ...(creativeBrief.videoSettings || {}),
      selectedLocation: selectedLocationData
    };
    const updatedBrief = {
      ...creativeBrief,
      selectedLocation: selectedLocationData,
      videoSettings: updatedVideoSettings
    };

    // Debug: Log product assets at frame generation time
    console.log('[handleLocationSelect] creativeBrief.selectedAssets:', creativeBrief.selectedAssets?.length || 0, creativeBrief.selectedAssets);
    console.log('[handleLocationSelect] creativeBrief.crawledImages:', creativeBrief.crawledImages?.length || 0, creativeBrief.crawledImages);
    console.log('[handleLocationSelect] updatedBrief.selectedAssets:', updatedBrief.selectedAssets?.length || 0, updatedBrief.selectedAssets);
    console.log('[handleLocationSelect] updatedBrief.crawledImages:', updatedBrief.crawledImages?.length || 0, updatedBrief.crawledImages);

    updateCreativeBrief('selectedLocation', selectedLocationData);
    updateCreativeBrief('videoSettings.selectedLocation', selectedLocationData);

    addUserMessage(option.title);

    // Clear location options after selection to prevent re-showing on next step
    setLocationOptions([]);

    // Only trigger first frame generation for video director phase
    if (currentPhase === 'video_director') {
      // Set the generating state BEFORE calling the async function
      // This ensures we don't race with advanceFlow checking this state
      setIsGeneratingFirstFrame(true);

      // Add loading message for first frame generation
      // This ensures there's a message to update when generation completes
      addAIMessage(
        "🎥 I'm composing opening shot options... This takes a moment as I'm creating the first frames.",
        [],
        { showFirstFrameLoading: true }
      );

      // Start generation in background (non-blocking)
      generateFirstFrameImagesInBackground(updatedBrief);

      // Don't call advanceFlow here - the flow will continue when the user selects a first frame
      // via handleFirstFrameSelect which calls advanceFlow after selection
      console.log('[handleLocationSelect] First frame generation started, waiting for user selection');
      return;
    }

    console.log('[handleLocationSelect] Calling advanceFlow with step override:', locationStepIndex);
    await advanceFlow(updatedBrief, locationStepIndex);
  };

  /**
   * Retry location generation if it failed
   */
  const handleRetryLocation = async () => {
    const sceneryDescription = creativeBrief.videoSettings?.scenery || creativeBrief.sceneType;
    if (sceneryDescription) {
      await generateLocationImagesInBackground(sceneryDescription, creativeBrief);
    }
  };

  // ============================================
  // LIBRARY FAVORITES INTEGRATION
  // ============================================

  /**
   * Load favorites or generate new casting options
   * Shows favorites first if they exist, otherwise generates new ones
   */
  const loadOrGenerateCasting = async (peopleDescription, brief) => {
    if (!currentUser?.uid || !selectedBrand?.id) {
      // No user/brand, just generate new
      await generateCastingImagesInBackground(peopleDescription, brief);
      return;
    }

    try {
      // Check for favorites first
      const favorites = await getFavorites(currentUser.uid, selectedBrand.id, 'casting');

      if (favorites.length > 0) {
        // Show favorites
        setCastingOptions(favorites.map(fav => ({
          ...fav,
          id: fav.id,
          libraryId: fav.id // Track that this came from library
        })));
        setShowingCastingFavorites(true);
        console.log('[ConversationalCreativeV2] Loaded', favorites.length, 'casting favorites from library');
      } else {
        // No favorites, generate new
        setShowingCastingFavorites(false);
        await generateCastingImagesInBackground(peopleDescription, brief);
      }
    } catch (error) {
      console.error('[ConversationalCreativeV2] Error loading casting favorites:', error);
      // Fall back to generation
      setShowingCastingFavorites(false);
      await generateCastingImagesInBackground(peopleDescription, brief);
    }
  };

  /**
   * Load favorites or generate new location options
   */
  const loadOrGenerateLocations = async (sceneryDescription, brief) => {
    if (!currentUser?.uid || !selectedBrand?.id) {
      await generateLocationImagesInBackground(sceneryDescription, brief);
      return;
    }

    try {
      const favorites = await getFavorites(currentUser.uid, selectedBrand.id, 'location');

      if (favorites.length > 0) {
        setLocationOptions(favorites.map(fav => ({
          ...fav,
          id: fav.id,
          libraryId: fav.id
        })));
        setShowingLocationFavorites(true);
        console.log('[ConversationalCreativeV2] Loaded', favorites.length, 'location favorites from library');
      } else {
        setShowingLocationFavorites(false);
        await generateLocationImagesInBackground(sceneryDescription, brief);
      }
    } catch (error) {
      console.error('[ConversationalCreativeV2] Error loading location favorites:', error);
      setShowingLocationFavorites(false);
      await generateLocationImagesInBackground(sceneryDescription, brief);
    }
  };

  /**
   * Generate more casting options and add them to the existing list
   */
  const handleGenerateMoreCasting = async () => {
    const peopleDescription = creativeBrief.peopleDescription || creativeBrief.videoSettings?.actors;
    if (!peopleDescription || !conversationAgent) return;

    setIsGeneratingMoreCasting(true);
    setCastingError(null);

    try {
      const castingPrompts = await conversationAgent.generateCastingPrompts(
        peopleDescription,
        creativeBrief,
        selectedBrand
      );

      if (!castingPrompts || castingPrompts.length === 0) {
        throw new Error('Failed to generate more casting prompts');
      }

      const newOptionsWithImages = [];

      for (let i = 0; i < castingPrompts.length; i++) {
        const option = castingPrompts[i];

        try {
          let headShotPrompt = option.imagePrompt;
          if (!headShotPrompt.toLowerCase().includes('headshot')) {
            headShotPrompt = `Professional casting headshot: ${headShotPrompt}. Head and shoulders portrait, clean neutral studio background, professional studio lighting, looking directly at camera, sharp focus on face.`;
          }
          if (!headShotPrompt.toLowerCase().includes('no text')) {
            headShotPrompt += ' Absolutely no text, no labels, no captions, no watermarks, no decorations, no borders, no graphics - clean photograph only.';
          }

          const response = await fetch('http://localhost:3001/api/generate-image-gemini', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              prompt: headShotPrompt,
              referenceImages: [],
              aspectRatio: '3:4'
            }),
          });

          if (!response.ok) throw new Error(`Image generation failed for option ${i + 1}`);

          const data = await response.json();
          newOptionsWithImages.push({
            ...option,
            imageUrl: data.url,
            id: `casting-${Date.now()}-${i}`
          });

          if (i < castingPrompts.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 1500));
          }
        } catch (imageError) {
          console.error(`Error generating casting image ${i + 1}:`, imageError);
        }
      }

      const successfulNewOptions = newOptionsWithImages.filter(opt => opt.imageUrl);

      if (successfulNewOptions.length > 0) {
        setShowingCastingFavorites(false); // Mixed content now

        // Save new options to library and get IDs back
        let optionsWithLibraryIds = successfulNewOptions;
        if (currentUser?.uid && selectedBrand?.id) {
          try {
            const libraryIds = await batchSaveItems(
              successfulNewOptions.map(opt => ({
                type: 'casting',
                title: opt.title,
                name: opt.name,
                description: opt.description,
                imageUrl: opt.imageUrl,
                imagePrompt: opt.imagePrompt,
                sourceContext: { chatId, phaseId: currentPhase }
              })),
              currentUser.uid,
              selectedBrand.id
            );
            // Add libraryId to each option so favorites can work
            optionsWithLibraryIds = successfulNewOptions.map((opt, idx) => ({
              ...opt,
              libraryId: libraryIds[idx]
            }));
          } catch (saveError) {
            console.error('Failed to save new casting to library:', saveError);
          }
        }
        // Append to existing options
        setCastingOptions(prev => {
          const newOptions = [...prev, ...optionsWithLibraryIds];
          // Also update the message that contains casting options so the grid refreshes
          setMessages(msgs => msgs.map(msg => {
            if (msg.data?.castingOptions) {
              return {
                ...msg,
                data: { ...msg.data, castingOptions: newOptions }
              };
            }
            return msg;
          }));
          return newOptions;
        });
      }
    } catch (error) {
      console.error('Error generating more casting:', error);
      setCastingError(error.message);
    } finally {
      setIsGeneratingMoreCasting(false);
    }
  };

  /**
   * Generate more location options and add them to the existing list
   */
  const handleGenerateMoreLocations = async () => {
    const sceneryDescription = creativeBrief.videoSettings?.scenery || creativeBrief.sceneType;
    if (!sceneryDescription || !conversationAgent) return;

    setIsGeneratingMoreLocations(true);
    setLocationError(null);

    try {
      const locationPrompts = await conversationAgent.generateLocationPrompts(
        sceneryDescription,
        creativeBrief,
        selectedBrand
      );

      if (!locationPrompts || locationPrompts.length === 0) {
        throw new Error('Failed to generate more location prompts');
      }

      const newOptionsWithImages = [];

      for (let i = 0; i < locationPrompts.length; i++) {
        const option = locationPrompts[i];

        try {
          const response = await fetch('http://localhost:3001/api/generate-image-gemini', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              prompt: option.imagePrompt,
              referenceImages: [],
              aspectRatio: '1:1'
            }),
          });

          if (!response.ok) throw new Error(`Image generation failed for location ${i + 1}`);

          const data = await response.json();
          newOptionsWithImages.push({
            ...option,
            imageUrl: data.url,
            id: `location-${Date.now()}-${i}`
          });

          if (i < locationPrompts.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 1500));
          }
        } catch (imageError) {
          console.error(`Error generating location image ${i + 1}:`, imageError);
        }
      }

      const successfulNewOptions = newOptionsWithImages.filter(opt => opt.imageUrl);

      if (successfulNewOptions.length > 0) {
        setShowingLocationFavorites(false);

        // Save new options to library and get IDs back
        let optionsWithLibraryIds = successfulNewOptions;
        if (currentUser?.uid && selectedBrand?.id) {
          try {
            const libraryIds = await batchSaveItems(
              successfulNewOptions.map(opt => ({
                type: 'location',
                title: opt.title,
                name: opt.name,
                description: opt.description,
                imageUrl: opt.imageUrl,
                imagePrompt: opt.imagePrompt,
                sourceContext: { chatId, phaseId: currentPhase }
              })),
              currentUser.uid,
              selectedBrand.id
            );
            // Add libraryId to each option so favorites can work
            optionsWithLibraryIds = successfulNewOptions.map((opt, idx) => ({
              ...opt,
              libraryId: libraryIds[idx]
            }));
          } catch (saveError) {
            console.error('Failed to save new locations to library:', saveError);
          }
        }
        // Append to existing options
        setLocationOptions(prev => {
          const newOptions = [...prev, ...optionsWithLibraryIds];
          // Also update the message that contains location options so the grid refreshes
          setMessages(msgs => msgs.map(msg => {
            if (msg.data?.locationOptions) {
              return {
                ...msg,
                data: { ...msg.data, locationOptions: newOptions }
              };
            }
            return msg;
          }));
          return newOptions;
        });
      }
    } catch (error) {
      console.error('Error generating more locations:', error);
      setLocationError(error.message);
    } finally {
      setIsGeneratingMoreLocations(false);
    }
  };

  /**
   * Toggle favorite status for an item in the current options
   * If item doesn't have a libraryId, save it to library first
   */
  const handleToggleCastingFavorite = async (option) => {
    if (!currentUser?.uid || !selectedBrand?.id) return;

    try {
      let libraryId = option.libraryId;

      // If no libraryId, save to library first
      if (!libraryId) {
        const { saveLibraryItem } = await import('../services/libraryService');
        libraryId = await saveLibraryItem({
          type: 'casting',
          title: option.title,
          name: option.name,
          description: option.description,
          imageUrl: option.imageUrl,
          imagePrompt: option.imagePrompt,
          isFavorite: true, // Set as favorite since that's what user wants
          sourceContext: { chatId, phaseId: currentPhase }
        }, currentUser.uid, selectedBrand.id);

        // Update local state with libraryId and favorite status
        setCastingOptions(prev =>
          prev.map(opt => opt.id === option.id ? { ...opt, libraryId, isFavorite: true } : opt)
        );
        if (zoomedCastingOption?.id === option.id) {
          setZoomedCastingOption({ ...zoomedCastingOption, libraryId, isFavorite: true });
        }
        return;
      }

      // Toggle favorite on existing library item
      const newStatus = await toggleFavorite(libraryId, currentUser.uid);
      setCastingOptions(prev =>
        prev.map(opt => opt.id === option.id ? { ...opt, isFavorite: newStatus } : opt)
      );
      if (zoomedCastingOption?.id === option.id) {
        setZoomedCastingOption({ ...zoomedCastingOption, isFavorite: newStatus });
      }
    } catch (error) {
      console.error('Failed to toggle casting favorite:', error);
    }
  };

  const handleToggleLocationFavorite = async (option) => {
    if (!currentUser?.uid || !selectedBrand?.id) return;

    try {
      let libraryId = option.libraryId;

      // If no libraryId, save to library first
      if (!libraryId) {
        const { saveLibraryItem } = await import('../services/libraryService');
        libraryId = await saveLibraryItem({
          type: 'location',
          title: option.title,
          name: option.name,
          description: option.description,
          imageUrl: option.imageUrl,
          imagePrompt: option.imagePrompt,
          isFavorite: true,
          sourceContext: { chatId, phaseId: currentPhase }
        }, currentUser.uid, selectedBrand.id);

        setLocationOptions(prev =>
          prev.map(opt => opt.id === option.id ? { ...opt, libraryId, isFavorite: true } : opt)
        );
        if (zoomedLocationOption?.id === option.id) {
          setZoomedLocationOption({ ...zoomedLocationOption, libraryId, isFavorite: true });
        }
        return;
      }

      const newStatus = await toggleFavorite(libraryId, currentUser.uid);
      setLocationOptions(prev =>
        prev.map(opt => opt.id === option.id ? { ...opt, isFavorite: newStatus } : opt)
      );
      if (zoomedLocationOption?.id === option.id) {
        setZoomedLocationOption({ ...zoomedLocationOption, isFavorite: newStatus });
      }
    } catch (error) {
      console.error('Failed to toggle location favorite:', error);
    }
  };

  // ============================================
  // VIDEO DIRECTOR: FIRST FRAME GENERATION
  // ============================================

  /**
   * Generate first frame images in background for video director phase
   */
  const generateFirstFrameImagesInBackground = async (brief) => {
    if (!conversationAgent) {
      console.error('[ConversationalCreativeV2] No conversation agent available for first frame generation');
      setFirstFrameError('Conversation agent not initialized');
      return;
    }

    console.log('[ConversationalCreativeV2] Starting background first frame generation');
    setIsGeneratingFirstFrame(true);
    setFirstFrameError(null);
    setFirstFrameOptions([]);

    try {
      // Step 1: Generate first frame prompts using AI
      // Build brief with latest ref values to ensure casting/location are included in AI prompt
      const videoSettingsForPrompt = brief.videoSettings || {};
      const briefWithLatestSelections = {
        ...brief,
        selectedCasting: latestCastingRef.current || brief.selectedCasting,
        selectedLocation: latestLocationRef.current || brief.selectedLocation,
        videoSettings: {
          ...videoSettingsForPrompt,
          selectedCasting: latestCastingRef.current || videoSettingsForPrompt.selectedCasting,
          selectedLocation: latestLocationRef.current || videoSettingsForPrompt.selectedLocation
        }
      };
      const framePrompts = await conversationAgent.generateFirstFramePrompts(briefWithLatestSelections, selectedBrand);

      if (!framePrompts || framePrompts.length === 0) {
        throw new Error('Failed to generate first frame prompts');
      }

      console.log('[ConversationalCreativeV2] Got first frame prompts, generating images...');

      // Step 2: Build assets array with all visual elements to compose the scene
      const videoSettings = brief.videoSettings || {};
      // Use refs first (most reliable due to React state timing), then fall back to brief values
      const selectedCasting = latestCastingRef.current || brief.selectedCasting || videoSettings.selectedCasting;
      const selectedLocation = latestLocationRef.current || brief.selectedLocation || videoSettings.selectedLocation;

      // DEBUG: Log what we found
      console.log('[FirstFrame DEBUG] latestCastingRef.current:', latestCastingRef.current);
      console.log('[FirstFrame DEBUG] brief.selectedCasting:', brief.selectedCasting);
      console.log('[FirstFrame DEBUG] videoSettings.selectedCasting:', videoSettings.selectedCasting);
      console.log('[FirstFrame DEBUG] Resolved selectedCasting:', selectedCasting);
      console.log('[FirstFrame DEBUG] latestLocationRef.current:', latestLocationRef.current);
      console.log('[FirstFrame DEBUG] brief.selectedLocation:', brief.selectedLocation);
      console.log('[FirstFrame DEBUG] videoSettings.selectedLocation:', videoSettings.selectedLocation);
      console.log('[FirstFrame DEBUG] Resolved selectedLocation:', selectedLocation);

      // Build reference images with instructions for the API
      const referenceImages = [];

      // Add selected casting/actor image (most important - must be first)
      if (selectedCasting?.imageUrl) {
        console.log('[FirstFrame DEBUG] Adding casting image:', selectedCasting.imageUrl.substring(0, 100));
        referenceImages.push({
          url: selectedCasting.imageUrl,
          instruction: `REFERENCE IMAGE (SELECTED ACTOR):
CRITICAL: This is the selected actor/presenter. You MUST use this EXACT person's appearance, face, body type, hair, and characteristics in the generated scene. Do NOT substitute with a different person. Maintain their exact likeness while adapting pose and expression to fit the scene naturally.

Actor description: ${selectedCasting.description || selectedCasting.title || 'The selected person'}`
        });
      } else {
        console.warn('[FirstFrame DEBUG] No casting imageUrl found! selectedCasting:', JSON.stringify(selectedCasting, null, 2));
      }

      // Add selected location image
      if (selectedLocation?.imageUrl) {
        console.log('[FirstFrame DEBUG] Adding location image:', selectedLocation.imageUrl.substring(0, 100));
        referenceImages.push({
          url: selectedLocation.imageUrl,
          instruction: `REFERENCE IMAGE (SELECTED LOCATION):
Use this as the background/setting for the scene. Match the lighting, atmosphere, and environment shown.

Location description: ${selectedLocation.description || selectedLocation.title || 'The selected location'}`
        });
      } else {
        console.warn('[FirstFrame DEBUG] No location imageUrl found! selectedLocation:', JSON.stringify(selectedLocation, null, 2));
      }

      // Add product images
      const productImages = brief.selectedAssets || brief.crawledImages || [];
      console.log('[FirstFrame DEBUG] brief keys:', Object.keys(brief));
      console.log('[FirstFrame DEBUG] brief.selectedAssets:', brief.selectedAssets?.length || 0, brief.selectedAssets);
      console.log('[FirstFrame DEBUG] brief.crawledImages:', brief.crawledImages?.length || 0, brief.crawledImages);
      console.log('[FirstFrame DEBUG] Product images found:', productImages.length);
      productImages.forEach((img, idx) => {
        const imgUrl = typeof img === 'string' ? img : img.url;
        if (imgUrl) {
          referenceImages.push({
            url: imgUrl,
            instruction: `REFERENCE IMAGE (PRODUCT ${idx + 1}):
Seamlessly integrate this product into the scene with realistic lighting, perspective, and natural placement.

Product: ${brief.product || 'the product'}`
          });
        }
      });

      console.log('[ConversationalCreativeV2] First frame reference images:', referenceImages.length, 'images');
      console.log('[FirstFrame DEBUG] Reference images:', referenceImages.map(r => ({ url: r.url?.substring(0, 50) })));

      // Get user's color choices from brief or colorPalette state
      const userColors = brief.brandColors || colorPalette || [];
      console.log('[ConversationalCreativeV2] Using brand colors for first frame:', userColors);

      // Step 3: Generate images for each frame option
      const optionsWithImages = [];

      // Prepare color string for prompts
      const colorString = userColors.length > 0 ? userColors.join(', ') : '';

      for (let i = 0; i < framePrompts.length; i++) {
        const option = framePrompts[i];
        let retries = 2; // Allow up to 2 retries for transient failures
        let lastError = null;

        // Build complete video frame prompt
        const videoFramePrompt = `CINEMATIC VIDEO FRAME - FIRST FRAME:

${option.imagePrompt}

CRITICAL VIDEO FRAME REQUIREMENTS:
- This is a VIDEO FRAME for a video advertisement, not a static ad
- Generate a clean, cinematic frame suitable for video
- NO text overlays, NO headlines, NO CTA buttons, NO logos in the image
- Professional video/cinema quality with natural, realistic lighting
- CRITICAL: Maintain the exact likeness of any people shown in reference images
- Compose for 9:16 vertical video format
${colorString ? `- Incorporate brand colors naturally: ${colorString}` : ''}

OUTPUT: A single, clean video frame ready for use in a video advertisement.`;

        while (retries >= 0) {
          try {
            const response = await fetch('http://localhost:3001/api/generate-image-gemini', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                prompt: videoFramePrompt,
                referenceImages,
                aspectRatio: '9:16'
              }),
            });

            if (!response.ok) {
              const errorData = await response.json().catch(() => ({}));
              const errorMsg = errorData.details || errorData.error || `HTTP ${response.status}`;
              throw new Error(`First frame ${i + 1} failed: ${errorMsg}`);
            }

            const data = await response.json();
            optionsWithImages.push({
              ...option,
              imageUrl: data.url,
              id: `first-frame-${Date.now()}-${i}`
            });

            console.log(`[ConversationalCreativeV2] Generated first frame image ${i + 1}/${framePrompts.length}`);
            break; // Success - exit retry loop
          } catch (imageError) {
            lastError = imageError;
            retries--;
            if (retries >= 0) {
              console.warn(`[ConversationalCreativeV2] Retrying first frame ${i + 1} (${retries + 1} attempts left):`, imageError.message);
              await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2s before retry
            }
          }
        }

        // If all retries failed, add with error flag
        if (retries < 0) {
          console.error(`[ConversationalCreativeV2] All retries failed for first frame ${i + 1}:`, lastError);
          optionsWithImages.push({
            ...option,
            imageUrl: null,
            id: `first-frame-${Date.now()}-${i}`,
            error: true
          });
        }

        // Delay between images (unless this was the last one)
        if (i < framePrompts.length - 1 && retries >= 0) {
          await new Promise(resolve => setTimeout(resolve, 1500));
        }
      }

      // Filter out failed options
      const successfulOptions = optionsWithImages.filter(opt => !opt.error);

      if (successfulOptions.length > 0) {
        setFirstFrameOptions(successfulOptions);
        console.log('[ConversationalCreativeV2] Background first frame generation complete:', successfulOptions.length, 'options');
        if (successfulOptions.length < framePrompts.length) {
          console.warn(`[ConversationalCreativeV2] ${framePrompts.length - successfulOptions.length} first frame images failed to generate`);
        }
      } else {
        // Only show error if ALL images failed
        throw new Error('All first frame images failed to generate');
      }

    } catch (error) {
      console.error('[ConversationalCreativeV2] Error in background first frame generation:', error);
      setFirstFrameError(error.message);
      setFirstFrameOptions([]);
    } finally {
      setIsGeneratingFirstFrame(false);
    }
  };

  /**
   * Handle first frame option selection
   */
  const handleFirstFrameSelect = async (option) => {
    // Get the actual step for firstFrame field (React state may be stale)
    const firstFrameStepIndex = conversationFlowService.findStepByField(currentPhase, 'videoSettings.firstFrame');
    console.log('[handleFirstFrameSelect] Selected first frame:', option.title, 'Actual step:', firstFrameStepIndex, 'State step:', phaseStep);

    const selectedFirstFrameData = {
      id: option.id,
      name: option.name,
      title: option.title,
      description: option.description,
      imageUrl: option.imageUrl,
      imagePrompt: option.imagePrompt
    };

    // Store in ref immediately for reliable access (React state updates are async)
    latestFirstFrameRef.current = selectedFirstFrameData;

    const updatedVideoSettings = {
      ...(creativeBrief.videoSettings || {}),
      firstFrame: selectedFirstFrameData
    };
    const updatedBrief = {
      ...creativeBrief,
      videoSettings: updatedVideoSettings
    };

    // Debug: Log product assets at last frame generation time
    console.log('[handleFirstFrameSelect] creativeBrief.selectedAssets:', creativeBrief.selectedAssets?.length || 0);
    console.log('[handleFirstFrameSelect] creativeBrief.crawledImages:', creativeBrief.crawledImages?.length || 0);
    console.log('[handleFirstFrameSelect] updatedBrief.selectedAssets:', updatedBrief.selectedAssets?.length || 0);
    console.log('[handleFirstFrameSelect] updatedBrief.crawledImages:', updatedBrief.crawledImages?.length || 0);

    updateCreativeBrief('videoSettings.firstFrame', selectedFirstFrameData);

    addUserMessage(option.title);

    // Clear first frame options after selection to prevent re-showing
    setFirstFrameOptions([]);

    // Set the generating state BEFORE calling the async function
    setIsGeneratingLastFrame(true);

    // Add loading message for last frame generation
    // This ensures there's a message to update when generation completes
    addAIMessage(
      "🎬 I'm designing closing frame options... This takes a moment as I'm creating the final shots.",
      [],
      { showLastFrameLoading: true }
    );

    // Trigger last frame generation in background after first frame is selected
    generateLastFrameImagesInBackground(updatedBrief);

    // Don't call advanceFlow here - the flow will continue when the user selects a last frame
    // via handleLastFrameSelect which calls advanceFlow after selection
    console.log('[handleFirstFrameSelect] Last frame generation started, waiting for user selection');
  };

  /**
   * Retry first frame generation if it failed
   */
  const handleRetryFirstFrame = async () => {
    await generateFirstFrameImagesInBackground(creativeBrief);
  };

  // ============================================
  // VIDEO DIRECTOR: LAST FRAME GENERATION
  // ============================================

  /**
   * Generate last frame images in background for video director phase
   */
  const generateLastFrameImagesInBackground = async (brief) => {
    if (!conversationAgent) {
      console.error('[ConversationalCreativeV2] No conversation agent available for last frame generation');
      setLastFrameError('Conversation agent not initialized');
      return;
    }

    console.log('[ConversationalCreativeV2] Starting background last frame generation');
    setIsGeneratingLastFrame(true);
    setLastFrameError(null);
    setLastFrameOptions([]);

    try {
      // Step 1: Generate last frame prompts using AI
      const framePrompts = await conversationAgent.generateLastFramePrompts(brief, selectedBrand);

      if (!framePrompts || framePrompts.length === 0) {
        throw new Error('Failed to generate last frame prompts');
      }

      console.log('[ConversationalCreativeV2] Got last frame prompts, generating images...');

      // Step 2: Build assets array with all visual elements for scene continuity
      const videoSettings = brief.videoSettings || {};
      const selectedCasting = brief.selectedCasting || videoSettings.selectedCasting;
      const selectedLocation = brief.selectedLocation || videoSettings.selectedLocation;
      const firstFrame = videoSettings.firstFrame;

      // Build reference images with instructions for the API
      const referenceImages = [];

      // Add first frame for visual continuity (most important for last frame)
      if (firstFrame?.imageUrl) {
        referenceImages.push({
          url: firstFrame.imageUrl,
          instruction: `REFERENCE IMAGE (OPENING FRAME):
This is the opening frame of the video. CRITICAL: Maintain visual continuity - same style, lighting, mood, and most importantly the SAME person's appearance.`
        });
      }

      // Add selected casting/actor image
      if (selectedCasting?.imageUrl) {
        referenceImages.push({
          url: selectedCasting.imageUrl,
          instruction: `REFERENCE IMAGE (SELECTED ACTOR):
CRITICAL: This is the selected actor/presenter. You MUST maintain this EXACT person's appearance from the opening shot. Their face, body type, hair, and characteristics must be consistent with the opening frame.

Actor description: ${selectedCasting.description || selectedCasting.title || 'The selected person'}`
        });
      }

      // Add selected location image
      if (selectedLocation?.imageUrl) {
        referenceImages.push({
          url: selectedLocation.imageUrl,
          instruction: `REFERENCE IMAGE (SELECTED LOCATION):
Maintain the same setting/environment continuity from the opening shot.

Location description: ${selectedLocation.description || selectedLocation.title || 'The selected location'}`
        });
      }

      // Add product images
      const productImages = brief.selectedAssets || brief.crawledImages || [];
      console.log('[LastFrame DEBUG] brief keys:', Object.keys(brief));
      console.log('[LastFrame DEBUG] brief.selectedAssets:', brief.selectedAssets?.length || 0, brief.selectedAssets);
      console.log('[LastFrame DEBUG] brief.crawledImages:', brief.crawledImages?.length || 0, brief.crawledImages);
      console.log('[LastFrame DEBUG] Product images found:', productImages.length);
      productImages.forEach((img, idx) => {
        const imgUrl = typeof img === 'string' ? img : img.url;
        if (imgUrl) {
          referenceImages.push({
            url: imgUrl,
            instruction: `REFERENCE IMAGE (PRODUCT ${idx + 1}):
Feature this product prominently in the closing frame with realistic lighting and natural placement.

Product: ${brief.product || 'the product'}`
          });
        }
      });

      // Add brand logo as reference image
      const brandLogoUrl = selectedBrand?.logoUrl || selectedBrand?.logo;
      if (brandLogoUrl) {
        referenceImages.push({
          url: brandLogoUrl,
          instruction: `REFERENCE IMAGE (BRAND LOGO):
This is the brand logo. Include this logo prominently in the closing frame - typically in the center or bottom portion of the frame. The logo should be clearly visible and professionally integrated into the composition.`
        });
      }

      console.log('[ConversationalCreativeV2] Last frame reference images:', referenceImages.length, 'images');

      // Get user's color choices from brief or colorPalette state
      const userColors = brief.brandColors || colorPalette || [];
      console.log('[ConversationalCreativeV2] Using brand colors for last frame:', userColors);

      // Step 3: Generate images for each frame option
      const optionsWithImages = [];

      // Prepare color string for prompts
      const colorString = userColors.length > 0 ? userColors.join(', ') : '';

      // Get headline and key message for text overlays
      const headline = brief.selectedHeadline || '';
      const keyMessages = videoSettings.script?.keyMessages;
      const keyMessage = Array.isArray(keyMessages) ? keyMessages[0] : (keyMessages || '');
      const brandName = selectedBrand?.name || '';

      for (let i = 0; i < framePrompts.length; i++) {
        const option = framePrompts[i];
        let retries = 2; // Allow up to 2 retries for transient failures
        let lastError = null;

        // Build complete video frame prompt for last frame with branding elements
        const videoFramePrompt = `CINEMATIC VIDEO FRAME - LAST FRAME (CLOSING SHOT) WITH BRANDING:

${option.imagePrompt}

CRITICAL VIDEO FRAME REQUIREMENTS:
- This is the CLOSING FRAME for a video advertisement
- Generate a professional, cinematic frame suitable for video
- Professional video/cinema quality with natural, realistic lighting
- CRITICAL: Maintain the EXACT same person from the opening frame - visual continuity is paramount
- The person's face, body, and appearance must be consistent with the first frame
- Compose for 9:16 vertical video format

TEXT AND BRANDING OVERLAY REQUIREMENTS:
${headline ? `- HEADLINE TEXT: Include the text "${headline}" prominently displayed in the frame. Use ${colorString || 'professional, contrasting'} colors for the text. Position in the upper third or center of the frame for maximum impact.` : ''}
${keyMessage ? `- KEY MESSAGE: Include "${keyMessage}" as a supporting message below or near the headline. Use ${colorString || 'brand-appropriate'} colors that complement the headline.` : ''}
${brandLogoUrl ? `- LOGO: Include the brand logo (provided as reference) prominently - typically centered or in the bottom portion of the frame.` : brandName ? `- BRAND NAME: Include "${brandName}" text styled professionally in ${colorString || 'brand'} colors.` : ''}
${colorString ? `- BRAND COLORS: All text elements MUST use these brand colors: ${colorString}. Ensure text is legible with appropriate contrast against the background.` : ''}

TEXT STYLING:
- Text should be bold, modern, and highly readable
- Use clean sans-serif typography
- Ensure sufficient contrast for readability
- Text should feel integrated with the visual composition, not just overlaid

OUTPUT: A single, cinematic video closing frame with integrated branding elements (logo, headline, key message) that concludes the video story while maintaining character consistency.`;

        while (retries >= 0) {
          try {
            const response = await fetch('http://localhost:3001/api/generate-image-gemini', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                prompt: videoFramePrompt,
                referenceImages,
                aspectRatio: '9:16'
              }),
            });

            if (!response.ok) {
              const errorData = await response.json().catch(() => ({}));
              const errorMsg = errorData.details || errorData.error || `HTTP ${response.status}`;
              throw new Error(`Last frame ${i + 1} failed: ${errorMsg}`);
            }

            const data = await response.json();
            optionsWithImages.push({
              ...option,
              imageUrl: data.url,
              id: `last-frame-${Date.now()}-${i}`,
              textElements: option.textElements // Preserve text placement info
            });

            console.log(`[ConversationalCreativeV2] Generated last frame image ${i + 1}/${framePrompts.length}`);
            break; // Success - exit retry loop
          } catch (imageError) {
            lastError = imageError;
            retries--;
            if (retries >= 0) {
              console.warn(`[ConversationalCreativeV2] Retrying last frame ${i + 1} (${retries + 1} attempts left):`, imageError.message);
              await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2s before retry
            }
          }
        }

        // If all retries failed, add with error flag
        if (retries < 0) {
          console.error(`[ConversationalCreativeV2] All retries failed for last frame ${i + 1}:`, lastError);
          optionsWithImages.push({
            ...option,
            imageUrl: null,
            id: `last-frame-${Date.now()}-${i}`,
            textElements: option.textElements,
            error: true
          });
        }

        // Delay between images (unless this was the last one)
        if (i < framePrompts.length - 1 && retries >= 0) {
          await new Promise(resolve => setTimeout(resolve, 1500));
        }
      }

      // Filter out failed options
      const successfulOptions = optionsWithImages.filter(opt => !opt.error);

      if (successfulOptions.length > 0) {
        setLastFrameOptions(successfulOptions);
        console.log('[ConversationalCreativeV2] Background last frame generation complete:', successfulOptions.length, 'options');
        if (successfulOptions.length < framePrompts.length) {
          console.warn(`[ConversationalCreativeV2] ${framePrompts.length - successfulOptions.length} last frame images failed to generate`);
        }
      } else {
        // Only show error if ALL images failed
        throw new Error('All last frame images failed to generate');
      }

    } catch (error) {
      console.error('[ConversationalCreativeV2] Error in background last frame generation:', error);
      setLastFrameError(error.message);
      setLastFrameOptions([]);
    } finally {
      setIsGeneratingLastFrame(false);
    }
  };

  /**
   * Handle last frame option selection
   */
  const handleLastFrameSelect = async (option) => {
    // Get the actual step for lastFrame field (React state may be stale)
    const lastFrameStepIndex = conversationFlowService.findStepByField(currentPhase, 'videoSettings.lastFrame');
    console.log('[handleLastFrameSelect] Selected last frame:', option.title, 'Actual step:', lastFrameStepIndex, 'State step:', phaseStep);

    const selectedLastFrameData = {
      id: option.id,
      name: option.name,
      title: option.title,
      description: option.description,
      imageUrl: option.imageUrl,
      imagePrompt: option.imagePrompt,
      textElements: option.textElements
    };

    // Store in ref immediately for reliable access (React state updates are async)
    latestLastFrameRef.current = selectedLastFrameData;

    // Build updatedVideoSettings using refs as fallback (React state may be stale)
    const updatedVideoSettings = {
      ...(creativeBrief.videoSettings || {}),
      firstFrame: latestFirstFrameRef.current || creativeBrief.videoSettings?.firstFrame,
      lastFrame: selectedLastFrameData
    };
    const updatedBrief = {
      ...creativeBrief,
      videoSettings: updatedVideoSettings
    };

    console.log('[handleLastFrameSelect] updatedBrief.videoSettings:', updatedBrief.videoSettings);

    updateCreativeBrief('videoSettings.lastFrame', selectedLastFrameData);

    addUserMessage(option.title);

    // Clear last frame options after selection to prevent re-showing
    setLastFrameOptions([]);

    console.log('[handleLastFrameSelect] Calling advanceFlow with step override:', lastFrameStepIndex);
    await advanceFlow(updatedBrief, lastFrameStepIndex);
  };

  /**
   * Retry last frame generation if it failed
   */
  const handleRetryLastFrame = async () => {
    await generateLastFrameImagesInBackground(creativeBrief);
  };

  /**
   * Refine an image based on user feedback
   * @param {string} imageType - Type of image: 'casting', 'location', 'firstFrame', 'lastFrame'
   * @param {object} currentOption - The current zoomed option with imageUrl
   * @param {string} feedback - User's refinement instructions
   */
  const handleImageRefinement = async (imageType, currentOption, feedback) => {
    if (!feedback.trim()) {
      setRefinementError('Please enter refinement instructions');
      return;
    }

    setIsRefining(true);
    setRefinementError(null);

    try {
      console.log(`[${new Date().toISOString()}] [ConversationalCreativeV2] Refining ${imageType} image with feedback:`, feedback);

      // Get user's color choices
      const userColors = creativeBrief.brandColors || colorPalette || [];
      const colorString = userColors.length > 0 ? userColors.join(', ') : '';

      // Build reference images with instructions
      const referenceImages = [];

      // Add the current image as reference
      if (currentOption.imageUrl) {
        referenceImages.push({
          url: currentOption.imageUrl,
          instruction: `REFERENCE IMAGE (ORIGINAL):
This is the original image to refine. Maintain the same style, composition, and quality while applying the user's requested changes.`
        });
      }

      // Add any existing scene elements based on image type
      const videoSettings = creativeBrief.videoSettings || {};
      const selectedCasting = creativeBrief.selectedCasting || videoSettings.selectedCasting;
      const selectedLocation = creativeBrief.selectedLocation || videoSettings.selectedLocation;

      if (selectedCasting?.imageUrl && imageType !== 'casting') {
        referenceImages.push({
          url: selectedCasting.imageUrl,
          instruction: `REFERENCE IMAGE (SELECTED ACTOR):
CRITICAL: Maintain this EXACT person's appearance in the refined image. Do not change their face, body, or characteristics.`
        });
      }

      if (selectedLocation?.imageUrl && imageType !== 'location') {
        referenceImages.push({
          url: selectedLocation.imageUrl,
          instruction: `REFERENCE IMAGE (SELECTED LOCATION):
Maintain this location/setting in the refined image.`
        });
      }

      // Polish the refinement prompt by analyzing the original image
      let polishedPrompt = feedback;
      try {
        console.log(`[${new Date().toISOString()}] [ConversationalCreativeV2] Polishing refinement prompt with image analysis...`);
        const polishResponse = await fetch('http://localhost:3001/api/polish-refinement-prompt', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            originalImageUrl: currentOption.imageUrl,
            userInstructions: feedback,
            context: {
              imageType,
              title: currentOption.title || currentOption.name,
              brandColors: colorString || undefined
            }
          })
        });

        if (polishResponse.ok) {
          const polishData = await polishResponse.json();
          polishedPrompt = polishData.polishedPrompt;
          console.log(`[${new Date().toISOString()}] [ConversationalCreativeV2] Polished prompt received:`, polishedPrompt.substring(0, 100) + '...');
        } else {
          console.warn(`[${new Date().toISOString()}] [ConversationalCreativeV2] Polish endpoint failed, using original feedback`);
        }
      } catch (polishError) {
        console.warn(`[${new Date().toISOString()}] [ConversationalCreativeV2] Polish request failed, using original feedback:`, polishError.message);
      }

      // Build the complete refinement prompt using the polished version
      const refinementPrompt = `IMAGE REFINEMENT REQUEST:

${polishedPrompt}

ADDITIONAL REQUIREMENTS:
- Maintain the overall scene setup and composition from the original
- Maintain the exact appearance of any people from the casting references
- Keep 9:16 vertical format for social media
${colorString ? `- Use brand colors: ${colorString}` : ''}

OUTPUT: A refined version of the original image with the changes applied.`;

      let response;
      let retries = 0;
      const MAX_RETRIES = 1;

      while (retries <= MAX_RETRIES) {
        response = await fetch('http://localhost:3001/api/generate-image-gemini', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            prompt: refinementPrompt,
            referenceImages,
            aspectRatio: '9:16'
          }),
        });

        if (response.ok) break;

        const errorData = await response.json().catch(() => ({}));
        const isOverloaded = response.status === 500 && (
          errorData.details?.toLowerCase().includes('overloaded') ||
          errorData.error?.toLowerCase().includes('overloaded')
        );

        if (isOverloaded && retries < MAX_RETRIES) {
          retries++;
          addAIMessage(`⚠️ The image model is currently overloaded. Automatically retrying in 60 seconds (Attempt ${retries}/${MAX_RETRIES})...`);
          await new Promise(resolve => setTimeout(resolve, 60000));
          continue;
        }

        const errorMessage = errorData.details || errorData.error || 'Failed to refine image';
        console.error(`[${new Date().toISOString()}] [ConversationalCreativeV2] Server error:`, errorData);
        throw new Error(errorMessage);
      }

      const data = await response.json();
      console.log(`[${new Date().toISOString()}] [ConversationalCreativeV2] Image refined successfully`);

      // Create a NEW option for the refined image (keep original intact)
      const refinedOption = {
        ...currentOption,
        id: `${currentOption.id}-refined-${Date.now()}`,
        imageUrl: data.url,
        title: `${currentOption.title || currentOption.name || 'Image'} (Refined)`,
        name: currentOption.name ? `${currentOption.name} (Refined)` : undefined,
        description: `Refined: ${feedback}`,
        isRefined: true,
        originalId: currentOption.id
      };

      // Add the refined image to the options array (don't replace the original)
      // Also update the message data so the grid displays the new refined image
      switch (imageType) {
        case 'casting':
          setCastingOptions(prev => {
            const originalIndex = prev.findIndex(opt => opt.id === currentOption.id);
            const newOptions = [...prev];
            newOptions.splice(originalIndex + 1, 0, refinedOption);
            // Sync messages so grid refreshes
            setMessages(msgs => msgs.map(msg => {
              if (msg.data?.castingOptions) {
                return { ...msg, data: { ...msg.data, castingOptions: newOptions } };
              }
              return msg;
            }));
            return newOptions;
          });
          setZoomedCastingOption(refinedOption);
          break;
        case 'location':
          setLocationOptions(prev => {
            const originalIndex = prev.findIndex(opt => opt.id === currentOption.id);
            const newOptions = [...prev];
            newOptions.splice(originalIndex + 1, 0, refinedOption);
            setMessages(msgs => msgs.map(msg => {
              if (msg.data?.locationOptions) {
                return { ...msg, data: { ...msg.data, locationOptions: newOptions } };
              }
              return msg;
            }));
            return newOptions;
          });
          setZoomedLocationOption(refinedOption);
          break;
        case 'firstFrame':
          setFirstFrameOptions(prev => {
            const originalIndex = prev.findIndex(opt => opt.id === currentOption.id);
            const newOptions = [...prev];
            newOptions.splice(originalIndex + 1, 0, refinedOption);
            setMessages(msgs => msgs.map(msg => {
              if (msg.data?.firstFrameOptions) {
                return { ...msg, data: { ...msg.data, firstFrameOptions: newOptions } };
              }
              return msg;
            }));
            return newOptions;
          });
          setZoomedFirstFrameOption(refinedOption);
          break;
        case 'lastFrame':
          setLastFrameOptions(prev => {
            const originalIndex = prev.findIndex(opt => opt.id === currentOption.id);
            const newOptions = [...prev];
            newOptions.splice(originalIndex + 1, 0, refinedOption);
            setMessages(msgs => msgs.map(msg => {
              if (msg.data?.lastFrameOptions) {
                return { ...msg, data: { ...msg.data, lastFrameOptions: newOptions } };
              }
              return msg;
            }));
            return newOptions;
          });
          setZoomedLastFrameOption(refinedOption);
          break;
      }

      // Clear refinement text after success
      setRefinementText('');

    } catch (error) {
      console.error(`[${new Date().toISOString()}] [ConversationalCreativeV2] Error refining image:`, error);
      setRefinementError(error.message || 'Failed to refine image. Please try again.');
    } finally {
      setIsRefining(false);
    }
  };

  /**
   * Toggle favorite status for a generated image
   * @param {object} image - The generated image object
   */
  const handleGeneratedImageFavoriteToggle = async (image) => {
    if (!currentUser?.uid || !image?.id) return;

    const libraryItemId = generatedImageLibraryIds[image.id];
    if (!libraryItemId) {
      console.warn('[ConversationalCreativeV2] No library ID found for image:', image.id, '- Images may still be saving to library');
      // Optimistically update the UI anyway - it will sync once the library ID is available
      setGeneratedImageFavorites(prev => ({
        ...prev,
        [image.id]: !prev[image.id]
      }));
      return;
    }

    try {
      // Optimistically update UI first
      setGeneratedImageFavorites(prev => ({
        ...prev,
        [image.id]: !prev[image.id]
      }));

      await toggleFavorite(libraryItemId, currentUser.uid);
      console.log('[ConversationalCreativeV2] Toggled favorite for image:', image.id);
    } catch (error) {
      console.error('[ConversationalCreativeV2] Error toggling favorite:', error);
      // Revert on error
      setGeneratedImageFavorites(prev => ({
        ...prev,
        [image.id]: !prev[image.id]
      }));
    }
  };

  /**
   * Download all generated images in full resolution
   * @param {Array} images - Array of generated image objects with imageUrl
   */
  const handleDownloadAllImages = async (images) => {
    if (!images || images.length === 0) return;

    // For each image, fetch as blob and trigger download
    for (let i = 0; i < images.length; i++) {
      const image = images[i];
      if (!image.imageUrl) continue;

      try {
        // Fetch image as blob to bypass CORS restrictions
        const response = await fetch(image.imageUrl);
        const blob = await response.blob();

        // Create object URL and download link
        const objectUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = `${image.title?.replace(/\s+/g, '-') || `generated-image-${i + 1}`}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Clean up object URL
        URL.revokeObjectURL(objectUrl);

        // Small delay between downloads to prevent browser blocking
        if (i < images.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 300));
        }
      } catch (error) {
        console.error(`[ConversationalCreativeV2] Error downloading image ${i + 1}:`, error);
      }
    }
  };

  /**
   * Refine a generated image based on user feedback
   * Creates a NEW separate creative, not a replacement
   * @param {object} image - The generated image to refine
   * @param {string} feedback - User's refinement instructions
   * @param {Array} userReferenceImages - Array of reference images [{ id, url, status, label }]
   */
  const handleGeneratedImageRefine = async (image, feedback, userReferenceImages = []) => {
    if (!feedback?.trim() || !image?.imageUrl) {
      setRefinementError('Please enter refinement instructions');
      return;
    }

    // Filter to only valid reference images
    const validRefImages = userReferenceImages.filter(img => img.status === 'valid' && img.url);

    setIsRefining(true);
    setRefiningImageId(image.id);
    setRefinementError(null);

    try {
      console.log(`[${new Date().toISOString()}] [ConversationalCreativeV2] Refining generated image:`, image.title);
      console.log(`[${new Date().toISOString()}] [ConversationalCreativeV2] Reference images count:`, validRefImages.length);

      // Get user's color choices
      const userColors = creativeBrief.brandColors || colorPalette || [];
      const colorString = userColors.length > 0 ? userColors.join(', ') : '';

      // Build reference images array for API
      const referenceImages = [{
        url: image.imageUrl,
        instruction: `[ORIGINAL IMAGE]
This is the original image to refine. Maintain the same style, composition, and quality while applying the user's requested changes.`
      }];

      // Add user's labeled reference images
      validRefImages.forEach((refImg) => {
        referenceImages.push({
          url: refImg.url,
          instruction: `[${refImg.label}]
User-provided reference image. When the user mentions "${refImg.label}" in their instructions, they are referring to this specific image. Use it as directed in the refinement request.`
        });
      });

      // Add original product photos so the AI maintains product fidelity
      const productImages = creativeBrief.selectedAssets || creativeBrief.crawledImages || [];
      productImages.slice(0, 3).forEach((img, idx) => {
        const imgUrl = typeof img === 'string' ? img : img.url;
        if (imgUrl) {
          referenceImages.push({
            url: imgUrl,
            instruction: `[PRODUCT PHOTO ${idx + 1}] - HIGH-FIDELITY OBJECT. The product in the image MUST match this reference exactly. Maintain exact shape, color, design, and branding.`
          });
        }
      });

      // Add brand logo if available
      const brandLogo = selectedBrand?.logo || creativeBrief.logo;
      if (brandLogo) {
        referenceImages.push({
          url: brandLogo,
          instruction: '[BRAND LOGO] - HIGH-FIDELITY OBJECT. Reproduce this logo EXACTLY as-is. Do not recolor, distort, or modify it in any way.'
        });
      }

      // Build reference image legend for the prompt
      const refImageLegend = validRefImages.length > 0
        ? `\n\nREFERENCE IMAGES PROVIDED:\n${validRefImages.map(img => `- ${img.label}: User reference image`).join('\n')}\nWhen the user mentions these labels (e.g., "${validRefImages[0]?.label}"), apply elements from that specific reference image.`
        : '';

      // Polish the refinement prompt by analyzing the original image
      let polishedPrompt = feedback;
      try {
        console.log(`[${new Date().toISOString()}] [ConversationalCreativeV2] Polishing refinement prompt with image analysis...`);
        const polishResponse = await fetch('http://localhost:3001/api/polish-refinement-prompt', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            originalImageUrl: image.imageUrl,
            userInstructions: feedback,
            referenceImages: validRefImages.map(img => img.url),
            context: {
              imageType: 'generated',
              title: image.title,
              description: image.description,
              brandColors: colorString || undefined
            }
          })
        });

        if (polishResponse.ok) {
          const polishData = await polishResponse.json();
          polishedPrompt = polishData.polishedPrompt;
          console.log(`[${new Date().toISOString()}] [ConversationalCreativeV2] Polished prompt received:`, polishedPrompt.substring(0, 100) + '...');
        } else {
          console.warn(`[${new Date().toISOString()}] [ConversationalCreativeV2] Polish endpoint failed, using original feedback`);
        }
      } catch (polishError) {
        console.warn(`[${new Date().toISOString()}] [ConversationalCreativeV2] Polish request failed, using original feedback:`, polishError.message);
      }

      const refinementPrompt = `IMAGE REFINEMENT REQUEST:

${polishedPrompt}
${refImageLegend}

INSTRUCTIONS:
1. **Contextual Awareness**: The AI has been provided with an ORIGINAL image and possibly several REFERENCE images.
2. **OPTIONAL STYLE TRANSFER**: Only apply fonts, colors, or design styles from the reference images IF the user explicitly asks for them (e.g., "Use the font from [Image A]" or "Apply these colors").
3. **Default Behavior**: If the user does NOT explicitly mention a reference image or its style, treat those images as optional context only. Focus primarily on modifying the ORIGINAL image based on the polished prompt above.
4. **Merge Logic**: If requested, merge the specific style characteristics from the reference into the composition of the original image.

CRITICAL REQUIREMENTS:
- The FIRST image provided is the ORIGINAL image.
- Apply the requested changes while maintaining the core subject and composition.
- **PRECISION**: Do not force styles from reference images unless specifically commanded.

OUTPUT: A refined version of the original image with the changes applied.`;

      const stillSettings = creativeBrief.stillSettings || {};
      const response = await fetch('http://localhost:3001/api/generate-image-gemini', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: refinementPrompt,
          referenceImages,
          aspectRatio: stillSettings.aspectRatio || '1:1',
          userId: currentUser?.uid
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.details || errorData.error || 'Failed to refine image');
      }

      const data = await response.json();
      console.log(`[${new Date().toISOString()}] [ConversationalCreativeV2] Image refined successfully`);

      // Create refined image as a NEW separate creative
      const refinedImage = {
        id: `refined-${Date.now()}`,
        imageUrl: data.url,
        title: `${image.title?.replace(' (Refined)', '')} - Refined`,
        description: `Refined from: ${image.title}\nChanges: ${feedback}`,
        variationStyle: 'Refined',
        isRefined: true,
        originalId: image.id
      };

      // Add as a NEW message with the refined creative (separate from original)
      const refImageNote = validRefImages.length > 0
        ? ` (with ${validRefImages.length} reference image${validRefImages.length > 1 ? 's' : ''}: ${validRefImages.map(i => i.label).join(', ')})`
        : '';
      const refinedImageMessage = {
        id: `msg-refined-${Date.now()}`,
        type: 'ai',
        text: `Here's your refined image based on: "${feedback}"${refImageNote}`,
        timestamp: new Date().toISOString(),
        data: {
          generatedImages: [refinedImage],
          isCarousel: false,
          socialMediaPost: {
            brandName: selectedBrand?.name || 'Your Brand'
          }
        }
      };

      setMessages(prev => [...prev, refinedImageMessage]);

      // Save refined image to library as a separate item
      if (currentUser?.uid && selectedBrand?.id) {
        try {
          let uploadedUrl = refinedImage.imageUrl;
          if (uploadedUrl.startsWith('data:image')) {
            uploadedUrl = await uploadLibraryImage(uploadedUrl, currentUser.uid, selectedBrand.id, 'image');
          }

          const savedIds = await batchSaveItems([{
            name: refinedImage.title,
            description: refinedImage.description,
            imageUrl: uploadedUrl,
            type: 'image',
            tags: ['image', 'ai-generated', 'refined'],
            metadata: {
              product: creativeBrief.product || '',
              audience: creativeBrief.audience || '',
              originalImageId: image.id,
              refinementPrompt: feedback,
              referenceImageCount: validRefImages.length,
              referenceImageLabels: validRefImages.map(i => i.label),
              generatedAt: new Date().toISOString()
            }
          }], currentUser.uid, selectedBrand.id);

          if (savedIds[0]) {
            setGeneratedImageLibraryIds(prev => ({
              ...prev,
              [refinedImage.id]: savedIds[0]
            }));
          }
        } catch (saveError) {
          console.error(`[${new Date().toISOString()}] [ConversationalCreativeV2] Error saving refined image:`, saveError);
        }
      }

      // Update zoom to show refined image
      setZoomedGeneratedImage(refinedImage);
      setRefinementText('');

    } catch (error) {
      console.error(`[${new Date().toISOString()}] [ConversationalCreativeV2] Error refining generated image:`, error);
      setRefinementError(error.message || 'Failed to refine image. Please try again.');
    } finally {
      setIsRefining(false);
      setRefiningImageId(null);
    }
  };

  // ============================================
  // IMAGE SELECTION HANDLERS
  // ============================================

  const showRetrievedImageSelector = () => {
    const crawledImages = creativeBrief.crawledImages || [];
    if (crawledImages.length === 0) {
      addAIMessage("It looks like we don't have any images from your website. Would you like to upload some instead?", [
        { text: "Upload from my device", value: 'upload_assets', action: 'trigger_upload', uploadContext: 'general' },
        { text: "Enter an image URL", value: 'enter_image_url' },
        { text: "No assets - generate from scratch", value: 'skip_assets' }
      ]);
      return;
    }

    // Enable image selection mode
    setIsSelectingImages(true);
    setPendingImageSelections([]);

    // Create selectable images (show up to 8)
    const selectableImages = crawledImages.slice(0, 8).map((img, i) => ({
      id: `retrieved-${i}`,
      index: i,
      url: img.url || img,
      alt: img.alt || `Image ${i + 1}`
    }));

    // Show image selector message with images
    addAIMessage(
      "Here are the images I found. Select up to 2 images you'd like to use, or choose another option:",
      [
        { text: "Continue with selection", value: 'confirm_image_selection', requiresSelection: true },
        { text: "Upload from my device", value: 'upload_assets', action: 'trigger_upload', uploadContext: 'general' },
        { text: "Choose from asset library", value: 'select_from_library' },
        { text: "Skip - no images needed", value: 'skip_assets' }
      ],
      { selectableImages, maxSelections: MAX_IMAGE_SELECTIONS }
    );
  };

  const MAX_IMAGE_SELECTIONS = 2;

  const handleSelectableImageClick = (imageIndex) => {
    if (!isSelectingImages) return;

    setPendingImageSelections(prev => {
      const isAlreadySelected = prev.includes(imageIndex);
      if (isAlreadySelected) {
        // Allow deselection
        return prev.filter(i => i !== imageIndex);
      }
      // Only allow selection if under the max limit
      if (prev.length >= MAX_IMAGE_SELECTIONS) {
        console.log(`[ConversationalCreativeV2] Max ${MAX_IMAGE_SELECTIONS} images can be selected`);
        return prev;
      }
      return [...prev, imageIndex];
    });
  };

  const handleImageSelection = (option) => {
    // Extract image index from option value (e.g., "select_image_0")
    const index = parseInt(option.value.replace('select_image_', ''), 10);
    handleSelectableImageClick(index);
  };

  const handleUseAllImages = async () => {
    const crawledImages = creativeBrief.crawledImages || [];
    const allAssets = crawledImages.map((img, i) => ({
      id: Date.now() + i,
      name: `Website Image ${i + 1}`,
      url: img.url || img,
      source: 'crawled'
    }));

    setIsSelectingImages(false);
    setPendingImageSelections([]);

    // Update creative brief with selected assets
    updateCreativeBrief('selectedAssets', allAssets);

    addAIMessage(`Great! I've added all ${allAssets.length} images to your creative. Want to add more assets?`, [
      { text: "Upload from my device", value: 'upload_assets', action: 'trigger_upload', uploadContext: 'general' },
      { text: "Enter an image URL", value: 'enter_image_url' },
      { text: "No, let's move on", value: 'finish_assets' }
    ]);
  };

  const handleConfirmImageSelection = async () => {
    const crawledImages = creativeBrief.crawledImages || [];
    const selectedAssets = pendingImageSelections.map(index => {
      const img = crawledImages[index];
      return {
        id: Date.now() + index,
        name: `Website Image ${index + 1}`,
        url: img?.url || img,
        source: 'crawled'
      };
    });

    setIsSelectingImages(false);
    setPendingImageSelections([]);

    if (selectedAssets.length === 0) {
      addAIMessage("No images selected. Would you like to add assets a different way?", [
        { text: "Upload from my device", value: 'upload_assets', action: 'trigger_upload', uploadContext: 'general' },
        { text: "Enter an image URL", value: 'enter_image_url' },
        { text: "No assets - generate from scratch", value: 'skip_assets' }
      ]);
      return;
    }

    // Update creative brief with selected assets
    updateCreativeBrief('selectedAssets', selectedAssets);

    // If we've selected the max number of images, don't prompt for more - just continue
    if (selectedAssets.length >= MAX_IMAGE_SELECTIONS) {
      addAIMessage(`Perfect! I've added ${selectedAssets.length} images to your creative. Let's continue!`);
      await advanceFlow();
    } else {
      addAIMessage(`Perfect! I've added ${selectedAssets.length} image${selectedAssets.length !== 1 ? 's' : ''} to your creative. Want to add more?`, [
        { text: "Select more from website", value: 'select_retrieved_images' },
        { text: "Upload from my device", value: 'upload_assets', action: 'trigger_upload', uploadContext: 'general' },
        { text: "Enter an image URL", value: 'enter_image_url' },
        { text: "No, let's move on", value: 'finish_assets' }
      ]);
    }
  };

  const showAssetLibrarySelector = () => {
    // Get assets from the selected brand's asset library
    const brandAssets = selectedBrand?.assets || [];

    if (brandAssets.length === 0) {
      addAIMessage("Your asset library is empty. Would you like to add images another way?", [
        { text: "Upload from my device", value: 'upload_assets', action: 'trigger_upload', uploadContext: 'general' },
        { text: "Select from website images", value: 'select_retrieved_images' },
        { text: "Skip - no images needed", value: 'skip_assets' }
      ]);
      return;
    }

    // Enable image selection mode for library assets
    setIsSelectingImages(true);
    setPendingImageSelections([]);

    // Create selectable images from brand assets (show up to 8)
    const selectableImages = brandAssets.slice(0, 8).map((asset, i) => ({
      id: `library-${i}`,
      index: i,
      url: asset.url || asset.imageUrl || asset,
      alt: asset.name || asset.alt || `Asset ${i + 1}`,
      source: 'library'
    }));

    // Show library selector message with images
    addAIMessage(
      "Here are the images from your asset library. Select up to 2 images:",
      [
        { text: "Continue with selection", value: 'confirm_library_selection', requiresSelection: true },
        { text: "Upload new images", value: 'upload_assets', action: 'trigger_upload', uploadContext: 'general' },
        { text: "Select from website instead", value: 'select_retrieved_images' },
        { text: "Skip - no images needed", value: 'skip_assets' }
      ],
      { selectableImages, maxSelections: MAX_IMAGE_SELECTIONS, source: 'library' }
    );
  };

  const handleConfirmLibrarySelection = async () => {
    const brandAssets = selectedBrand?.assets || [];
    const selectedAssets = pendingImageSelections.map(index => {
      const asset = brandAssets[index];
      return {
        id: Date.now() + index,
        name: asset?.name || `Library Asset ${index + 1}`,
        url: asset?.url || asset?.imageUrl || asset,
        source: 'library'
      };
    });

    setIsSelectingImages(false);
    setPendingImageSelections([]);

    if (selectedAssets.length === 0) {
      addAIMessage("No images selected. Would you like to add assets a different way?", [
        { text: "Upload from my device", value: 'upload_assets', action: 'trigger_upload', uploadContext: 'general' },
        { text: "Select from website images", value: 'select_retrieved_images' },
        { text: "Skip - no images needed", value: 'skip_assets' }
      ]);
      return;
    }

    // Update creative brief with selected assets
    updateCreativeBrief('selectedAssets', selectedAssets);

    // If we've selected the max number of images, don't prompt for more - just continue
    if (selectedAssets.length >= MAX_IMAGE_SELECTIONS) {
      addAIMessage(`Perfect! I've added ${selectedAssets.length} images from your library. Let's continue!`);
      await advanceFlow();
    } else {
      addAIMessage(`Perfect! I've added ${selectedAssets.length} image${selectedAssets.length !== 1 ? 's' : ''} from your library. Want to add more?`, [
        { text: "Select more from library", value: 'select_from_library' },
        { text: "Upload from my device", value: 'upload_assets', action: 'trigger_upload', uploadContext: 'general' },
        { text: "No, let's move on", value: 'finish_assets' }
      ]);
    }
  };

  // ============================================
  // USER INPUT HANDLING
  // ============================================

  const handleUserSubmit = async (e) => {
    e.preventDefault();
    if (!userInput.trim() || isLoading) return;

    const input = userInput.trim();
    setUserInput('');
    if (userInputRef.current) {
      userInputRef.current.style.height = 'auto';
    }
    addUserMessage(input);

    setIsLoading(true);
    try {
      const urlMatch = input.match(/https?:\/\/[^\s]+/);
      if (urlMatch) {
        await handleUrlInput(urlMatch[0], input);
        return;
      }

      await handleFreeTextInput(input);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUrlInput = async (url, fullInput) => {
    addAIMessage("Analyzing that URL...");

    try {
      const response = await fetch('http://localhost:3001/api/crawl-images', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url })
      });

      if (response.ok) {
        const data = await response.json();

        // Store product info from URL
        if (data.summary) {
          updateCreativeBrief('product', data.summary.title || data.summary.headline || url);
          updateCreativeBrief('productUrl', url);
          updateCreativeBrief('urlSummary', data.summary);
        }

        // Store crawled images for later use in art director phase
        if (data.images && data.images.length > 0) {
          updateCreativeBrief('crawledImages', data.images);
        }

        const imageCount = data.count || data.images?.length || 0;
        const description = data.summary?.description ? `\n\n${data.summary.description}` : '';

        // Inform user about images found, but don't ask them to select now
        // They'll pick images when chatting with the art director
        addAIMessage(
          `Got it! I found ${imageCount} image${imageCount !== 1 ? 's' : ''} on that page that we can use later.${description}\n\nLet's continue building your creative brief - you'll be able to pick the perfect images when we get to the visual design phase with the Art Director.`
        );

        // Advance to the next step in the flow
        await advanceFlow();
      } else {
        throw new Error('Failed to crawl URL');
      }
    } catch (error) {
      console.error('Error crawling URL:', error);
      addAIMessage("I couldn't analyze that URL, but no worries - let's continue. You can always add images later.");
      await advanceFlow();
    }
  };

  const handleFreeTextInput = async (input) => {
    // Check if we're awaiting a custom scenario input
    // Note: messages use role: 'ai', not 'assistant'
    const lastAIMessage = [...messages].reverse().find(m => m.role === 'ai');

    // Check for card refinement request
    if (lastAIMessage?.data?.awaitingCardRefinement) {
      console.log(`[${new Date().toISOString()}] [handleFreeTextInput] Detected awaitingCardRefinement, calling processCardRefinement`);
      await processCardRefinement(input);
      return;
    }

    // Check for generated image refinement request
    if (lastAIMessage?.data?.awaitingImageRefinement) {
      console.log(`[${new Date().toISOString()}] [handleFreeTextInput] Detected awaitingImageRefinement, processing image refinement`);
      await processGeneratedImageRefinement(input, lastAIMessage.data.imagesToRefine);
      return;
    }

    // Check for image description input (when user chose to generate from scratch)
    if (lastAIMessage?.data?.awaitingImageDescription || isDescribingImage) {
      console.log('[handleFreeTextInput] Detected awaitingImageDescription, processing image description');
      await handleImageDescriptionSubmit(input);
      return;
    }

    if (lastAIMessage?.data?.awaitingCustomScenario) {
      // User is providing their custom scenario description
      console.log('[handleFreeTextInput] Detected awaitingCustomScenario, calling handleCustomScenarioSubmit');
      await handleCustomScenarioSubmit(input);
      return;
    }

    if (!conversationAgent) {
      const currentField = conversationFlowService.getStepField(currentPhase, phaseStep);
      if (currentField) {
        updateCreativeBrief(currentField, input);

        // Trigger casting: first check for favorites, otherwise generate
        if (currentField === 'peopleDescription' || currentField === 'videoSettings.actors') {
          const updatedBrief = currentField.includes('.')
            ? conversationFlowService.setNestedValue({ ...creativeBrief }, currentField, input)
            : { ...creativeBrief, [currentField]: input };
          loadOrGenerateCasting(input, updatedBrief);
        }

        // Create updated brief for navigation (React state updates are async)
        const updatedBriefForNav = { ...creativeBrief, [currentField]: input };
        await advanceFlow(updatedBriefForNav);
      }
      return;
    }

    const result = await conversationAgent.processFreeText({
      input,
      phaseId: currentPhase,
      stepIndex: phaseStep,
      creativeBrief,
      brand: selectedBrand
    });

    // Track updated brief for navigation (React state updates are async)
    let updatedBriefForNav = creativeBrief;

    if (result?.briefUpdate && result.briefUpdate.field) {
      // Create updated brief before state update
      if (result.briefUpdate.field.includes('.')) {
        updatedBriefForNav = conversationFlowService.setNestedValue({ ...creativeBrief }, result.briefUpdate.field, result.briefUpdate.value);
      } else {
        updatedBriefForNav = { ...creativeBrief, [result.briefUpdate.field]: result.briefUpdate.value };
      }

      updateCreativeBrief(result.briefUpdate.field, result.briefUpdate.value);

      // Trigger casting: first check for favorites, otherwise generate
      if (result.briefUpdate.field === 'peopleDescription' || result.briefUpdate.field === 'videoSettings.actors') {
        loadOrGenerateCasting(result.briefUpdate.value, updatedBriefForNav);
      }
    }

    if (result?.message) {
      addAIMessage(result.message, result.options);
    }

    if (result?.advance) {
      await advanceFlow(updatedBriefForNav);
    }
  };

  // ============================================
  // EXPERT NOTES (Sidebar)
  // ============================================

  // Get notes collected for each expert phase
  const getExpertNotes = (phaseKey) => {
    const noteFields = {
      strategist: [
        { key: 'product', label: 'Product/Service' },
        { key: 'targetAudience', label: 'Target Audience' },
        { key: 'desiredEmotion', label: 'Desired Emotion' },
        { key: 'objective', label: 'Objective' }
      ],
      creative_director: [
        { key: 'creativeRiskTolerance', label: 'Creative Boldness' },
        { key: 'marketingAngle', label: 'Marketing Angle' }
      ],
      copywriter: [
        { key: 'selectedHeadline', label: 'Headline' },
        { key: 'tone', label: 'Tone' },
        { key: 'cta', label: 'Call to Action' }
      ],
      art_director: [
        { key: 'visualStyle', label: 'Visual Style' },
        { key: 'imageType', label: 'Image Type' },
        { key: 'photographyMood', label: 'Lighting' },
        { key: 'selectedCasting', label: 'Selected Cast', isObject: true },
        { key: 'selectedLocation', label: 'Selected Location', isObject: true }
      ],
      video_director: [
        { key: 'videoSettings.videoLength', label: 'Video Length' },
        { key: 'videoSettings.videoStyle', label: 'Video Style' },
        { key: 'videoSettings.dialogueType', label: 'Dialogue Type' }
      ],
      still_director: [
        { key: 'stillSettings.aspectRatio', label: 'Aspect Ratio' },
        { key: 'stillSettings.compositionStyle', label: 'Composition' }
      ]
    };

    const fields = noteFields[phaseKey] || [];
    const notes = [];

    for (const field of fields) {
      const value = conversationFlowService.getNestedValue(creativeBrief, field.key);
      if (value) {
        if (field.isObject && typeof value === 'object') {
          notes.push({
            key: field.key,
            label: field.label,
            value: value.description || value.name || JSON.stringify(value),
            imageUrl: value.imageUrl
          });
        } else if (typeof value !== 'object') {
          notes.push({
            key: field.key,
            label: field.label,
            value: String(value)
          });
        }
      }
    }

    return notes;
  };

  // Toggle expert expansion in sidebar
  const toggleExpertExpanded = (phaseKey) => {
    setExpandedExperts(prev => ({
      ...prev,
      [phaseKey]: !prev[phaseKey]
    }));
  };

  // Start editing a note
  const startEditingNote = (expertKey, field, currentValue) => {
    setEditingNote({ expertKey, field });
    setEditNoteValue(currentValue);
    setNoteAiInstructions('');
  };

  // Save edited note
  const saveEditedNote = () => {
    if (editingNote) {
      updateCreativeBrief(editingNote.field, editNoteValue);
      setEditingNote(null);
      setEditNoteValue('');
    }
  };

  // Cancel editing
  const cancelEditingNote = () => {
    setEditingNote(null);
    setEditNoteValue('');
    setNoteAiInstructions('');
  };

  // Apply AI edit to note
  const applyAiEditToNote = async () => {
    if (!editingNote || !noteAiInstructions.trim() || !conversationAgent) return;

    setIsProcessingNoteAi(true);
    try {
      const result = await conversationAgent.processAIBriefEdit(
        editingNote.field,
        editNoteValue,
        noteAiInstructions
      );
      if (result) {
        setEditNoteValue(result);
      }
    } catch (error) {
      console.error('Error applying AI edit to note:', error);
    } finally {
      setIsProcessingNoteAi(false);
      setNoteAiInstructions('');
    }
  };

  // ============================================
  // RENDER
  // ============================================

  return (
    <div className="conversational-creative">
      <div className="conversation-container">
        {/* Progress Sidebar */}
        <div className="progress-sidebar">
          <h3>Creative Brief</h3>
          <div className="phase-indicators">
            {Object.entries(PHASE_INFO).map(([phase, info]) => {
              const notes = getExpertNotes(phase);
              const isActive = currentPhase === phase;
              const isCompleted = Object.keys(PHASES).indexOf(currentPhase) > Object.keys(PHASES).indexOf(phase);
              const isExpanded = expandedExperts[phase];
              const hasNotes = notes.length > 0;

              return (
                <div key={phase} className="phase-section">
                  <div
                    className={`phase-indicator ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''} ${hasNotes ? 'has-notes' : ''}`}
                    onClick={() => hasNotes && toggleExpertExpanded(phase)}
                    style={{ cursor: hasNotes ? 'pointer' : 'default' }}
                  >
                    <span className="phase-emoji">{info.emoji}</span>
                    <span className="phase-name">{info.title}</span>
                    {hasNotes && (
                      <span className="expand-indicator">{isExpanded ? '▼' : '▶'}</span>
                    )}
                  </div>

                  {/* Expert Notes (expandable) */}
                  {isExpanded && hasNotes && (
                    <div className="expert-notes">
                      {notes.map((note, idx) => (
                        <div key={idx} className="expert-note">
                          {editingNote?.expertKey === phase && editingNote?.field === note.key ? (
                            <div className="note-edit-container">
                              <div className="note-label">{note.label}</div>
                              <textarea
                                className="note-edit-input"
                                value={editNoteValue}
                                onChange={(e) => setEditNoteValue(e.target.value)}
                                rows={3}
                              />
                              <div className="note-ai-helper">
                                <input
                                  type="text"
                                  placeholder="AI: e.g., make it more punchy..."
                                  value={noteAiInstructions}
                                  onChange={(e) => setNoteAiInstructions(e.target.value)}
                                  onKeyDown={(e) => e.key === 'Enter' && applyAiEditToNote()}
                                  disabled={isProcessingNoteAi}
                                />
                                <button
                                  onClick={applyAiEditToNote}
                                  disabled={isProcessingNoteAi || !noteAiInstructions.trim()}
                                  className="ai-apply-btn"
                                >
                                  {isProcessingNoteAi ? '...' : '✨'}
                                </button>
                              </div>
                              <div className="note-edit-actions">
                                <button onClick={saveEditedNote} className="note-save-btn">Save</button>
                                <button onClick={cancelEditingNote} className="note-cancel-btn">Cancel</button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <div className="note-label">{note.label}</div>
                              {note.imageUrl && (
                                <img src={note.imageUrl} alt={note.label} className="note-thumbnail" />
                              )}
                              <div className="note-value" onClick={() => startEditingNote(phase, note.key, note.value)}>
                                {note.value}
                                <span className="edit-hint">✏️</span>
                              </div>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="config-info">
            Config: {conversationFlowService.getConfigFileName()}
          </div>
        </div>

        {/* Chat Area */}
        <div className="chat-area">
          <div className="chat-header">
            <div>
              <h2>{PHASE_INFO[currentPhase]?.emoji || '💬'} {PHASE_INFO[currentPhase]?.title || 'Chat'}</h2>
              <p>{PHASE_INFO[currentPhase]?.subtitle || ''}</p>
            </div>
            <div className="chat-header-actions">
              {/* Variant Lab Button - show when we have a creative brief with content */}
              {creativeBrief?.product && (
                <button
                  className="variant-lab-header-btn"
                  onClick={() => setShowVariantLab(true)}
                  title="Create A/B Test Variants"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)',
                    padding: 'var(--space-2) var(--space-3)',
                    background: generatedVariants ? 'var(--color-success)' : 'var(--color-bg-tertiary)',
                    color: generatedVariants ? 'white' : 'var(--color-text-secondary)',
                    border: '1px solid var(--color-border)',
                    borderRadius: 'var(--radius-md)',
                    fontSize: 'var(--font-size-sm)',
                    cursor: 'pointer',
                    transition: 'all var(--transition-fast)'
                  }}
                >
                  <Beaker size={16} />
                  {generatedVariants ? `${generatedVariants.variants?.length || 0} Variants` : 'A/B Test'}
                </button>
              )}
            </div>
          </div>

          {/* Funnel Context Bar - shown when navigating from Funnel Builder */}
          {funnelContext?.funnelStage && (
            <div className="funnel-context-bar">
              <div className="funnel-context-stage">
                <span className="funnel-stage-badge" data-stage={funnelContext.funnelStage}>
                  {funnelContext.funnelStage}
                </span>
                <span className="funnel-stage-name">
                  {FUNNEL_STAGE_CONTEXT[funnelContext.funnelStage]?.name}
                </span>
              </div>
              <div className="funnel-context-details">
                {funnelContext.campaignName && (
                  <span className="funnel-campaign-name">
                    📢 {funnelContext.campaignName}
                  </span>
                )}
                <span className="funnel-stage-goal">
                  Goal: {FUNNEL_STAGE_CONTEXT[funnelContext.funnelStage]?.goal}
                </span>
              </div>
              <button
                className="funnel-context-close"
                onClick={() => setFunnelContext(null)}
                title="Remove funnel context"
              >
                ×
              </button>
            </div>
          )}

          {/* Pillar Context Banner - shown when a pillar is selected */}
          {selectedPillar && (
            <PillarContextBanner
              pillar={selectedPillar}
              funnelStage={funnelContext?.funnelStage}
              onChangePillar={() => setShowPillarSelector(true)}
              onClearPillar={() => setSelectedPillar(null)}
              expanded={pillarBannerExpanded}
              onToggleExpanded={() => setPillarBannerExpanded(!pillarBannerExpanded)}
            />
          )}

          {/* Pillar Selector - shown when no pillar selected and pillars are available */}
          {!selectedPillar && availablePillars.length > 0 && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              padding: 'var(--space-3)',
              background: 'var(--color-bg-secondary)',
              borderRadius: 'var(--radius-md)',
              marginBottom: 'var(--space-4)'
            }}>
              <Compass size={16} style={{ color: 'var(--color-text-tertiary)' }} />
              <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                Select a marketing pillar for this creative:
              </span>
              <PillarSelector
                pillars={availablePillars}
                value={null}
                onChange={(pillarId) => {
                  const pillar = availablePillars.find(p => p.id === pillarId);
                  setSelectedPillar(pillar);
                }}
                mode="compact"
                placeholder="Choose pillar..."
                showClear={false}
              />
            </div>
          )}

          {/* Pillar Selector Modal */}
          {showPillarSelector && (
            <div style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }} onClick={() => setShowPillarSelector(false)}>
              <div style={{
                background: 'var(--color-bg-primary)',
                borderRadius: 'var(--radius-lg)',
                padding: 'var(--space-6)',
                maxWidth: '500px',
                width: '90%'
              }} onClick={e => e.stopPropagation()}>
                <h3 style={{ marginBottom: 'var(--space-4)' }}>Select Marketing Pillar</h3>
                <PillarSelector
                  pillars={availablePillars}
                  value={selectedPillar?.id}
                  onChange={(pillarId) => {
                    const pillar = availablePillars.find(p => p.id === pillarId);
                    setSelectedPillar(pillar);
                    setShowPillarSelector(false);
                  }}
                  mode="pills"
                  showFunnelStage={true}
                />
                <button
                  onClick={() => setShowPillarSelector(false)}
                  style={{ marginTop: 'var(--space-4)' }}
                  className="btn btn-ghost"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          <div className="chat-messages" ref={chatContainerRef}>
            {messages.map((message, idx) => {
              // Determine if we should show the brief sheet after this message
              // Only show in brief_review phase, not in video_director or still_director
              const shouldShowBriefAfter = briefSheetData &&
                message.phase === 'brief_review' &&
                message.text &&
                message.text.includes("Here's your complete creative brief");

              return (
                <div key={idx} className="message-wrapper">
                  <div className={`message ${message.role}`}>
                    {message.role === 'ai' && (
                      <div className="message-avatar">
                        {PHASE_INFO[message.phase || currentPhase]?.emoji || '🤖'}
                      </div>
                    )}
                    <div className="message-content">
                      <div className="message-text-wrapper">
                        <div className="message-text">{renderMarkdown(message.text)}</div>
                        <div className="message-actions">
                          {/* Fork button - show on AI messages only, except the last one */}
                          {chatId && message.role === 'ai' && idx < messages.length - 1 && !isLoading && (
                            <button
                              className="message-fork-btn"
                              onClick={() => handleForkFromMessage(idx)}
                              title="Fork conversation from this point"
                            >
                              <GitFork size={14} />
                            </button>
                          )}
                          {/* Delete button - only show on user messages, not loading */}
                          {message.role === 'user' && !isLoading && (
                            <button
                              className="message-delete-btn"
                              onClick={() => handleDeleteMessage(idx)}
                              title="Delete from here and redo"
                            >
                              ×
                            </button>
                          )}
                        </div>
                      </div>
                      {/* Color Swatches UI */}
                      {message.data?.showColorSwatches && (
                        <div className="color-swatches-container">
                          <div className="color-swatches">
                            {colorPalette.map((color, index) => (
                              <div key={index} className="color-swatch-wrapper">
                                <div
                                  className="color-swatch"
                                  style={{ backgroundColor: color }}
                                  onClick={() => handleEditColor(index)}
                                  title={`Click to edit: ${color}`}
                                />
                                <button
                                  className="color-remove-btn"
                                  onClick={() => handleRemoveColor(index)}
                                  title="Remove color"
                                >
                                  ×
                                </button>
                              </div>
                            ))}
                            <button className="add-color-btn" onClick={handleAddColor} title="Add color">
                              +
                            </button>
                          </div>
                          <button
                            className="confirm-colors-btn"
                            onClick={handleConfirmColorPalette}
                            disabled={isLoading}
                          >
                            Confirm Colors
                          </button>
                          {showColorPicker && (
                            <div className="color-picker-modal">
                              <div className="color-picker-content">
                                <h4>{editingColorIndex !== null ? 'Edit Color' : 'Add Color'}</h4>
                                <input
                                  type="color"
                                  value={tempColor || '#667eea'}
                                  onChange={(e) => handleColorChange(e.target.value)}
                                  className="color-input"
                                />
                                <input
                                  type="text"
                                  placeholder="#667eea or rgb(102, 126, 234)"
                                  value={tempColor}
                                  onChange={(e) => handleColorChange(e.target.value)}
                                  className="color-text-input"
                                />
                                <div className="color-picker-actions">
                                  <button onClick={handleConfirmColor} className="btn-confirm">
                                    {editingColorIndex !== null ? 'Update' : 'Add'}
                                  </button>
                                  <button onClick={handleCancelColor} className="btn-cancel">
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      {/* Logo Preview */}
                      {message.data?.showLogoPreview && message.data?.logoUrl && (
                        <div className="logo-preview-container">
                          <div className="logo-preview">
                            <img
                              src={message.data.logoUrl}
                              alt={`${message.data.logoName || 'Brand'} Logo`}
                              className="logo-preview-image"
                            />
                          </div>
                          <p className="logo-preview-label">{message.data.logoName || 'Brand'} Logo</p>
                        </div>
                      )}
                      {/* Casting Options Grid */}
                      {message.data?.castingOptions && (
                        <div className="casting-options-container">
                          {showingCastingFavorites && (
                            <div className="favorites-badge" style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 'var(--space-2)',
                              marginBottom: 'var(--space-2)',
                              fontSize: 'var(--font-size-sm)',
                              color: 'var(--color-text-secondary)'
                            }}>
                              <span>Showing your favorites</span>
                            </div>
                          )}
                          <div className="casting-options-grid">
                            {message.data.castingOptions.map((option) => (
                              <div
                                key={option.id}
                                className={`casting-option${option.isRefined ? ' refined' : ''}`}
                                onClick={() => setZoomedCastingOption(option)}
                                style={{ position: 'relative' }}
                              >
                                {option.isRefined && (
                                  <div style={{
                                    position: 'absolute',
                                    top: 'var(--space-2)',
                                    left: 'var(--space-2)',
                                    zIndex: 5,
                                    background: 'var(--color-primary)',
                                    color: 'white',
                                    borderRadius: '4px',
                                    padding: '2px 6px',
                                    fontSize: '10px',
                                    fontWeight: 600
                                  }}>
                                    Refined
                                  </div>
                                )}
                                {option.isFavorite && (
                                  <div style={{
                                    position: 'absolute',
                                    top: 'var(--space-2)',
                                    right: 'var(--space-2)',
                                    zIndex: 5,
                                    background: 'rgba(0,0,0,0.5)',
                                    borderRadius: '50%',
                                    padding: '2px'
                                  }}>
                                    <FavoriteButton isFavorite={true} size={16} disabled />
                                  </div>
                                )}
                                <div className="casting-image-wrapper">
                                  <img src={option.imageUrl} alt={option.name || option.title} />
                                </div>
                                <div className="casting-info">
                                  <h4>{option.name || option.title}</h4>
                                  <p>{option.title}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                          {/* Generate More Button */}
                          <button
                            className="btn btn-secondary generate-more-btn"
                            onClick={handleGenerateMoreCasting}
                            disabled={isGeneratingMoreCasting || isGeneratingCasting}
                            style={{
                              width: '100%',
                              marginTop: 'var(--space-3)',
                              borderStyle: 'dashed',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 'var(--space-2)'
                            }}
                          >
                            {isGeneratingMoreCasting ? 'Generating more options...' : '+ Generate 4 More Options'}
                          </button>
                        </div>
                      )}
                      {/* Casting Loading Indicator */}
                      {message.data?.showCastingLoading && (
                        <div className="casting-loading-container">
                          <div className="casting-loading">
                            <div className="casting-loading-spinner"></div>
                            <p>Generating casting headshots...</p>
                          </div>
                        </div>
                      )}
                      {/* Location Options Grid (Video Director) */}
                      {message.data?.locationOptions && (
                        <div className="casting-options-container location-options-container">
                          {showingLocationFavorites && (
                            <div className="favorites-badge" style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 'var(--space-2)',
                              marginBottom: 'var(--space-2)',
                              fontSize: 'var(--font-size-sm)',
                              color: 'var(--color-text-secondary)'
                            }}>
                              <span>Showing your favorites</span>
                            </div>
                          )}
                          <div className="casting-options-grid location-options-grid">
                            {message.data.locationOptions.map((option) => (
                              <div
                                key={option.id}
                                className={`casting-option location-option${option.isRefined ? ' refined' : ''}`}
                                onClick={() => setZoomedLocationOption(option)}
                                style={{ position: 'relative' }}
                              >
                                {option.isRefined && (
                                  <div style={{
                                    position: 'absolute',
                                    top: 'var(--space-2)',
                                    left: 'var(--space-2)',
                                    zIndex: 5,
                                    background: 'var(--color-primary)',
                                    color: 'white',
                                    borderRadius: '4px',
                                    padding: '2px 6px',
                                    fontSize: '10px',
                                    fontWeight: 600
                                  }}>
                                    Refined
                                  </div>
                                )}
                                {option.isFavorite && (
                                  <div style={{
                                    position: 'absolute',
                                    top: 'var(--space-2)',
                                    right: 'var(--space-2)',
                                    zIndex: 5,
                                    background: 'rgba(0,0,0,0.5)',
                                    borderRadius: '50%',
                                    padding: '2px'
                                  }}>
                                    <FavoriteButton isFavorite={true} size={16} disabled />
                                  </div>
                                )}
                                <div className="casting-image-wrapper location-image-wrapper">
                                  <img src={option.imageUrl} alt={option.name || option.title} />
                                </div>
                                <div className="casting-info">
                                  <h4>{option.name || option.title}</h4>
                                  <p>{option.title}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                          {/* Generate More Button */}
                          <button
                            className="btn btn-secondary generate-more-btn"
                            onClick={handleGenerateMoreLocations}
                            disabled={isGeneratingMoreLocations || isGeneratingLocations}
                            style={{
                              width: '100%',
                              marginTop: 'var(--space-3)',
                              borderStyle: 'dashed',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 'var(--space-2)'
                            }}
                          >
                            {isGeneratingMoreLocations ? 'Generating more options...' : '+ Generate 4 More Options'}
                          </button>
                        </div>
                      )}
                      {/* Location Loading Indicator */}
                      {message.data?.showLocationLoading && (
                        <div className="casting-loading-container">
                          <div className="casting-loading">
                            <div className="casting-loading-spinner"></div>
                            <p>Scouting locations...</p>
                          </div>
                        </div>
                      )}
                      {/* First Frame Options Grid (Video Director) */}
                      {message.data?.firstFrameOptions && (
                        <div className="casting-options-container frame-options-container">
                          <div className="casting-options-grid frame-options-grid">
                            {message.data.firstFrameOptions.map((option) => (
                              <div
                                key={option.id}
                                className={`casting-option frame-option${option.isRefined ? ' refined' : ''}`}
                                onClick={() => setZoomedFirstFrameOption(option)}
                                style={{ position: 'relative' }}
                              >
                                {option.isRefined && (
                                  <div style={{
                                    position: 'absolute',
                                    top: 'var(--space-2)',
                                    left: 'var(--space-2)',
                                    zIndex: 5,
                                    background: 'var(--color-primary)',
                                    color: 'white',
                                    borderRadius: '4px',
                                    padding: '2px 6px',
                                    fontSize: '10px',
                                    fontWeight: 600
                                  }}>
                                    Refined
                                  </div>
                                )}
                                <div className="casting-image-wrapper frame-image-wrapper">
                                  <img src={option.imageUrl} alt={option.name || option.title} />
                                </div>
                                <div className="casting-info">
                                  <h4>{option.name || option.title}</h4>
                                  <p>{option.description?.substring(0, 60)}...</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {/* First Frame Loading Indicator */}
                      {message.data?.showFirstFrameLoading && (
                        <div className="casting-loading-container">
                          <div className="casting-loading">
                            <div className="casting-loading-spinner"></div>
                            <p>Composing opening shots...</p>
                          </div>
                        </div>
                      )}
                      {/* Last Frame Options Grid (Video Director) */}
                      {message.data?.lastFrameOptions && (
                        <div className="casting-options-container frame-options-container">
                          <div className="casting-options-grid frame-options-grid">
                            {message.data.lastFrameOptions.map((option) => (
                              <div
                                key={option.id}
                                className={`casting-option frame-option${option.isRefined ? ' refined' : ''}`}
                                onClick={() => setZoomedLastFrameOption(option)}
                                style={{ position: 'relative' }}
                              >
                                {option.isRefined && (
                                  <div style={{
                                    position: 'absolute',
                                    top: 'var(--space-2)',
                                    left: 'var(--space-2)',
                                    zIndex: 5,
                                    background: 'var(--color-primary)',
                                    color: 'white',
                                    borderRadius: '4px',
                                    padding: '2px 6px',
                                    fontSize: '10px',
                                    fontWeight: 600
                                  }}>
                                    Refined
                                  </div>
                                )}
                                <div className="casting-image-wrapper frame-image-wrapper">
                                  <img src={option.imageUrl} alt={option.name || option.title} />
                                </div>
                                <div className="casting-info">
                                  <h4>{option.name || option.title}</h4>
                                  <p>{option.description?.substring(0, 60)}...</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {/* Last Frame Loading Indicator */}
                      {message.data?.showLastFrameLoading && (
                        <div className="casting-loading-container">
                          <div className="casting-loading">
                            <div className="casting-loading-spinner"></div>
                            <p>Designing closing frames...</p>
                          </div>
                        </div>
                      )}
                      {/* Story Arc Scenario Selection */}
                      {message.data?.storyArcScenarios && message.data.storyArcScenarios.length > 0 && (
                        <div className="story-arc-scenarios-container" style={{
                          marginTop: 'var(--space-3)',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 'var(--space-3)'
                        }}>
                          {message.data.storyArcScenarios.map((scenario, idx) => (
                            <div
                              key={scenario.id || idx}
                              className="story-arc-scenario-card"
                              onClick={() => handleScenarioSelect(scenario)}
                              style={{
                                background: 'var(--color-bg-secondary)',
                                border: '2px solid var(--color-border)',
                                borderRadius: 'var(--radius-lg)',
                                padding: 'var(--space-4)',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = 'var(--color-primary)';
                                e.currentTarget.style.transform = 'translateY(-2px)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = 'var(--color-border)';
                                e.currentTarget.style.transform = 'translateY(0)';
                              }}
                            >
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-2)',
                                marginBottom: 'var(--space-2)'
                              }}>
                                <span style={{
                                  background: 'var(--color-primary)',
                                  color: 'white',
                                  borderRadius: '50%',
                                  width: '24px',
                                  height: '24px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '12px',
                                  fontWeight: 600
                                }}>
                                  {idx + 1}
                                </span>
                                <h4 style={{
                                  fontSize: 'var(--font-size-md)',
                                  fontWeight: 600,
                                  margin: 0,
                                  flex: 1
                                }}>{scenario.title}</h4>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditScenario(scenario);
                                  }}
                                  style={{
                                    background: 'var(--color-bg-tertiary)',
                                    border: '1px solid var(--color-border)',
                                    borderRadius: 'var(--radius-md)',
                                    padding: '4px 8px',
                                    fontSize: 'var(--font-size-xs)',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    color: 'var(--color-text-secondary)'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.background = 'var(--color-bg-secondary)';
                                    e.currentTarget.style.borderColor = 'var(--color-primary)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.background = 'var(--color-bg-tertiary)';
                                    e.currentTarget.style.borderColor = 'var(--color-border)';
                                  }}
                                >
                                  Edit
                                </button>
                              </div>
                              <p style={{
                                fontSize: 'var(--font-size-sm)',
                                color: 'var(--color-text-secondary)',
                                marginBottom: 'var(--space-3)'
                              }}>{scenario.description}</p>
                              {/* Preview of card headlines */}
                              <div style={{
                                display: 'flex',
                                gap: 'var(--space-2)',
                                overflowX: 'auto',
                                paddingBottom: 'var(--space-2)'
                              }}>
                                {scenario.cards?.slice(0, 4).map((card, cardIdx) => (
                                  <div
                                    key={cardIdx}
                                    style={{
                                      flex: '0 0 auto',
                                      background: 'var(--color-bg-tertiary)',
                                      borderRadius: 'var(--radius-md)',
                                      padding: 'var(--space-2) var(--space-3)',
                                      fontSize: 'var(--font-size-xs)',
                                      minWidth: '120px',
                                      textAlign: 'center'
                                    }}
                                  >
                                    <div style={{
                                      fontSize: '10px',
                                      color: 'var(--color-text-secondary)',
                                      marginBottom: '4px'
                                    }}>
                                      Card {card.position}
                                    </div>
                                    <div style={{
                                      fontWeight: 500,
                                      lineHeight: 1.3
                                    }}>
                                      {card.headline?.substring(0, 40)}{card.headline?.length > 40 ? '...' : ''}
                                    </div>
                                  </div>
                                ))}
                                {scenario.cards?.length > 4 && (
                                  <div style={{
                                    flex: '0 0 auto',
                                    display: 'flex',
                                    alignItems: 'center',
                                    padding: 'var(--space-2)',
                                    fontSize: 'var(--font-size-xs)',
                                    color: 'var(--color-text-secondary)'
                                  }}>
                                    +{scenario.cards.length - 4} more
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      {/* Custom Scenario Preview */}
                      {message.data?.customScenarioPreview && (
                        <div className="custom-scenario-preview" style={{
                          marginTop: 'var(--space-3)',
                          background: 'var(--color-bg-secondary)',
                          border: '2px solid var(--color-primary)',
                          borderRadius: 'var(--radius-lg)',
                          padding: 'var(--space-4)'
                        }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-2)',
                            marginBottom: 'var(--space-2)'
                          }}>
                            <span style={{
                              background: 'var(--color-primary)',
                              color: 'white',
                              borderRadius: 'var(--radius-md)',
                              padding: '2px 8px',
                              fontSize: 'var(--font-size-xs)',
                              fontWeight: 600
                            }}>
                              Your Scenario
                            </span>
                            <h4 style={{
                              fontSize: 'var(--font-size-md)',
                              fontWeight: 600,
                              margin: 0,
                              flex: 1
                            }}>{message.data.customScenarioPreview.title}</h4>
                          </div>
                          {message.data.customScenarioPreview.description && (
                            <p style={{
                              fontSize: 'var(--font-size-sm)',
                              color: 'var(--color-text-secondary)',
                              marginBottom: 'var(--space-3)'
                            }}>{message.data.customScenarioPreview.description}</p>
                          )}
                          {/* Card headlines preview */}
                          <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 'var(--space-2)'
                          }}>
                            {message.data.customScenarioPreview.cards?.map((card, cardIdx) => (
                              <div
                                key={cardIdx}
                                style={{
                                  background: 'var(--color-bg-tertiary)',
                                  borderRadius: 'var(--radius-md)',
                                  padding: 'var(--space-3)',
                                  display: 'flex',
                                  gap: 'var(--space-3)',
                                  alignItems: 'flex-start'
                                }}
                              >
                                <span style={{
                                  background: 'var(--color-primary)',
                                  color: 'white',
                                  borderRadius: '50%',
                                  width: '24px',
                                  height: '24px',
                                  minWidth: '24px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '12px',
                                  fontWeight: 600
                                }}>
                                  {card.position}
                                </span>
                                <div style={{ flex: 1 }}>
                                  <div style={{
                                    fontSize: '10px',
                                    color: 'var(--color-text-secondary)',
                                    textTransform: 'uppercase',
                                    marginBottom: '4px'
                                  }}>
                                    {card.role}
                                  </div>
                                  <div style={{
                                    fontWeight: 600,
                                    fontSize: 'var(--font-size-sm)',
                                    marginBottom: '4px'
                                  }}>
                                    {card.headline}
                                  </div>
                                  {card.subtext && (
                                    <div style={{
                                      fontSize: 'var(--font-size-xs)',
                                      color: 'var(--color-text-secondary)'
                                    }}>
                                      {card.subtext}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {/* Scenario Loading Indicator */}
                      {message.data?.showScenarioLoading && (
                        <div className="casting-loading-container">
                          <div className="casting-loading">
                            <div className="casting-loading-spinner"></div>
                            <p>Creating personalized story scenarios...</p>
                          </div>
                        </div>
                      )}
                      {/* Image/Carousel Generation Loading Indicator */}
                      {message.data?.showImageGenerationLoading && (
                        <div className="casting-loading-container">
                          <div className="casting-loading">
                            <div className="casting-loading-spinner"></div>
                            <p>Generating your creative...</p>
                          </div>
                        </div>
                      )}
                      {/* Social Media Post Preview (Image/Carousel) */}
                      {message.data?.generatedImages && message.data.generatedImages.length > 0 && (
                        <div className="social-media-post-preview" style={{
                          marginTop: 'var(--space-3)',
                          background: 'var(--color-bg-secondary)',
                          borderRadius: 'var(--radius-lg)',
                          border: '1px solid var(--color-border)',
                          overflow: 'hidden',
                          maxWidth: '500px'
                        }}>
                          {/* Post Header */}
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-3)',
                            padding: 'var(--space-3)',
                            borderBottom: '1px solid var(--color-border)'
                          }}>
                            <div style={{
                              width: '32px',
                              height: '32px',
                              borderRadius: '50%',
                              background: 'var(--color-primary)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              fontWeight: 600,
                              fontSize: '14px'
                            }}>
                              {(message.data.socialMediaPost?.brandName || 'B').charAt(0).toUpperCase()}
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>
                                {message.data.socialMediaPost?.brandName || 'Your Brand'}
                              </div>
                              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
                                {message.data.isCarousel ? 'Carousel Post' : 'Sponsored'}
                              </div>
                            </div>
                            {/* Favorite Button */}
                            {message.data.savedLibraryItemId && (
                              <div onClick={(e) => e.stopPropagation()}>
                                <FavoriteButton
                                  isFavorite={message.data.isFavorite}
                                  onToggle={() => handleToggleFavorite(idx, message.data.savedLibraryItemId)}
                                  size={20}
                                />
                              </div>
                            )}
                          </div>

                          {/* Carousel/Image Container */}
                          <div style={{ position: 'relative' }}>
                            {message.data.isCarousel ? (
                              <div style={{
                                display: 'flex',
                                overflowX: 'auto',
                                scrollSnapType: 'x mandatory',
                                scrollbarWidth: 'none',
                                msOverflowStyle: 'none'
                              }}>
                                {message.data.generatedImages.map((image, idx) => (
                                  <div
                                    key={image.id}
                                    onClick={() => setZoomedGeneratedImage(image)}
                                    style={{
                                      flex: '0 0 100%',
                                      scrollSnapAlign: 'start',
                                      cursor: 'pointer',
                                      position: 'relative'
                                    }}
                                  >
                                    <img
                                      src={image.imageUrl}
                                      alt={image.title}
                                      style={{
                                        width: '100%',
                                        aspectRatio: '1',
                                        objectFit: 'cover'
                                      }}
                                    />
                                    {/* Card indicator */}
                                    <div style={{
                                      position: 'absolute',
                                      top: 'var(--space-2)',
                                      right: 'var(--space-2)',
                                      background: 'rgba(0,0,0,0.7)',
                                      color: 'white',
                                      padding: '4px 8px',
                                      borderRadius: '12px',
                                      fontSize: '11px',
                                      fontWeight: 500
                                    }}>
                                      {idx + 1}/{message.data.generatedImages.length}
                                    </div>
                                    {/* Refine button */}
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleRefineCarouselCard(image, idx, message.data.generatedImages);
                                      }}
                                      style={{
                                        position: 'absolute',
                                        bottom: 'var(--space-2)',
                                        right: 'var(--space-2)',
                                        background: 'rgba(0,0,0,0.7)',
                                        color: 'white',
                                        border: 'none',
                                        padding: '6px 12px',
                                        borderRadius: 'var(--radius-md)',
                                        fontSize: '11px',
                                        fontWeight: 500,
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px'
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.background = 'var(--color-primary)';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.background = 'rgba(0,0,0,0.7)';
                                      }}
                                    >
                                      ✨ Refine
                                    </button>
                                  </div>
                                ))}
                              </div>
                            ) : message.data.generatedImages.length > 1 ? (
                              /* Multiple variations - show in a grid */
                              <div style={{
                                display: 'grid',
                                gridTemplateColumns: message.data.generatedImages.length === 2 ? 'repeat(2, 1fr)' : 'repeat(2, 1fr)',
                                gap: '2px'
                              }}>
                                {message.data.generatedImages.map((image, imageIdx) => (
                                  <div
                                    key={image.id}
                                    onClick={() => setZoomedGeneratedImage(image)}
                                    style={{
                                      position: 'relative',
                                      cursor: 'pointer'
                                    }}
                                  >
                                    <img
                                      src={image.imageUrl}
                                      alt={image.title || `Variation ${imageIdx + 1}`}
                                      style={{
                                        width: '100%',
                                        aspectRatio: '1',
                                        objectFit: 'cover'
                                      }}
                                    />
                                    {/* Variation name badge */}
                                    <div style={{
                                      position: 'absolute',
                                      top: 'var(--space-1)',
                                      left: 'var(--space-1)',
                                      background: 'rgba(0,0,0,0.7)',
                                      color: 'white',
                                      padding: '2px 8px',
                                      borderRadius: '8px',
                                      fontSize: '10px',
                                      fontWeight: 500,
                                      maxWidth: 'calc(100% - 40px)',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap'
                                    }}>
                                      {image.variationStyle || `#${imageIdx + 1}`}
                                    </div>
                                    {/* Favorite button */}
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleGeneratedImageFavoriteToggle(image);
                                      }}
                                      style={{
                                        position: 'absolute',
                                        top: 'var(--space-1)',
                                        right: 'var(--space-1)',
                                        background: 'rgba(0,0,0,0.5)',
                                        border: 'none',
                                        borderRadius: '50%',
                                        width: '28px',
                                        height: '28px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s'
                                      }}
                                      title={generatedImageFavorites[image.id] ? 'Remove from favorites' : 'Add to favorites'}
                                    >
                                      <Star
                                        size={14}
                                        fill={generatedImageFavorites[image.id] ? 'var(--color-warning)' : 'none'}
                                        color={generatedImageFavorites[image.id] ? 'var(--color-warning)' : 'white'}
                                      />
                                    </button>
                                    {/* Refine button */}
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setZoomedGeneratedImage(image);
                                      }}
                                      style={{
                                        position: 'absolute',
                                        bottom: 'var(--space-1)',
                                        right: 'var(--space-1)',
                                        background: 'rgba(0,0,0,0.7)',
                                        color: 'white',
                                        border: 'none',
                                        padding: '4px 8px',
                                        borderRadius: 'var(--radius-md)',
                                        fontSize: '10px',
                                        fontWeight: 500,
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '3px'
                                      }}
                                      title="Refine this image"
                                    >
                                      <Wand2 size={10} />
                                      Refine
                                    </button>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              /* Single image */
                              <div style={{ position: 'relative' }}>
                                <img
                                  src={message.data.generatedImages[0]?.imageUrl}
                                  alt="Generated ad"
                                  onClick={() => setZoomedGeneratedImage(message.data.generatedImages[0])}
                                  style={{
                                    width: '100%',
                                    aspectRatio: '1',
                                    objectFit: 'cover',
                                    cursor: 'pointer'
                                  }}
                                />
                                {/* Favorite button */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleGeneratedImageFavoriteToggle(message.data.generatedImages[0]);
                                  }}
                                  style={{
                                    position: 'absolute',
                                    top: 'var(--space-2)',
                                    right: 'var(--space-2)',
                                    background: 'rgba(0,0,0,0.5)',
                                    border: 'none',
                                    borderRadius: '50%',
                                    width: '32px',
                                    height: '32px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer'
                                  }}
                                  title={generatedImageFavorites[message.data.generatedImages[0]?.id] ? 'Remove from favorites' : 'Add to favorites'}
                                >
                                  <Star
                                    size={16}
                                    fill={generatedImageFavorites[message.data.generatedImages[0]?.id] ? 'var(--color-warning)' : 'none'}
                                    color={generatedImageFavorites[message.data.generatedImages[0]?.id] ? 'var(--color-warning)' : 'white'}
                                  />
                                </button>
                                {/* Refine button */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setZoomedGeneratedImage(message.data.generatedImages[0]);
                                  }}
                                  style={{
                                    position: 'absolute',
                                    bottom: 'var(--space-2)',
                                    right: 'var(--space-2)',
                                    background: 'rgba(0,0,0,0.7)',
                                    color: 'white',
                                    border: 'none',
                                    padding: '6px 12px',
                                    borderRadius: 'var(--radius-md)',
                                    fontSize: '12px',
                                    fontWeight: 500,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                  }}
                                  title="Refine this image"
                                >
                                  <Wand2 size={12} />
                                  Refine
                                </button>
                              </div>
                            )}
                            {/* Carousel dots indicator */}
                            {message.data.isCarousel && (
                              <div style={{
                                display: 'flex',
                                justifyContent: 'center',
                                gap: '4px',
                                padding: 'var(--space-2)',
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                background: 'linear-gradient(transparent, rgba(0,0,0,0.3))'
                              }}>
                                {message.data.generatedImages.map((_, idx) => (
                                  <div
                                    key={idx}
                                    style={{
                                      width: '6px',
                                      height: '6px',
                                      borderRadius: '50%',
                                      background: idx === 0 ? 'white' : 'rgba(255,255,255,0.5)'
                                    }}
                                  />
                                ))}
                              </div>
                            )}
                          </div>

                          {/* Caption Section */}
                          {message.data.socialMediaPost && (
                            <div style={{ padding: 'var(--space-3)' }}>
                              {/* Caption text */}
                              <div style={{
                                fontSize: 'var(--font-size-sm)',
                                lineHeight: 1.5,
                                whiteSpace: 'pre-wrap',
                                marginBottom: 'var(--space-2)'
                              }}>
                                <span style={{ fontWeight: 600 }}>
                                  {message.data.socialMediaPost.brandName || 'Your Brand'}
                                </span>{' '}
                                {message.data.socialMediaPost.caption}
                              </div>

                              {/* Hashtags */}
                              <div style={{
                                fontSize: 'var(--font-size-sm)',
                                color: 'var(--color-primary)',
                                marginBottom: 'var(--space-3)',
                                wordBreak: 'break-word'
                              }}>
                                {message.data.socialMediaPost.hashtags}
                              </div>

                            </div>
                          )}

                          {/* Swipe hint for carousel */}
                          {message.data.isCarousel && (
                            <div style={{
                              textAlign: 'center',
                              padding: 'var(--space-2)',
                              background: 'var(--color-bg-tertiary)',
                              fontSize: 'var(--font-size-xs)',
                              color: 'var(--color-text-secondary)'
                            }}>
                              ← Scroll horizontally to see all {message.data.generatedImages.length} cards →
                            </div>
                          )}
                          {/* Hint for multiple variations */}
                          {!message.data.isCarousel && message.data.generatedImages.length > 1 && (
                            <div style={{
                              textAlign: 'center',
                              padding: 'var(--space-2)',
                              background: 'var(--color-bg-tertiary)',
                              fontSize: 'var(--font-size-xs)',
                              color: 'var(--color-text-secondary)'
                            }}>
                              Click any variation to view full size
                            </div>
                          )}

                          {/* Download All Button - shown when there are multiple images */}
                          {message.data.generatedImages.length > 1 && (
                            <div style={{
                              display: 'flex',
                              justifyContent: 'center',
                              padding: 'var(--space-3)',
                              borderTop: '1px solid var(--color-border)',
                              background: 'var(--color-bg-secondary)'
                            }}>
                              <button
                                onClick={() => handleDownloadAllImages(message.data.generatedImages)}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 'var(--space-2)',
                                  padding: 'var(--space-2) var(--space-4)',
                                  background: 'var(--color-primary)',
                                  color: 'var(--color-primary-foreground)',
                                  border: 'none',
                                  borderRadius: 'var(--radius-md)',
                                  fontSize: 'var(--font-size-sm)',
                                  fontWeight: 500,
                                  cursor: 'pointer',
                                  transition: 'all 0.2s'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.opacity = '0.9';
                                  e.currentTarget.style.transform = 'translateY(-1px)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.opacity = '1';
                                  e.currentTarget.style.transform = 'translateY(0)';
                                }}
                              >
                                <Download size={16} />
                                Download All ({message.data.generatedImages.length} images)
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                      {/* Video Generation Progress Indicator */}
                      {message.data?.videoProgress && (
                        <div className="video-progress-container" style={{
                          marginTop: 'var(--space-4)',
                          padding: 'var(--space-4)',
                          borderRadius: 'var(--radius-lg)',
                          backgroundColor: 'var(--color-bg-tertiary)',
                          border: '1px solid var(--color-border)'
                        }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-3)',
                            marginBottom: 'var(--space-3)'
                          }}>
                            <div className="video-progress-spinner" style={{
                              width: '24px',
                              height: '24px',
                              border: '3px solid var(--color-border)',
                              borderTopColor: 'var(--color-primary)',
                              borderRadius: '50%',
                              animation: 'spin 1s linear infinite'
                            }} />
                            <div>
                              <div style={{
                                fontWeight: '600',
                                fontSize: 'var(--font-size-base)',
                                color: 'var(--color-text-primary)'
                              }}>
                                {message.data.videoProgress.status === 'queued' ? '⏳ Waiting in queue...' :
                                  message.data.videoProgress.status === 'generating' ? '🎬 Generating video...' :
                                    '⚡ Starting generation...'}
                              </div>
                              <div style={{
                                fontSize: 'var(--font-size-sm)',
                                color: 'var(--color-text-secondary)',
                                marginTop: 'var(--space-1)'
                              }}>
                                {message.data.videoProgress.message}
                              </div>
                            </div>
                          </div>

                          {/* Progress bar - only show if we have a percentage */}
                          {message.data.videoProgress.progress !== null && (
                            <div style={{
                              marginBottom: 'var(--space-3)'
                            }}>
                              <div style={{
                                height: '8px',
                                backgroundColor: 'var(--color-bg-secondary)',
                                borderRadius: '4px',
                                overflow: 'hidden'
                              }}>
                                <div style={{
                                  height: '100%',
                                  width: `${message.data.videoProgress.progress}%`,
                                  backgroundColor: 'var(--color-primary)',
                                  borderRadius: '4px',
                                  transition: 'width 0.5s ease-out'
                                }} />
                              </div>
                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                fontSize: 'var(--font-size-xs)',
                                color: 'var(--color-text-tertiary)',
                                marginTop: 'var(--space-1)'
                              }}>
                                <span>{message.data.videoProgress.progress}% complete</span>
                                <span>Est. {message.data.videoProgress.estimatedTime}</span>
                              </div>
                            </div>
                          )}

                          {/* Queue position if available */}
                          {message.data.videoProgress.queuePosition !== undefined && (
                            <div style={{
                              fontSize: 'var(--font-size-sm)',
                              color: 'var(--color-text-secondary)',
                              padding: 'var(--space-2)',
                              backgroundColor: 'var(--color-bg-secondary)',
                              borderRadius: 'var(--radius-sm)',
                              textAlign: 'center'
                            }}>
                              Queue position: #{message.data.videoProgress.queuePosition + 1}
                            </div>
                          )}

                          {/* Provider and timing info */}
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            gap: 'var(--space-2)',
                            fontSize: 'var(--font-size-xs)',
                            color: 'var(--color-text-tertiary)',
                            marginTop: 'var(--space-2)'
                          }}>
                            <span>Provider: {message.data.videoProgress.provider || 'AI Video'}</span>
                            {message.data.videoProgress.startedAt && (
                              <span>Started: {new Date(message.data.videoProgress.startedAt).toLocaleTimeString()}</span>
                            )}
                            {message.data.videoProgress.lastChecked && (
                              <span style={{ opacity: 0.8 }}>
                                Last checked: {new Date(message.data.videoProgress.lastChecked).toLocaleTimeString()}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                      {/* Generated Video Player */}
                      {message.data?.generatedVideo && (
                        <div className="generated-video-container" style={{
                          marginTop: 'var(--space-4)',
                          borderRadius: 'var(--radius-lg)',
                          overflow: 'hidden',
                          backgroundColor: 'var(--color-bg-tertiary)',
                          padding: 'var(--space-3)'
                        }}>
                          <video
                            controls
                            autoPlay
                            loop
                            style={{
                              width: '100%',
                              maxHeight: '500px',
                              borderRadius: 'var(--radius-md)'
                            }}
                          >
                            <source src={message.data.generatedVideo.url} type={message.data.generatedVideo.mimeType} />
                            Your browser does not support video playback.
                          </video>
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginTop: 'var(--space-2)',
                            fontSize: 'var(--font-size-sm)',
                            color: 'var(--color-text-secondary)'
                          }}>
                            <span>Generated {message.data.generatedVideo.generatedAt ? new Date(message.data.generatedVideo.generatedAt).toLocaleString() : 'just now'}</span>
                            <a
                              href={message.data.generatedVideo.url}
                              download="generated-video.mp4"
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: 'var(--color-primary)',
                                textDecoration: 'none',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-1)'
                              }}
                            >
                              ⬇️ Download Video
                            </a>
                          </div>
                        </div>
                      )}
                      {/* Selectable Images Grid */}
                      {message.data?.selectableImages && (
                        <div className="selectable-images-container">
                          <div className="selectable-images-grid">
                            {message.data.selectableImages.map((img, imgIdx) => {
                              const isSelected = pendingImageSelections.includes(img.index);
                              const isActiveSelection = isSelectingImages && idx === messages.length - 1;
                              const maxReached = pendingImageSelections.length >= (message.data.maxSelections || 2);
                              const isDisabled = !isSelected && maxReached;
                              return (
                                <div
                                  key={img.id}
                                  className={`selectable-image ${isSelected ? 'selected' : ''} ${isActiveSelection ? 'active' : ''} ${isDisabled ? 'disabled' : ''}`}
                                  onClick={() => isActiveSelection && !isDisabled && handleSelectableImageClick(img.index)}
                                  title={isDisabled ? `Maximum ${message.data.maxSelections || 2} images allowed` : ''}
                                >
                                  <img src={img.url} alt={img.alt} />
                                  {isSelected && <div className="selection-indicator">✔</div>}
                                </div>
                              );
                            })}
                          </div>
                          {isSelectingImages && idx === messages.length - 1 && (
                            <div className="selection-status">
                              {pendingImageSelections.length > 0
                                ? `${pendingImageSelections.length}/${message.data.maxSelections || 2} image${pendingImageSelections.length !== 1 ? 's' : ''} selected`
                                : `Click images to select (max ${message.data.maxSelections || 2})`}
                            </div>
                          )}
                        </div>
                      )}
                      {/* Inline Script Editor */}
                      {message.data?.scriptEditor && (
                        <div className="script-editor-inline">
                          <textarea
                            className="script-textarea-inline"
                            value={pendingScript || message.data.scriptEditor.script}
                            onChange={(e) => setPendingScript(e.target.value)}
                            rows={16}
                            disabled={isGeneratingScript || isLoading}
                          />
                          <div className="script-editor-actions">
                            <button
                              className="btn btn-secondary"
                              onClick={handleScriptRegenerateFromChat}
                              disabled={isGeneratingScript || isLoading}
                            >
                              🔄 Regenerate Script
                            </button>
                            <button
                              className="btn btn-primary"
                              onClick={() => handleScriptApprovalFromChat(pendingScript || message.data.scriptEditor.script)}
                              disabled={isGeneratingScript || isLoading}
                            >
                              ✅ Approve & Generate Video
                            </button>
                          </div>
                          <p className="script-editor-note">
                            💡 You can edit the script above before approving. The approved script will be used directly for video generation.
                          </p>
                        </div>
                      )}
                      {/* Refinement Selectable Images Grid */}
                      {message.data?.refinementSelectableImages && message.data.refinementSelectableImages.length > 0 && (
                        <div className="refinement-image-grid" style={{
                          marginTop: 'var(--space-3)',
                          marginBottom: 'var(--space-3)'
                        }}>
                          <div style={{
                            fontSize: 'var(--font-size-sm)',
                            color: 'var(--color-text-secondary)',
                            marginBottom: 'var(--space-2)',
                            fontWeight: '500'
                          }}>
                            Click an image to use as reference:
                          </div>
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                            gap: 'var(--space-2)',
                            maxWidth: '500px'
                          }}>
                            {message.data.refinementSelectableImages.map((img) => (
                              <div
                                key={img.id}
                                onClick={() => handleRefinementImageClick(img.url)}
                                style={{
                                  cursor: 'pointer',
                                  borderRadius: 'var(--radius-sm)',
                                  overflow: 'hidden',
                                  border: '2px solid var(--color-border)',
                                  transition: 'all 0.2s ease',
                                  aspectRatio: '1'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.borderColor = 'var(--color-primary)';
                                  e.currentTarget.style.transform = 'scale(1.02)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.borderColor = 'var(--color-border)';
                                  e.currentTarget.style.transform = 'scale(1)';
                                }}
                                title={img.alt || 'Click to use as reference'}
                              >
                                <img
                                  src={img.url}
                                  alt={img.alt || 'Reference option'}
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover'
                                  }}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {/* Refinement Reference Image Preview */}
                      {message.data?.refinementReferencePreview && (
                        <div className="refinement-reference-preview" style={{
                          marginTop: 'var(--space-3)',
                          marginBottom: 'var(--space-3)',
                          padding: 'var(--space-3)',
                          backgroundColor: 'var(--color-bg-tertiary)',
                          borderRadius: 'var(--radius-md)',
                          border: '1px solid var(--color-border)'
                        }}>
                          <div style={{
                            fontSize: 'var(--font-size-sm)',
                            color: 'var(--color-text-secondary)',
                            marginBottom: 'var(--space-2)',
                            fontWeight: '500'
                          }}>
                            📷 Reference Image:
                          </div>
                          <img
                            src={message.data.refinementReferencePreview}
                            alt="Reference for refinement"
                            style={{
                              maxWidth: '200px',
                              maxHeight: '150px',
                              borderRadius: 'var(--radius-sm)',
                              objectFit: 'cover',
                              border: '2px solid var(--color-primary)'
                            }}
                          />
                        </div>
                      )}
                      {message.options && (
                        <div className="message-options">
                          {message.options.map((option, optIdx) => {
                            // Check if this option requires a selection and we're in image selection mode
                            const requiresSelectionDisabled = option.requiresSelection &&
                              isSelectingImages &&
                              idx === messages.length - 1 &&
                              pendingImageSelections.length === 0;

                            return (
                              <button
                                key={optIdx}
                                className={`option-button ${option.action ? 'special' : ''} ${requiresSelectionDisabled ? 'disabled-hint' : ''}`}
                                onClick={() => handleOptionSelect(option, message)}
                                disabled={isLoading || requiresSelectionDisabled}
                                title={requiresSelectionDisabled ? 'Select at least one image first' : ''}
                              >
                                {option.text}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Insert Brief Sheet after the message that introduces it */}
                  {shouldShowBriefAfter && (
                    <BriefSheet
                      briefData={briefSheetData}
                      onApprove={handleBriefApprove}
                      onEdit={handleBriefEdit}
                      onAIEdit={handleBriefAIEdit}
                      isApproved={false}
                      selectedBrand={selectedBrand}
                      creatives={[]}
                      outputType={creativeBrief.outputType || 'image'}
                    />
                  )}
                </div>
              );
            })}

            {isLoading && (
              <div className="message ai">
                <div className="message-avatar">{PHASE_INFO[currentPhase]?.emoji || '💬'}</div>
                <div className="message-content">
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}

            {/* Fallback global progress indicator - shows if videoProgress state is set but message-based progress isn't visible */}
            {videoProgress && (
              <div className="video-progress-fallback" style={{
                margin: 'var(--space-4)',
                padding: 'var(--space-4)',
                borderRadius: 'var(--radius-lg)',
                backgroundColor: 'var(--color-bg-tertiary)',
                border: '2px solid var(--color-primary)',
                animation: 'pulse-border 2s ease-in-out infinite'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                  <div style={{
                    width: '24px',
                    height: '24px',
                    border: '3px solid var(--color-border)',
                    borderTopColor: 'var(--color-primary)',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                  <div>
                    <div style={{ fontWeight: '600', color: 'var(--color-text-primary)' }}>
                      {videoProgress.status === 'queued' ? '⏳ Waiting in queue...' :
                        videoProgress.status === 'generating' ? '🎬 Generating video...' :
                          '⚡ Processing...'}
                    </div>
                    <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                      {videoProgress.message}
                    </div>
                    {videoProgress.lastChecked && (
                      <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)', marginTop: 'var(--space-1)' }}>
                        Last checked: {new Date(videoProgress.lastChecked).toLocaleTimeString()}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Direct Mode Input Area - only show during prompt crafting step */}
          {isDirectMode && directModeStep === 'prompt' ? (
            <div className="direct-mode-input-area" style={{
              borderTop: '1px solid var(--color-border)',
              padding: 'var(--space-4)',
              background: 'var(--color-bg-primary)'
            }}>
              {/* Reference Images Section */}
              <div style={{ marginBottom: 'var(--space-3)' }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 'var(--space-2)'
                }}>
                  <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: '600', color: 'var(--color-text-secondary)' }}>
                    Reference Images ({directModeReferenceImages.length}/5)
                  </span>
                  {directModeReferenceImages.length < 5 && (
                    <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                      <input
                        type="file"
                        ref={directModeFileInputRef}
                        accept="image/*"
                        multiple
                        onChange={handleDirectModeFileUpload}
                        style={{ display: 'none' }}
                      />
                      <button
                        type="button"
                        onClick={() => directModeFileInputRef.current?.click()}
                        style={{
                          padding: '4px 8px',
                          fontSize: 'var(--font-size-xs)',
                          background: 'var(--color-bg-secondary)',
                          border: '1px solid var(--color-border)',
                          borderRadius: 'var(--radius-sm)',
                          cursor: 'pointer',
                          color: 'var(--color-text-secondary)'
                        }}
                      >
                        Upload
                      </button>
                    </div>
                  )}
                </div>

                {/* Reference Images Display */}
                {directModeReferenceImages.length > 0 && (
                  <div style={{
                    display: 'flex',
                    gap: 'var(--space-2)',
                    flexWrap: 'wrap',
                    marginBottom: 'var(--space-2)'
                  }}>
                    {directModeReferenceImages.map((refImg) => (
                      <div key={refImg.id} style={{
                        position: 'relative',
                        width: '60px',
                        height: '60px',
                        borderRadius: 'var(--radius-sm)',
                        overflow: 'hidden',
                        border: refImg.status === 'error' ? '2px solid var(--color-error)' : '2px solid var(--color-border)'
                      }}>
                        {refImg.status === 'valid' ? (
                          <img src={refImg.url} alt={refImg.label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : refImg.status === 'loading' ? (
                          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--color-bg-secondary)' }}>
                            <Loader2 size={16} className="animate-spin" />
                          </div>
                        ) : (
                          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--color-bg-secondary)' }}>
                            <AlertCircle size={16} color="var(--color-error)" />
                          </div>
                        )}
                        <span style={{
                          position: 'absolute',
                          bottom: '2px',
                          left: '2px',
                          background: 'rgba(0,0,0,0.7)',
                          color: 'white',
                          fontSize: '9px',
                          padding: '1px 4px',
                          borderRadius: '2px'
                        }}>{refImg.label}</span>
                        <button
                          onClick={() => removeDirectModeReferenceImage(refImg.id)}
                          style={{
                            position: 'absolute',
                            top: '2px',
                            right: '2px',
                            width: '16px',
                            height: '16px',
                            borderRadius: '50%',
                            border: 'none',
                            background: 'rgba(0,0,0,0.6)',
                            color: 'white',
                            cursor: 'pointer',
                            fontSize: '10px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >×</button>
                      </div>
                    ))}
                  </div>
                )}

                {/* URL Input for reference images */}
                {directModeReferenceImages.length < 5 && (
                  <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                    <input
                      type="text"
                      placeholder="Paste image URL and press Enter..."
                      value={directModeRefUrlInput}
                      onChange={(e) => setDirectModeRefUrlInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && directModeRefUrlInput.trim()) {
                          e.preventDefault();
                          addDirectModeReferenceImage(directModeRefUrlInput);
                        }
                      }}
                      style={{
                        flex: 1,
                        padding: '6px 10px',
                        fontSize: 'var(--font-size-sm)',
                        border: '1px solid var(--color-border)',
                        borderRadius: 'var(--radius-sm)',
                        background: 'var(--color-bg-secondary)'
                      }}
                    />
                  </div>
                )}

                <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)', marginTop: 'var(--space-1)' }}>
                  Tip: Reference images by label, e.g., "Use the colors from [Image A]"
                </p>
              </div>

              {/* Current Prompt Preview - Editable */}
              {directModePrompt && (
                <div style={{
                  marginBottom: 'var(--space-3)',
                  padding: 'var(--space-3)',
                  background: 'var(--color-bg-tertiary)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--color-primary)'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 'var(--space-2)'
                  }}>
                    <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: '600', color: 'var(--color-primary)' }}>
                      Current Prompt (edit below)
                    </span>
                    <button
                      onClick={handleDirectModeApply}
                      className="btn btn-primary"
                      style={{ padding: '6px 16px', fontSize: 'var(--font-size-sm)' }}
                    >
                      <Check size={14} style={{ marginRight: '4px' }} />
                      Use This Prompt
                    </button>
                  </div>
                  <textarea
                    value={directModePrompt}
                    onChange={(e) => setDirectModePrompt(e.target.value)}
                    style={{
                      width: '100%',
                      minHeight: '120px',
                      maxHeight: '200px',
                      padding: 'var(--space-2)',
                      fontSize: 'var(--font-size-sm)',
                      color: 'var(--color-text-primary)',
                      background: 'var(--color-bg-secondary)',
                      borderRadius: 'var(--radius-sm)',
                      border: '1px solid var(--color-border)',
                      lineHeight: 1.6,
                      resize: 'vertical',
                      fontFamily: 'inherit'
                    }}
                  />
                </div>
              )}

              {/* Direct Mode Text Input */}
              <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'flex-end' }}>
                <textarea
                  className="chat-input"
                  placeholder="Describe your creative vision... (Ctrl+Enter to send)"
                  value={directModeInput}
                  onChange={(e) => setDirectModeInput(e.target.value)}
                  onKeyDown={(e) => {
                    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                      e.preventDefault();
                      handleDirectModeSubmit();
                    }
                  }}
                  disabled={directModeLoading}
                  rows={3}
                  style={{ flex: 1, resize: 'none' }}
                />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                  <button
                    type="button"
                    onClick={handleDirectModeSubmit}
                    className="btn btn-primary"
                    disabled={!directModeInput.trim() || directModeLoading}
                    style={{ padding: '10px 16px' }}
                  >
                    {directModeLoading ? <Loader2 size={16} className="animate-spin" /> : 'Send'}
                  </button>
                  <button
                    type="button"
                    onClick={handleDirectModeReset}
                    className="btn btn-secondary"
                    style={{ padding: '6px 12px', fontSize: 'var(--font-size-xs)' }}
                  >
                    Guide Me
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <form className="chat-input-form" onSubmit={handleUserSubmit}>
              <textarea
                ref={userInputRef}
                className="chat-input"
                placeholder="Type your answer... (Enter to send, Shift+Enter for new line)"
                value={userInput}
                onChange={(e) => {
                  setUserInput(e.target.value);
                  // Auto-resize textarea
                  e.target.style.height = 'auto';
                  e.target.style.height = Math.min(e.target.scrollHeight, 200) + 'px';
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    if (!isLoading && userInput.trim()) {
                      handleUserSubmit(e);
                    }
                  }
                }}
                disabled={isLoading}
                rows={2}
              />
              <button
                type="submit"
                className="send-button"
                disabled={!userInput.trim() || isLoading}
              >
                Send
              </button>
            </form>
          )}
        </div>
      </div>

      {/* Casting Zoom Modal */}
      {zoomedCastingOption && (
        <div className="casting-zoom-backdrop" onClick={() => { setZoomedCastingOption(null); setRefinementText(''); setRefinementError(null); }}>
          <div className="casting-zoom-modal" onClick={(e) => e.stopPropagation()}>
            <button className="casting-zoom-close" onClick={() => { setZoomedCastingOption(null); setRefinementText(''); setRefinementError(null); }}>
              &times;
            </button>
            <div className="casting-zoom-image">
              {isRefining && <div className="refinement-loading-overlay"><div className="refinement-spinner" /></div>}
              <img src={zoomedCastingOption.imageUrl} alt={zoomedCastingOption.name || zoomedCastingOption.title} />
            </div>
            <div className="casting-zoom-info">
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--space-3)' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                    <h3>{zoomedCastingOption.name || zoomedCastingOption.title}</h3>
                    {zoomedCastingOption.isRefined && (
                      <span style={{
                        background: 'var(--color-primary)',
                        color: 'white',
                        borderRadius: '4px',
                        padding: '2px 8px',
                        fontSize: '11px',
                        fontWeight: 600
                      }}>Refined</span>
                    )}
                  </div>
                  <span className="casting-zoom-title">{zoomedCastingOption.title}</span>
                </div>
                <FavoriteButton
                  isFavorite={zoomedCastingOption.isFavorite}
                  onToggle={() => handleToggleCastingFavorite(zoomedCastingOption)}
                  size={24}
                />
              </div>
              <p>{zoomedCastingOption.description}</p>

              {/* Refinement Section */}
              <div className="refinement-section">
                <div className="refinement-input-group">
                  <input
                    type="text"
                    className="refinement-input"
                    placeholder="Refine: e.g., 'Make the lighting warmer'"
                    value={refinementText}
                    onChange={(e) => setRefinementText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !isRefining) {
                        handleImageRefinement('casting', zoomedCastingOption, refinementText);
                      }
                    }}
                    disabled={isRefining}
                  />
                  <button
                    className="btn btn-secondary refinement-btn"
                    onClick={() => handleImageRefinement('casting', zoomedCastingOption, refinementText)}
                    disabled={isRefining || !refinementText.trim()}
                  >
                    {isRefining ? 'Refining...' : 'Refine'}
                  </button>
                </div>
                {refinementError && <p className="refinement-error">{refinementError}</p>}
              </div>

              <button
                className="btn btn-primary casting-zoom-select"
                onClick={() => {
                  handleCastingSelect(zoomedCastingOption);
                  setZoomedCastingOption(null);
                  setRefinementText('');
                  setRefinementError(null);
                }}
                disabled={isRefining}
              >
                Cast {zoomedCastingOption.name || 'this person'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Location Zoom Modal */}
      {zoomedLocationOption && (
        <div className="casting-zoom-backdrop" onClick={() => { setZoomedLocationOption(null); setRefinementText(''); setRefinementError(null); }}>
          <div className="casting-zoom-modal location-zoom-modal" onClick={(e) => e.stopPropagation()}>
            <button className="casting-zoom-close" onClick={() => { setZoomedLocationOption(null); setRefinementText(''); setRefinementError(null); }}>
              &times;
            </button>
            <div className="casting-zoom-image">
              {isRefining && <div className="refinement-loading-overlay"><div className="refinement-spinner" /></div>}
              <img src={zoomedLocationOption.imageUrl} alt={zoomedLocationOption.name || zoomedLocationOption.title} />
            </div>
            <div className="casting-zoom-info">
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--space-3)' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                    <h3>{zoomedLocationOption.name || zoomedLocationOption.title}</h3>
                    {zoomedLocationOption.isRefined && (
                      <span style={{
                        background: 'var(--color-primary)',
                        color: 'white',
                        borderRadius: '4px',
                        padding: '2px 8px',
                        fontSize: '11px',
                        fontWeight: 600
                      }}>Refined</span>
                    )}
                  </div>
                  <span className="casting-zoom-title">{zoomedLocationOption.title}</span>
                </div>
                <FavoriteButton
                  isFavorite={zoomedLocationOption.isFavorite}
                  onToggle={() => handleToggleLocationFavorite(zoomedLocationOption)}
                  size={24}
                />
              </div>
              <p>{zoomedLocationOption.description}</p>

              {/* Refinement Section */}
              <div className="refinement-section">
                <div className="refinement-input-group">
                  <input
                    type="text"
                    className="refinement-input"
                    placeholder="Refine: e.g., 'Add more natural lighting'"
                    value={refinementText}
                    onChange={(e) => setRefinementText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !isRefining) {
                        handleImageRefinement('location', zoomedLocationOption, refinementText);
                      }
                    }}
                    disabled={isRefining}
                  />
                  <button
                    className="btn btn-secondary refinement-btn"
                    onClick={() => handleImageRefinement('location', zoomedLocationOption, refinementText)}
                    disabled={isRefining || !refinementText.trim()}
                  >
                    {isRefining ? 'Refining...' : 'Refine'}
                  </button>
                </div>
                {refinementError && <p className="refinement-error">{refinementError}</p>}
              </div>

              <button
                className="btn btn-primary casting-zoom-select"
                onClick={() => {
                  handleLocationSelect(zoomedLocationOption);
                  setZoomedLocationOption(null);
                  setRefinementText('');
                  setRefinementError(null);
                }}
                disabled={isRefining}
              >
                Select this location
              </button>
            </div>
          </div>
        </div>
      )}

      {/* First Frame Zoom Modal */}
      {zoomedFirstFrameOption && (
        <div className="casting-zoom-backdrop" onClick={() => { setZoomedFirstFrameOption(null); setRefinementText(''); setRefinementError(null); }}>
          <div className="casting-zoom-modal frame-zoom-modal" onClick={(e) => e.stopPropagation()}>
            <button className="casting-zoom-close" onClick={() => { setZoomedFirstFrameOption(null); setRefinementText(''); setRefinementError(null); }}>
              &times;
            </button>
            <div className="casting-zoom-image">
              {isRefining && <div className="refinement-loading-overlay"><div className="refinement-spinner" /></div>}
              <img src={zoomedFirstFrameOption.imageUrl} alt={zoomedFirstFrameOption.name || zoomedFirstFrameOption.title} />
            </div>
            <div className="casting-zoom-info">
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                <h3>{zoomedFirstFrameOption.name || zoomedFirstFrameOption.title}</h3>
                {zoomedFirstFrameOption.isRefined && (
                  <span style={{
                    background: 'var(--color-primary)',
                    color: 'white',
                    borderRadius: '4px',
                    padding: '2px 8px',
                    fontSize: '11px',
                    fontWeight: 600
                  }}>Refined</span>
                )}
              </div>
              <span className="casting-zoom-title">{zoomedFirstFrameOption.title}</span>
              <p>{zoomedFirstFrameOption.description}</p>

              {/* Refinement Section */}
              <div className="refinement-section">
                <div className="refinement-input-group">
                  <input
                    type="text"
                    className="refinement-input"
                    placeholder="Refine: e.g., 'Remove the lid from the image'"
                    value={refinementText}
                    onChange={(e) => setRefinementText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !isRefining) {
                        handleImageRefinement('firstFrame', zoomedFirstFrameOption, refinementText);
                      }
                    }}
                    disabled={isRefining}
                  />
                  <button
                    className="btn btn-secondary refinement-btn"
                    onClick={() => handleImageRefinement('firstFrame', zoomedFirstFrameOption, refinementText)}
                    disabled={isRefining || !refinementText.trim()}
                  >
                    {isRefining ? 'Refining...' : 'Refine'}
                  </button>
                </div>
                {refinementError && <p className="refinement-error">{refinementError}</p>}
              </div>

              <button
                className="btn btn-primary casting-zoom-select"
                onClick={() => {
                  handleFirstFrameSelect(zoomedFirstFrameOption);
                  setZoomedFirstFrameOption(null);
                  setRefinementText('');
                  setRefinementError(null);
                }}
                disabled={isRefining}
              >
                Use as opening shot
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Last Frame Zoom Modal */}
      {zoomedLastFrameOption && (
        <div className="casting-zoom-backdrop" onClick={() => { setZoomedLastFrameOption(null); setRefinementText(''); setRefinementError(null); }}>
          <div className="casting-zoom-modal frame-zoom-modal" onClick={(e) => e.stopPropagation()}>
            <button className="casting-zoom-close" onClick={() => { setZoomedLastFrameOption(null); setRefinementText(''); setRefinementError(null); }}>
              &times;
            </button>
            <div className="casting-zoom-image">
              {isRefining && <div className="refinement-loading-overlay"><div className="refinement-spinner" /></div>}
              <img src={zoomedLastFrameOption.imageUrl} alt={zoomedLastFrameOption.name || zoomedLastFrameOption.title} />
            </div>
            <div className="casting-zoom-info">
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                <h3>{zoomedLastFrameOption.name || zoomedLastFrameOption.title}</h3>
                {zoomedLastFrameOption.isRefined && (
                  <span style={{
                    background: 'var(--color-primary)',
                    color: 'white',
                    borderRadius: '4px',
                    padding: '2px 8px',
                    fontSize: '11px',
                    fontWeight: 600
                  }}>Refined</span>
                )}
              </div>
              <span className="casting-zoom-title">{zoomedLastFrameOption.title}</span>
              <p>{zoomedLastFrameOption.description}</p>
              {zoomedLastFrameOption.textElements && (
                <div className="frame-text-elements">
                  <small>Logo: {zoomedLastFrameOption.textElements.logoPlacement}</small>
                  <small>CTA: {zoomedLastFrameOption.textElements.ctaPlacement}</small>
                </div>
              )}

              {/* Refinement Section */}
              <div className="refinement-section">
                <div className="refinement-input-group">
                  <input
                    type="text"
                    className="refinement-input"
                    placeholder="Refine: e.g., 'Make the product more prominent'"
                    value={refinementText}
                    onChange={(e) => setRefinementText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !isRefining) {
                        handleImageRefinement('lastFrame', zoomedLastFrameOption, refinementText);
                      }
                    }}
                    disabled={isRefining}
                  />
                  <button
                    className="btn btn-secondary refinement-btn"
                    onClick={() => handleImageRefinement('lastFrame', zoomedLastFrameOption, refinementText)}
                    disabled={isRefining || !refinementText.trim()}
                  >
                    {isRefining ? 'Refining...' : 'Refine'}
                  </button>
                </div>
                {refinementError && <p className="refinement-error">{refinementError}</p>}
              </div>

              <button
                className="btn btn-primary casting-zoom-select"
                onClick={() => {
                  handleLastFrameSelect(zoomedLastFrameOption);
                  setZoomedLastFrameOption(null);
                  setRefinementText('');
                  setRefinementError(null);
                }}
                disabled={isRefining}
              >
                Use as closing shot
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Generated Image Fullscreen Zoom Modal */}
      {zoomedGeneratedImage && (
        <div className="casting-zoom-backdrop" onClick={() => { setZoomedGeneratedImage(null); setRefinementError(null); setImageZoomLevel(1); }}>
          <div className="casting-zoom-modal generated-image-zoom-modal" onClick={(e) => e.stopPropagation()}>
            <button className="casting-zoom-close" onClick={() => { setZoomedGeneratedImage(null); setRefinementError(null); setImageZoomLevel(1); }}>
              &times;
            </button>
            {/* Main image area - takes remaining space */}
            <div className="casting-zoom-image" style={{ position: 'relative' }}>
              {isRefining && refiningImageId === zoomedGeneratedImage.id && (
                <div className="refinement-loading-overlay"><div className="refinement-spinner" /></div>
              )}
              {/* Zoom controls overlay */}
              <div style={{
                position: 'absolute',
                top: 'var(--space-3)',
                right: 'var(--space-3)',
                display: 'flex',
                gap: 'var(--space-1)',
                zIndex: 10,
                background: 'rgba(0,0,0,0.6)',
                borderRadius: 'var(--radius-md)',
                padding: 'var(--space-1)'
              }}>
                <button
                  onClick={() => setImageZoomLevel(prev => Math.max(1, prev - 0.5))}
                  disabled={imageZoomLevel <= 1}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: imageZoomLevel <= 1 ? 'rgba(255,255,255,0.3)' : 'white',
                    cursor: imageZoomLevel <= 1 ? 'not-allowed' : 'pointer',
                    padding: 'var(--space-1)',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                  title="Zoom out"
                >
                  <ZoomOut size={18} />
                </button>
                <span style={{ color: 'white', fontSize: 'var(--font-size-xs)', display: 'flex', alignItems: 'center', minWidth: '40px', justifyContent: 'center' }}>
                  {Math.round(imageZoomLevel * 100)}%
                </span>
                <button
                  onClick={() => setImageZoomLevel(prev => Math.min(4, prev + 0.5))}
                  disabled={imageZoomLevel >= 4}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: imageZoomLevel >= 4 ? 'rgba(255,255,255,0.3)' : 'white',
                    cursor: imageZoomLevel >= 4 ? 'not-allowed' : 'pointer',
                    padding: 'var(--space-1)',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                  title="Zoom in"
                >
                  <ZoomIn size={18} />
                </button>
              </div>
              {/* Zoomable image container */}
              <div style={{
                width: '100%',
                height: '100%',
                overflow: imageZoomLevel > 1 ? 'auto' : 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: imageZoomLevel > 1 ? 'grab' : 'default'
              }}>
                <img
                  src={zoomedGeneratedImage.imageUrl}
                  alt={zoomedGeneratedImage.title}
                  style={{
                    transform: `scale(${imageZoomLevel})`,
                    transformOrigin: imageZoomLevel > 1 ? 'top left' : 'center center',
                    transition: 'transform 0.2s ease'
                  }}
                />
              </div>
            </div>
            {/* Side panel with image thumbnail and details */}
            <div className="casting-zoom-info">
              {/* Scaled thumbnail in side panel */}
              <div className="side-panel-thumbnail">
                <img
                  src={zoomedGeneratedImage.imageUrl}
                  alt={zoomedGeneratedImage.title}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
                <h3 style={{ margin: 0, flex: 1, fontSize: 'var(--font-size-lg)' }}>{zoomedGeneratedImage.title}</h3>
                {zoomedGeneratedImage.isRefined && (
                  <span style={{
                    background: 'var(--color-primary)',
                    color: 'white',
                    borderRadius: '4px',
                    padding: '2px 8px',
                    fontSize: '11px',
                    fontWeight: 600
                  }}>Refined</span>
                )}
                {/* Favorite button */}
                <button
                  onClick={() => handleGeneratedImageFavoriteToggle(zoomedGeneratedImage)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 'var(--space-1)'
                  }}
                  title={generatedImageFavorites[zoomedGeneratedImage.id] ? 'Remove from favorites' : 'Add to favorites'}
                >
                  <Star
                    size={22}
                    fill={generatedImageFavorites[zoomedGeneratedImage.id] ? 'var(--color-warning)' : 'none'}
                    color={generatedImageFavorites[zoomedGeneratedImage.id] ? 'var(--color-warning)' : 'var(--color-text-tertiary)'}
                  />
                </button>
              </div>
              {zoomedGeneratedImage.description && (
                <p style={{ marginBottom: 'var(--space-3)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>{zoomedGeneratedImage.description}</p>
              )}

              {/* Refinement Section */}
              <ImageRefinementPanel
                onRefine={(prompt, referenceImages) => handleGeneratedImageRefine(zoomedGeneratedImage, prompt, referenceImages)}
                isRefining={isRefining && refiningImageId === zoomedGeneratedImage.id}
                error={refinementError}
                userId={currentUser?.uid}
                brandId={selectedBrand?.id}
                originalImageUrl={zoomedGeneratedImage.imageUrl}
                originalPrompt={zoomedGeneratedImage.description}
              />

              <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                <a
                  href={zoomedGeneratedImage.imageUrl}
                  download={`${zoomedGeneratedImage.title?.replace(/\s+/g, '-') || 'generated-image'}.png`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary"
                  style={{ textDecoration: 'none', flex: 1, textAlign: 'center' }}
                >
                  <Download size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                  Download
                </a>
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    navigator.clipboard.writeText(zoomedGeneratedImage.imageUrl);
                  }}
                >
                  Copy URL
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Fork Confirmation Modal */}
      <ConfirmationModal
        isOpen={forkConfirmation?.show}
        onClose={() => setForkConfirmation(null)}
        onConfirm={handleConfirmFork}
        title="Fork Conversation?"
        message={`This will create a new conversation branch starting from message ${(forkConfirmation?.messageIndex ?? 0) + 1}. You can continue in a different direction from that point.`}
        confirmText="Fork"
        cancelText="Cancel"
        variant="primary"
      />

      {/* Scenario Editing Modal */}
      {editingScenario && (
        <div className="scenario-edit-modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div className="scenario-edit-modal" style={{
            background: 'var(--color-bg-primary)',
            borderRadius: 'var(--radius-xl)',
            width: '90%',
            maxWidth: '700px',
            maxHeight: '85vh',
            overflow: 'hidden',
            boxShadow: 'var(--shadow-xl)',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Modal Header */}
            <div style={{
              padding: 'var(--space-4) var(--space-5)',
              borderBottom: '1px solid var(--color-border)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <h3 style={{ margin: 0, fontSize: 'var(--font-size-lg)', fontWeight: 600 }}>
                Edit Scenario: {editingScenario.title}
              </h3>
              <button
                onClick={() => setEditingScenario(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: 'var(--color-text-secondary)',
                  padding: '4px 8px'
                }}
              >
                ×
              </button>
            </div>

            {/* Modal Body - Scrollable */}
            <div style={{
              padding: 'var(--space-5)',
              overflowY: 'auto',
              flex: 1
            }}>
              {/* Scenario Title */}
              <div style={{ marginBottom: 'var(--space-4)' }}>
                <label style={{
                  display: 'block',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 500,
                  marginBottom: 'var(--space-2)',
                  color: 'var(--color-text-secondary)'
                }}>
                  Scenario Title
                </label>
                <input
                  type="text"
                  value={editingScenario.title || ''}
                  onChange={(e) => setEditingScenario({ ...editingScenario, title: e.target.value })}
                  style={{
                    width: '100%',
                    padding: 'var(--space-3)',
                    border: '1px solid var(--color-border)',
                    borderRadius: 'var(--radius-md)',
                    fontSize: 'var(--font-size-md)',
                    background: 'var(--color-bg-secondary)'
                  }}
                />
              </div>

              {/* Cards */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                {editingScenario.cards?.map((card, cardIdx) => (
                  <div
                    key={cardIdx}
                    style={{
                      background: 'var(--color-bg-secondary)',
                      border: '1px solid var(--color-border)',
                      borderRadius: 'var(--radius-lg)',
                      padding: 'var(--space-4)'
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--space-2)',
                      marginBottom: 'var(--space-3)'
                    }}>
                      <span style={{
                        background: 'var(--color-primary)',
                        color: 'white',
                        borderRadius: '50%',
                        width: '24px',
                        height: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        fontWeight: 600
                      }}>
                        {card.position}
                      </span>
                      <span style={{
                        fontSize: 'var(--font-size-sm)',
                        color: 'var(--color-text-secondary)',
                        textTransform: 'capitalize'
                      }}>
                        {card.role || `Card ${card.position}`}
                      </span>
                    </div>

                    <div style={{ marginBottom: 'var(--space-3)' }}>
                      <label style={{
                        display: 'block',
                        fontSize: 'var(--font-size-xs)',
                        fontWeight: 500,
                        marginBottom: 'var(--space-1)',
                        color: 'var(--color-text-secondary)'
                      }}>
                        Headline
                      </label>
                      <input
                        type="text"
                        value={card.headline || ''}
                        onChange={(e) => handleUpdateScenarioCard(cardIdx, 'headline', e.target.value)}
                        style={{
                          width: '100%',
                          padding: 'var(--space-2) var(--space-3)',
                          border: '1px solid var(--color-border)',
                          borderRadius: 'var(--radius-md)',
                          fontSize: 'var(--font-size-sm)',
                          background: 'var(--color-bg-primary)'
                        }}
                      />
                    </div>

                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: 'var(--font-size-xs)',
                        fontWeight: 500,
                        marginBottom: 'var(--space-1)',
                        color: 'var(--color-text-secondary)'
                      }}>
                        Subtext
                      </label>
                      <textarea
                        value={card.subtext || ''}
                        onChange={(e) => handleUpdateScenarioCard(cardIdx, 'subtext', e.target.value)}
                        rows={2}
                        style={{
                          width: '100%',
                          padding: 'var(--space-2) var(--space-3)',
                          border: '1px solid var(--color-border)',
                          borderRadius: 'var(--radius-md)',
                          fontSize: 'var(--font-size-sm)',
                          background: 'var(--color-bg-primary)',
                          resize: 'vertical'
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: 'var(--space-4) var(--space-5)',
              borderTop: '1px solid var(--color-border)',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 'var(--space-3)'
            }}>
              <button
                onClick={() => setEditingScenario(null)}
                style={{
                  padding: 'var(--space-2) var(--space-4)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-md)',
                  background: 'transparent',
                  cursor: 'pointer',
                  fontSize: 'var(--font-size-sm)'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEditedScenario}
                style={{
                  padding: 'var(--space-2) var(--space-4)',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  background: 'var(--color-primary)',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 500
                }}
              >
                Save & Continue
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .conversational-creative {
          height: calc(100vh - var(--space-8) * 2);
          background: var(--color-bg-primary);
          padding: 0;
        }

        .conversation-container {
          display: grid;
          grid-template-columns: 280px 1fr;
          gap: var(--space-5);
          height: 100%;
          max-width: 2000px;
          margin: 0 auto;
        }

        .progress-sidebar {
          background: var(--color-surface);
          border-radius: var(--radius-xl);
          padding: var(--space-6);
          box-shadow: var(--shadow-lg);
          overflow-y: auto;
          border: 1px solid var(--color-border-light);
          display: flex;
          flex-direction: column;
        }

        .progress-sidebar h3 {
          margin: 0 0 var(--space-6) 0;
          font-size: var(--font-size-lg);
          font-weight: 600;
          color: var(--color-text-primary);
        }

        .phase-indicators {
          display: flex;
          flex-direction: column;
          gap: var(--space-3);
          margin-bottom: var(--space-8);
        }

        .phase-indicator {
          display: flex;
          align-items: center;
          gap: var(--space-3);
          padding: var(--space-3);
          border-radius: var(--radius-md);
          transition: all var(--transition-base);
          border: 2px solid transparent;
        }

        .phase-indicator.active {
          background: var(--gradient-primary);
          color: var(--color-text-inverted);
          border-color: var(--color-primary);
        }

        .phase-indicator.completed {
          background: var(--color-bg-tertiary);
        }

        .phase-emoji {
          font-size: var(--font-size-xl);
        }

        .phase-name {
          font-size: var(--font-size-sm);
          font-weight: 500;
          flex: 1;
        }

        .phase-section {
          display: flex;
          flex-direction: column;
        }

        .phase-indicator.has-notes {
          cursor: pointer;
        }

        .phase-indicator.has-notes:hover {
          background: var(--color-bg-secondary);
        }

        .phase-indicator.active.has-notes:hover {
          background: var(--gradient-primary);
        }

        .expand-indicator {
          font-size: var(--font-size-xs);
          opacity: 0.6;
          margin-left: auto;
        }

        .expert-notes {
          margin-left: var(--space-8);
          margin-top: var(--space-2);
          padding: var(--space-3);
          background: var(--color-bg-secondary);
          border-radius: var(--radius-md);
          border-left: 3px solid var(--color-primary);
        }

        .expert-note {
          padding: var(--space-2) 0;
          border-bottom: 1px solid var(--color-border-light);
        }

        .expert-note:last-child {
          border-bottom: none;
          padding-bottom: 0;
        }

        .note-label {
          font-size: var(--font-size-xs);
          color: var(--color-text-tertiary);
          margin-bottom: var(--space-1);
        }

        .note-value {
          font-size: var(--font-size-sm);
          color: var(--color-text-primary);
          cursor: pointer;
          padding: var(--space-1);
          border-radius: var(--radius-sm);
          transition: background var(--transition-fast);
          display: flex;
          align-items: center;
          gap: var(--space-2);
        }

        .note-value:hover {
          background: var(--color-surface-hover);
        }

        .edit-hint {
          opacity: 0;
          font-size: var(--font-size-xs);
          transition: opacity var(--transition-fast);
        }

        .note-value:hover .edit-hint {
          opacity: 0.6;
        }

        .note-thumbnail {
          width: 40px;
          height: 40px;
          object-fit: cover;
          border-radius: var(--radius-sm);
          margin-bottom: var(--space-1);
        }

        .note-edit-container {
          display: flex;
          flex-direction: column;
          gap: var(--space-2);
        }

        .note-edit-input {
          width: 100%;
          padding: var(--space-2);
          font-size: var(--font-size-sm);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-sm);
          resize: vertical;
          font-family: inherit;
        }

        .note-edit-input:focus {
          outline: none;
          border-color: var(--color-primary);
        }

        .note-ai-helper {
          display: flex;
          gap: var(--space-2);
        }

        .note-ai-helper input {
          flex: 1;
          padding: var(--space-2);
          font-size: var(--font-size-xs);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-sm);
        }

        .note-ai-helper input:focus {
          outline: none;
          border-color: var(--color-primary);
        }

        .ai-apply-btn {
          padding: var(--space-2);
          background: var(--gradient-primary);
          color: white;
          border: none;
          border-radius: var(--radius-sm);
          cursor: pointer;
          font-size: var(--font-size-sm);
        }

        .ai-apply-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .note-edit-actions {
          display: flex;
          gap: var(--space-2);
        }

        .note-save-btn, .note-cancel-btn {
          padding: var(--space-1) var(--space-3);
          font-size: var(--font-size-xs);
          border: none;
          border-radius: var(--radius-sm);
          cursor: pointer;
        }

        .note-save-btn {
          background: var(--color-success);
          color: white;
        }

        .note-cancel-btn {
          background: var(--color-bg-tertiary);
          color: var(--color-text-secondary);
        }

        .progress-summary {
          display: flex;
          flex-direction: column;
          gap: var(--space-4);
          flex: 1;
        }

        .summary-item {
          display: flex;
          gap: var(--space-3);
          padding-bottom: var(--space-4);
          border-bottom: 1px solid var(--color-border-light);
        }

        .summary-item:last-child {
          border-bottom: none;
          padding-bottom: 0;
        }

        .checkmark {
          color: var(--color-success);
          font-size: var(--font-size-base);
          font-weight: bold;
        }

        .summary-label {
          font-size: var(--font-size-xs);
          color: var(--color-text-tertiary);
          margin-bottom: var(--space-1);
        }

        .summary-value {
          font-size: var(--font-size-sm);
          color: var(--color-text-primary);
          font-weight: 500;
        }

        .config-info {
          margin-top: auto;
          padding-top: var(--space-4);
          font-size: var(--font-size-xs);
          color: var(--color-text-tertiary);
          border-top: 1px solid var(--color-border-light);
        }

        .chat-area {
          background: var(--color-surface);
          border-radius: var(--radius-xl);
          box-shadow: var(--shadow-lg);
          display: flex;
          flex-direction: column;
          overflow: hidden;
          border: 1px solid var(--color-border-light);
        }

        .chat-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: var(--space-6);
          border-bottom: 1px solid var(--color-border-light);
          background: var(--gradient-surface);
        }

        .chat-header h2 {
          margin: 0 0 var(--space-1) 0;
          font-size: var(--font-size-2xl);
          font-weight: 600;
          color: var(--color-text-primary);
        }

        .chat-header p {
          margin: 0;
          font-size: var(--font-size-sm);
          color: var(--color-text-secondary);
        }

        /* Funnel Context Bar */
        .funnel-context-bar {
          display: flex;
          align-items: center;
          gap: var(--space-4);
          padding: var(--space-3) var(--space-6);
          background: linear-gradient(135deg, var(--color-primary-light) 0%, var(--color-bg-secondary) 100%);
          border-bottom: 1px solid var(--color-border-light);
          position: relative;
        }

        .funnel-context-stage {
          display: flex;
          align-items: center;
          gap: var(--space-2);
        }

        .funnel-stage-badge {
          padding: var(--space-1) var(--space-2);
          border-radius: var(--radius-sm);
          font-size: var(--font-size-xs);
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .funnel-stage-badge[data-stage="TOFU"] {
          background: var(--color-info);
          color: white;
        }

        .funnel-stage-badge[data-stage="MOFU"] {
          background: var(--color-warning);
          color: white;
        }

        .funnel-stage-badge[data-stage="BOFU"] {
          background: var(--color-success);
          color: white;
        }

        .funnel-stage-name {
          font-size: var(--font-size-sm);
          font-weight: 600;
          color: var(--color-text-primary);
        }

        .funnel-context-details {
          display: flex;
          align-items: center;
          gap: var(--space-3);
          flex: 1;
        }

        .funnel-campaign-name {
          font-size: var(--font-size-sm);
          color: var(--color-text-secondary);
          padding: var(--space-1) var(--space-2);
          background: var(--color-bg-tertiary);
          border-radius: var(--radius-sm);
        }

        .funnel-stage-goal {
          font-size: var(--font-size-xs);
          color: var(--color-text-tertiary);
          font-style: italic;
        }

        .funnel-context-close {
          position: absolute;
          right: var(--space-3);
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          font-size: var(--font-size-lg);
          color: var(--color-text-tertiary);
          cursor: pointer;
          padding: var(--space-1);
          line-height: 1;
          opacity: 0.6;
          transition: opacity var(--transition-fast);
        }

        .funnel-context-close:hover {
          opacity: 1;
          color: var(--color-text-secondary);
        }

        .chat-messages {
          flex: 1;
          overflow-y: auto;
          padding: var(--space-6);
          display: flex;
          flex-direction: column;
          gap: var(--space-5);
          background: var(--color-bg-primary);
        }

        .message-wrapper {
          position: relative;
        }

        .message {
          display: flex;
          gap: var(--space-3);
          align-items: flex-start;
          position: relative;
        }

        .message.user {
          flex-direction: row-reverse;
        }

        .message-text-wrapper {
          position: relative;
          display: inline-block;
        }

        .message.user .message-text-wrapper {
          display: inline-block;
        }

        .message-actions {
          position: absolute;
          top: -10px;
          right: -10px;
          display: flex;
          gap: 4px;
          opacity: 0;
          transition: opacity var(--transition-base);
          z-index: 10;
        }

        .message-wrapper:hover .message-actions {
          opacity: 1;
        }

        .message-fork-btn,
        .message-delete-btn {
          width: 22px;
          height: 22px;
          border-radius: var(--radius-full);
          border: 1px solid var(--color-border);
          background: var(--color-surface);
          color: var(--color-text-tertiary);
          font-size: 14px;
          line-height: 1;
          cursor: pointer;
          transition: all var(--transition-base);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .message-fork-btn:hover {
          background: var(--color-primary, #6366f1);
          border-color: var(--color-primary, #6366f1);
          color: white;
          transform: scale(1.1);
        }

        .message-delete-btn:hover {
          background: var(--color-error, #ef4444);
          border-color: var(--color-error, #ef4444);
          color: white;
          transform: scale(1.1);
        }

        .message-avatar {
          width: 40px;
          height: 40px;
          border-radius: var(--radius-full);
          background: var(--gradient-primary);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: var(--font-size-xl);
          flex-shrink: 0;
          box-shadow: var(--shadow-sm);
        }

        .message-content {
          flex: 1;
          max-width: 70%;
        }

        .message.user .message-content {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
        }

        .message-text {
          background: var(--color-surface);
          border: 1px solid var(--color-border-light);
          border-radius: var(--radius-lg);
          padding: var(--space-4);
          font-size: var(--font-size-base);
          line-height: 1.6;
          color: var(--color-text-primary);
        }

        .message.user .message-text {
          background: var(--gradient-primary);
          color: var(--color-text-inverted);
          border: none;
        }

        .message-options {
          display: flex;
          flex-direction: column;
          gap: var(--space-2);
          margin-top: var(--space-3);
        }

        .option-button {
          padding: var(--space-3) var(--space-4);
          border-radius: var(--radius-md);
          border: 2px solid var(--color-primary);
          background: var(--color-surface);
          color: var(--color-primary);
          font-size: var(--font-size-sm);
          font-weight: 500;
          cursor: pointer;
          transition: all var(--transition-base);
          text-align: left;
        }

        .option-button:hover:not(:disabled) {
          background: var(--gradient-primary);
          color: white;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
        }

        .option-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .option-button.special {
          border-color: var(--color-text-tertiary);
          color: var(--color-text-secondary);
        }

        .option-button.special:hover:not(:disabled) {
          border-color: var(--color-text-secondary);
          color: var(--color-text-primary);
        }

        .typing-indicator {
          display: flex;
          gap: 4px;
          padding: var(--space-4);
          background: var(--color-surface);
          border-radius: var(--radius-lg);
          border: 1px solid var(--color-border-light);
        }

        .typing-indicator span {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: var(--color-primary);
          animation: bounce 1.4s infinite ease-in-out both;
        }

        .typing-indicator span:nth-child(1) {
          animation-delay: -0.32s;
        }

        .typing-indicator span:nth-child(2) {
          animation-delay: -0.16s;
        }

        @keyframes bounce {
          0%, 80%, 100% {
            transform: scale(0);
          }
          40% {
            transform: scale(1);
          }
        }

        .chat-input-form {
          display: flex;
          gap: var(--space-3);
          padding: var(--space-4) var(--space-6);
          border-top: 1px solid var(--color-border-light);
          background: var(--color-surface);
        }

        .chat-input {
          flex: 1;
          padding: var(--space-3) var(--space-4);
          border-radius: var(--radius-lg);
          border: 2px solid var(--color-border);
          background: var(--color-bg-primary);
          color: var(--color-text-primary);
          font-size: var(--font-size-base);
          font-family: inherit;
          resize: none;
          min-height: 50px;
          max-height: 200px;
          transition: all var(--transition-base);
        }

        .chat-input:focus {
          outline: none;
          border-color: var(--color-primary);
          box-shadow: 0 0 0 3px var(--color-primary-alpha);
        }

        .chat-input::placeholder {
          color: var(--color-text-tertiary);
        }

        .chat-input:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .send-button {
          padding: var(--space-3) var(--space-6);
          border-radius: var(--radius-lg);
          border: none;
          background: var(--gradient-primary);
          color: var(--color-text-inverted);
          font-size: var(--font-size-base);
          font-weight: 600;
          cursor: pointer;
          transition: all var(--transition-base);
          align-self: flex-end;
        }

        .send-button:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: var(--shadow-md);
        }

        .send-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        @media (max-width: 768px) {
          .conversation-container {
            grid-template-columns: 1fr;
          }

          .progress-sidebar {
            display: none;
          }
        }

        /* Color Swatches Styles */
        .color-swatches-container {
          margin-top: var(--space-4);
          padding: var(--space-4);
          background: var(--color-bg-secondary, #f8fafc);
          border-radius: var(--radius-lg);
          border: 1px solid var(--color-border-light);
        }

        .color-swatches {
          display: flex;
          flex-wrap: wrap;
          gap: var(--space-3);
          align-items: center;
          margin-bottom: var(--space-4);
        }

        .color-swatch-wrapper {
          position: relative;
        }

        .color-swatch {
          width: 48px;
          height: 48px;
          border-radius: var(--radius-md);
          cursor: pointer;
          border: 2px solid var(--color-border);
          transition: all var(--transition-base);
          box-shadow: var(--shadow-sm);
        }

        .color-swatch:hover {
          transform: scale(1.1);
          box-shadow: var(--shadow-md);
        }

        .color-remove-btn {
          position: absolute;
          top: -8px;
          right: -8px;
          width: 20px;
          height: 20px;
          border-radius: var(--radius-full);
          border: 1px solid var(--color-border);
          background: var(--color-surface);
          color: var(--color-text-tertiary);
          font-size: 14px;
          line-height: 1;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transition: all var(--transition-base);
        }

        .color-swatch-wrapper:hover .color-remove-btn {
          opacity: 1;
        }

        .color-remove-btn:hover {
          background: var(--color-error, #ef4444);
          border-color: var(--color-error, #ef4444);
          color: white;
        }

        .add-color-btn {
          width: 48px;
          height: 48px;
          border-radius: var(--radius-md);
          border: 2px dashed var(--color-border);
          background: transparent;
          color: var(--color-text-tertiary);
          font-size: 24px;
          cursor: pointer;
          transition: all var(--transition-base);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .add-color-btn:hover {
          border-color: var(--color-primary);
          color: var(--color-primary);
          background: var(--color-primary-alpha, rgba(102, 126, 234, 0.1));
        }

        .confirm-colors-btn {
          padding: var(--space-3) var(--space-5);
          border-radius: var(--radius-md);
          border: none;
          background: var(--gradient-primary);
          color: var(--color-text-inverted);
          font-size: var(--font-size-sm);
          font-weight: 600;
          cursor: pointer;
          transition: all var(--transition-base);
        }

        .confirm-colors-btn:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: var(--shadow-md);
        }

        .confirm-colors-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .color-picker-modal {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .color-picker-content {
          background: var(--color-surface);
          padding: var(--space-6);
          border-radius: var(--radius-xl);
          box-shadow: var(--shadow-xl);
          min-width: 300px;
        }

        .color-picker-content h4 {
          margin: 0 0 var(--space-4) 0;
          font-size: var(--font-size-lg);
          font-weight: 600;
          color: var(--color-text-primary);
        }

        .color-input {
          width: 100%;
          height: 60px;
          border: none;
          border-radius: var(--radius-md);
          cursor: pointer;
          margin-bottom: var(--space-3);
        }

        .color-text-input {
          width: 100%;
          padding: var(--space-3);
          border: 2px solid var(--color-border);
          border-radius: var(--radius-md);
          font-size: var(--font-size-sm);
          margin-bottom: var(--space-4);
          background: var(--color-bg-primary);
          color: var(--color-text-primary);
        }

        .color-text-input:focus {
          outline: none;
          border-color: var(--color-primary);
        }

        .color-picker-actions {
          display: flex;
          gap: var(--space-3);
          justify-content: flex-end;
        }

        .color-picker-actions .btn-confirm {
          padding: var(--space-2) var(--space-4);
          border-radius: var(--radius-md);
          border: none;
          background: var(--gradient-primary);
          color: var(--color-text-inverted);
          font-weight: 500;
          cursor: pointer;
          transition: all var(--transition-base);
        }

        .color-picker-actions .btn-confirm:hover {
          transform: translateY(-1px);
          box-shadow: var(--shadow-sm);
        }

        .color-picker-actions .btn-cancel {
          padding: var(--space-2) var(--space-4);
          border-radius: var(--radius-md);
          border: 1px solid var(--color-border);
          background: var(--color-surface);
          color: var(--color-text-secondary);
          font-weight: 500;
          cursor: pointer;
          transition: all var(--transition-base);
        }

        .color-picker-actions .btn-cancel:hover {
          background: var(--color-bg-tertiary);
        }

        /* Casting Options Styles */
        .casting-options-container {
          margin-top: var(--space-4);
          padding: var(--space-4);
          background: var(--color-bg-secondary, #f8fafc);
          border-radius: var(--radius-lg);
          border: 1px solid var(--color-border-light);
        }

        .casting-options-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: var(--space-4);
        }

        @media (max-width: 768px) {
          .casting-options-grid {
            grid-template-columns: 1fr;
          }
        }

        .casting-option {
          background: var(--color-surface);
          border: 2px solid var(--color-border-light);
          border-radius: var(--radius-lg);
          overflow: hidden;
          cursor: pointer;
          transition: all var(--transition-base);
        }

        .casting-option:hover {
          border-color: var(--color-primary);
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(102, 126, 234, 0.2);
        }

        .casting-image-wrapper {
          position: relative;
          aspect-ratio: 3/4;
          overflow: hidden;
          background: var(--color-bg-tertiary);
        }

        .casting-image-wrapper img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform var(--transition-base);
        }

        .casting-option:hover .casting-image-wrapper img {
          transform: scale(1.05);
        }

        .casting-info {
          padding: var(--space-3);
        }

        .casting-info h4 {
          margin: 0 0 var(--space-2) 0;
          font-size: var(--font-size-base);
          font-weight: 600;
          color: var(--color-text-primary);
        }

        .casting-info p {
          margin: 0;
          font-size: var(--font-size-sm);
          color: var(--color-text-secondary);
          line-height: 1.4;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        /* Casting Loading Styles */
        .casting-loading-container {
          margin-top: var(--space-4);
          padding: var(--space-6);
          background: var(--color-bg-secondary, #f8fafc);
          border-radius: var(--radius-lg);
          border: 1px solid var(--color-border-light);
          text-align: center;
        }

        .casting-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: var(--space-3);
        }

        .casting-loading-spinner {
          width: 40px;
          height: 40px;
          border: 3px solid var(--color-border-light);
          border-top-color: var(--color-primary);
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        @keyframes pulse-border {
          0%, 100% {
            border-color: var(--color-border);
            box-shadow: 0 0 0 0 rgba(var(--color-primary-rgb, 99, 102, 241), 0);
          }
          50% {
            border-color: var(--color-primary);
            box-shadow: 0 0 8px 2px rgba(var(--color-primary-rgb, 99, 102, 241), 0.3);
          }
        }

        .video-progress-container {
          animation: pulse-border 2s ease-in-out infinite;
        }

        .casting-loading p {
          margin: 0;
          font-size: var(--font-size-sm);
          color: var(--color-text-secondary);
        }

        /* Location Options Styles (Video Director) */
        .location-options-container .location-image-wrapper {
          aspect-ratio: 16/9;
        }

        /* Frame Options Styles (Video Director - First/Last Frame) */
        .frame-options-container .frame-image-wrapper {
          aspect-ratio: 9/16;
        }

        .frame-options-grid {
          grid-template-columns: repeat(4, 1fr);
        }

        @media (max-width: 1200px) {
          .frame-options-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 768px) {
          .frame-options-grid {
            grid-template-columns: 1fr;
          }
        }

        .frame-text-elements {
          display: flex;
          flex-direction: column;
          gap: var(--space-1);
          margin-top: var(--space-2);
          padding: var(--space-2);
          background: var(--color-bg-tertiary);
          border-radius: var(--radius-sm);
        }

        .frame-text-elements small {
          font-size: var(--font-size-xs);
          color: var(--color-text-tertiary);
        }

        /* Location zoom modal - wider for landscape images */
        .location-zoom-modal {
          max-width: 90vw;
          width: auto;
        }

        .location-zoom-modal .casting-zoom-image {
          max-height: 60vh;
          width: auto;
          min-width: 400px;
        }

        .location-zoom-modal .casting-zoom-image img {
          width: auto;
          height: auto;
          max-width: 100%;
          max-height: 60vh;
          object-fit: contain;
        }

        /* Frame zoom modal - flexible for any aspect ratio */
        .frame-zoom-modal {
          max-width: 90vw;
          width: auto;
        }

        .frame-zoom-modal .casting-zoom-image {
          max-height: 70vh;
          width: auto;
          min-width: 300px;
        }

        .frame-zoom-modal .casting-zoom-image img {
          width: auto;
          height: auto;
          max-width: 100%;
          max-height: 70vh;
          object-fit: contain;
        }

        /* Inline Script Editor Styles */
        .script-editor-inline {
          margin-top: var(--space-4);
          padding: var(--space-4);
          background: var(--color-bg-secondary);
          border-radius: var(--radius-lg);
          border: 1px solid var(--color-border);
        }

        .script-textarea-inline {
          width: 100%;
          padding: var(--space-4);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-lg);
          font-family: 'Fira Code', 'Monaco', 'Consolas', monospace;
          font-size: var(--font-size-sm);
          line-height: 1.6;
          background: var(--color-bg-primary);
          color: var(--color-text-primary);
          resize: vertical;
          min-height: 300px;
        }

        .script-textarea-inline:focus {
          outline: none;
          border-color: var(--color-primary);
          box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.1);
        }

        .script-textarea-inline:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .script-editor-actions {
          display: flex;
          gap: var(--space-3);
          justify-content: flex-end;
          margin-top: var(--space-4);
        }

        .script-editor-note {
          font-size: var(--font-size-xs);
          color: var(--color-text-tertiary);
          text-align: center;
          margin: var(--space-3) 0 0 0;
        }

        /* Image Refinement Styles */
        .refinement-section {
          margin: var(--space-4) 0;
          padding-top: var(--space-3);
          border-top: 1px solid var(--color-border-light);
        }

        .refinement-input-group {
          display: flex;
          gap: var(--space-2);
        }

        .refinement-input {
          flex: 1;
          padding: var(--space-2) var(--space-3);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-md);
          font-size: var(--font-size-sm);
          background: var(--color-bg-primary);
          color: var(--color-text-primary);
          transition: border-color var(--transition-fast);
        }

        .refinement-input:focus {
          outline: none;
          border-color: var(--color-primary);
          box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.1);
        }

        .refinement-input:disabled {
          background: var(--color-bg-tertiary);
          cursor: not-allowed;
        }

        .refinement-input::placeholder {
          color: var(--color-text-tertiary);
        }

        .refinement-btn {
          white-space: nowrap;
          padding: var(--space-2) var(--space-4);
          font-size: var(--font-size-sm);
        }

        .refinement-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .refinement-error {
          margin-top: var(--space-2);
          font-size: var(--font-size-sm);
          color: var(--color-error, #ef4444);
        }

        .refinement-loading-overlay {
          position: absolute;
          inset: 0;
          background: rgba(0, 0, 0, 0.6);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10;
        }

        .refinement-spinner {
          width: 48px;
          height: 48px;
          border: 4px solid rgba(255, 255, 255, 0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        .casting-zoom-image {
          position: relative;
        }

        /* Selectable Images Styles */
        .selectable-images-container {
          margin-top: var(--space-4);
          padding: var(--space-4);
          background: var(--color-bg-secondary, #f8fafc);
          border-radius: var(--radius-lg);
          border: 1px solid var(--color-border-light);
        }

        .selectable-images-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: var(--space-3);
        }

        @media (max-width: 768px) {
          .selectable-images-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        .selectable-image {
          position: relative;
          aspect-ratio: 1;
          border-radius: var(--radius-md);
          overflow: hidden;
          border: 3px solid transparent;
          cursor: pointer;
          transition: all var(--transition-base);
        }

        .selectable-image.active {
          cursor: pointer;
        }

        .selectable-image.active:hover {
          transform: scale(1.02);
          box-shadow: var(--shadow-md);
        }

        .selectable-image.selected {
          border-color: var(--color-primary);
        }

        .selectable-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .selection-indicator {
          position: absolute;
          top: 8px;
          right: 8px;
          width: 24px;
          height: 24px;
          background: var(--color-primary);
          color: white;
          border-radius: var(--radius-full);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          font-weight: bold;
        }

        .selection-status {
          margin-top: var(--space-3);
          padding: var(--space-2) var(--space-3);
          background: var(--color-surface);
          border-radius: var(--radius-md);
          font-size: var(--font-size-sm);
          color: var(--color-text-secondary);
          text-align: center;
        }

        /* Variant Lab Modal Overlay */
        .variant-lab-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.6);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
          padding: var(--space-4);
        }

        /* Variant Lab Button in chat */
        .variant-lab-trigger {
          display: inline-flex;
          align-items: center;
          gap: var(--space-2);
          padding: var(--space-3) var(--space-4);
          background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-dark) 100%);
          color: white;
          border: none;
          border-radius: var(--radius-md);
          font-weight: 500;
          cursor: pointer;
          transition: all var(--transition-fast);
          margin-top: var(--space-3);
        }

        .variant-lab-trigger:hover {
          transform: translateY(-2px);
          box-shadow: var(--shadow-lg);
        }

        /* Variants Badge */
        .variants-badge {
          display: inline-flex;
          align-items: center;
          gap: var(--space-2);
          padding: var(--space-2) var(--space-3);
          background: var(--color-success-light);
          color: var(--color-success);
          border-radius: var(--radius-md);
          font-size: var(--font-size-sm);
          font-weight: 500;
          margin-top: var(--space-3);
        }
      `}</style>

      {/* Variant Lab Modal */}
      {showVariantLab && (
        <div className="variant-lab-overlay" onClick={() => setShowVariantLab(false)}>
          <div onClick={(e) => e.stopPropagation()}>
            <VariantLab
              creativeBrief={creativeBrief}
              conversationAgent={conversationAgent}
              existingVariants={generatedVariants?.variants || []}
              onVariantsGenerated={(variants) => {
                setGeneratedVariants(variants);
                setShowVariantLab(false);
                // Add a message to the chat about the variants
                setMessages(prev => [...prev, {
                  id: Date.now(),
                  role: 'ai',
                  text: `I've generated **${variants.variants?.length || 0} A/B test variants** for your ${variants.testElement} test.\n\n**Test Description:** ${variants.testDescription}\n\n${variants.variants?.map(v => `**Variant ${v.id}** (${v.label}): ${v.hypothesis}`).join('\n\n')}\n\nThese variants will be saved along with your creative when you export to the Asset Library.`,
                  phase: currentPhase,
                  timestamp: new Date(),
                  data: { variants }
                }]);
              }}
              onClose={() => setShowVariantLab(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
}
